import { camelCaseNormalizer } from '@amtrav/webservice';
import { randomString } from '@amtrav/utils';
import React, { useState, useEffect, useMemo, memo, Fragment, useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css, useTheme } from 'styled-components';
import { media, getThemeProp, isMatchedMaxWidth, ZINDEX, lighten, MatchMedia } from '@amtrav/themes';
import { Flex, Box } from '@rebass/grid';
import { getTokenValue, THEME_TOKENS, SPACE } from '@amtrav/theme-system';
import { skeletonLoadingStyles, formatUSCurrencyNum, PriceButton, AreaDistance, AddressRegular, Button, PreferredBadge, OutOfPolicy, formatLocationAddress, Carousel, OptionMini, AddressMini, MetricTileCompact, IconFlex, AddressOverview, LightBox, Heading3, Heading4, ActivityIndicator, EmptyState, Popover, Modal, Tooltip, Badge, RefundabilityBadge, ListHeaderDark, HorizontalScroll, Solofilter, FilterCheckbox, SolofilterCheckbox, Notice, Multifilter, FilterSection, Tabs, DatesSummary, ElasticDotsLoader } from '@amtrav/view-components';
import { IconLine, IconClose, IconClock, IconCheck, IconPriceTag, IconWarning, IconOpenNew } from '@amtrav/icons';
import { shortMonthDayYear, parseDate } from '@amtrav/dates';
import tinycolor from 'tinycolor2';

const processHotelCardData = h => {
  var _h$policyViolations, _rooms$0$valueAdds;
  const {
    rooms
  } = h;
  const hasRoomsArr = (rooms === null || rooms === void 0 ? void 0 : rooms.length) > 0;
  const newHotelObj = {
    propId: h.propID,
    vendorHotelId: h.vendorHotelID,
    vendorSource: h.vendorSource,
    hotelRating: h.hotelRating,
    reviewRate: h.reviewRate,
    reviewCount: h.reviewCount,
    hotelName: h.name,
    hotelAddress: {
      address1: h.address1 || null,
      city: h.addressCity,
      state: h.addressState,
      zip: h.addressZip
    },
    area: h.area,
    distance: h.distance,
    geoLat: h.geoLat,
    geoLon: h.geoLon,
    travelerBookings: h.travelerBookings,
    colleaguesVisited: h.colleaguesVisited,
    colleagueNames: h.colleagueNames || [],
    isPreferred: h.isPreferred === 1,
    inPolicy: ((_h$policyViolations = h.policyViolations) === null || _h$policyViolations === void 0 ? void 0 : _h$policyViolations.length) === 0,
    soldOut: h.soldOut,
    policyViolations: h.policyViolations || [],
    averageNightlyRate: h.averageNightlyRate,
    averageBaseRate: h.averageBaseRate,
    hotelImages: h.images.slice(0, 10),
    vendorPromo: {}
  };
  if (hasRoomsArr && rooms[0].vendorPromoText) {
    newHotelObj.vendorPromo.vendorPromoText = rooms[0].vendorPromoText;
  }
  if (hasRoomsArr && ((_rooms$0$valueAdds = rooms[0].valueAdds) === null || _rooms$0$valueAdds === void 0 ? void 0 : _rooms$0$valueAdds.length) > 0) {
    newHotelObj.vendorPromo.valueAdds = rooms[0].valueAdds;
  }
  if (hasRoomsArr && rooms[0].promoText) {
    newHotelObj.vendorPromo.promoText = rooms[0].promoText;
  }
  return newHotelObj;
};
const normalizeHotelResults = rawData => {
  const output = camelCaseNormalizer(rawData);
  output.hotels = output.data.map(h => processHotelCardData(h));
  delete output.data;
  return output;
};
const normalizeRoomResults = rawData => {
  const rooms = rawData.rooms.map(room => {
    // Apply a random string to each rate to be used as a key. Mostly for react optimization.
    const rates = room.rates.map(rate => {
      return {
        frontEndKey: randomString(),
        ...rate
      };
    });
    return {
      ...room,
      frontEndKey: randomString(),
      rates
    };
  });
  return {
    ...rawData,
    rooms
  };
};

const DescriptionContainer = styled(Flex)`
  display: flex;
  flex-direction: column;

  ${_ref => {
  let {
    standalone
  } = _ref;
  return standalone && css`
      background: white;
      border-top: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
    `;
}};

  ${media.Medium`
      ${props => props.standalone && css`
          border: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
          border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)};
        `};
    `};
`;
const DetailsText = styled.div`
  line-height: 1.3;

  b {
    color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
    font-weight: 600;
  }

  p:not(:last-child) {
    margin-bottom: 16px;
  }

  ul {
    li {
      list-style: none;
      position: relative;
      font-size: 14px;
      line-height: 18px;
      padding-left: 16px;
      margin-bottom: 8px;

      &:last-of-type {
        margin-bottom: 0;
      }

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 7px;
        width: 3px;
        height: 3px;
        border-radius: 50%;
        background-color: ${getThemeProp('global.textBody--color')};
      }
    }
  }
`;
const HotelDescription = _ref2 => {
  let {
    description,
    standalone = false
  } = _ref2;
  return /*#__PURE__*/React.createElement(DescriptionContainer, {
    standalone: standalone,
    mt: standalone ? [3] : [],
    p: standalone ? [4] : []
  }, /*#__PURE__*/React.createElement(DetailsText, null, /*#__PURE__*/React.createElement("div", {
    dangerouslySetInnerHTML: {
      __html: description
    }
  })));
};
HotelDescription.propTypes = {
  standalone: PropTypes.bool,
  description: PropTypes.string
};

const PriceContainer$2 = styled(Flex)`
  flex-direction: column;
  margin-right: 2px;
`;
const PriceButtonWrapper$1 = styled.div`
  min-width: 52px;
  /* space for skeleton loader if no content is provided */
  min-height: 40px;
  ${_ref => {
  let {
    isLoading
  } = _ref;
  return skeletonLoadingStyles(isLoading, true);
}};
`;
const PriceStrike$1 = styled.div`
  display: inline-block;
  position: relative;
  opacity: 0.8;
  font-size: 16px;
  color: #535353;
  text-align: right;
  margin-top: 12px;
  text-decoration: line-through;
`;
const SoldOut$1 = styled.div`
  font-size: 17px;
  font-weight: bold;
  color: gray;
  text-align: center;
  white-space: nowrap;
`;
const Price = _ref2 => {
  let {
    nightlyRate,
    baseRate,
    soldOut,
    isLoading,
    onClick
  } = _ref2;
  const showPriceStrike = baseRate > nightlyRate && !isLoading;
  const getPriceButton = () => nightlyRate === 0 ? null : /*#__PURE__*/React.createElement(PriceButton, {
    price: nightlyRate,
    onClick: onClick
  });
  return /*#__PURE__*/React.createElement(PriceContainer$2, null, /*#__PURE__*/React.createElement(PriceButtonWrapper$1, {
    isLoading: isLoading
  }, soldOut ? /*#__PURE__*/React.createElement(SoldOut$1, null, "Sold Out") : getPriceButton()), showPriceStrike && /*#__PURE__*/React.createElement(PriceStrike$1, null, "$", formatUSCurrencyNum(baseRate)));
};
Price.propTypes = {
  nightlyRate: PropTypes.number,
  baseRate: PropTypes.number,
  soldOut: PropTypes.bool,
  isLoading: PropTypes.bool,
  onClick: PropTypes.func
};

const getGuestRating = reviewRate => {
  const exaggerateGuestRating = reviewRate * 2;
  const newNum = exaggerateGuestRating.toFixed(1);
  if (newNum % 1 !== 0) {
    return newNum;
  }
  return Math.round(newNum);
};
const getRatingName = rating => {
  const truncatedRating = Math.trunc(rating);
  switch (truncatedRating) {
    case 2:
      return '';
    case 4:
      return 'Poor';
    case 5:
      return 'Poor';
    case 6:
      return 'Okay';
    case 7:
      return 'Okay';
    case 8:
      return 'Good';
    case 9:
      return 'Great';
    case 10:
      return 'Excellent';
    default:
      return '';
  }

  // ORIGINAL LOGIC
  // switch (truncatedRating) {
  //   case 2:
  //     return 'Poor';
  //   case 3:
  //     return 'Okay';
  //   case 4:
  //     return 'Good';
  //   case 5:
  //     return 'Excellent';
  //   default:
  //     return '';
  // }
};
const processLightboxHotelPhotos = htlUrlList => htlUrlList.map(e => ({
  src: e.replace('_b', '_w')
}));
const expediaImgPrefix = 'i.travelapi.com';
const getLargeImgUrl = url => {
  if (url.includes(expediaImgPrefix)) {
    return url.replace('_b', '_z');
  }
  return url;
};

const RatingSummary$1 = styled.span`
  font-size: 12px;
  text-decoration: ${_ref => {
  let {
    hasClickHandler
  } = _ref;
  return hasClickHandler ? 'underline' : 'none';
}};
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
`;
const Ratings = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  white-space: nowrap;
  cursor: default;

  ${_ref2 => {
  let {
    onClick
  } = _ref2;
  return onClick && css`
      @media (hover: hover) {
        &:hover {
          cursor: pointer;
        }

        &:hover ${RatingSummary$1} {
          text-decoration: underline;
        }
      }
    `;
}};
`;
const getGuestRatingColor = ratingNumber => {
  if (ratingNumber >= 8) {
    return getTokenValue(THEME_TOKENS.base.color.success);
  }
  if (ratingNumber <= 6) {
    return getTokenValue(THEME_TOKENS.base.color.warning);
  }
  return getTokenValue(THEME_TOKENS.base.color.primaryText);
};
const GuestRatingScore = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  font-size: 14px;
  font-weight: bold;
  color: #fff;
  background-color: ${_ref3 => {
  let {
    ratingNumber
  } = _ref3;
  return getGuestRatingColor(ratingNumber);
}};
  border-radius: 4px 4px 4px 0px;
  padding-top: 2px;
  padding-bottom: 2px;
  margin: 0 8px;
  cursor: pointer;
`;
const HotelClass$1 = styled.span`
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  font-size: 14px;
`;
const StarClassGuestRating = _ref4 => {
  let {
    onReviewsSummaryClick = null,
    starRating,
    reviewRate,
    reviewCount,
    reviewCountLabel = '+ Reviews',
    ...rest
  } = _ref4;
  const showStarRating = starRating > 0;
  const showReviewSummary = reviewCount > 0;
  if (showStarRating) {
    const is375OrSmaller = isMatchedMaxWidth('375px');
    const reviewCountDisplay = ` ${reviewCount}${reviewCountLabel}`;
    return /*#__PURE__*/React.createElement(Box, rest, /*#__PURE__*/React.createElement(Ratings, {
      "data-click-target": "reviews",
      onClick: onReviewsSummaryClick
    }, /*#__PURE__*/React.createElement(HotelClass$1, null, starRating, "-Star Hotel"), showReviewSummary && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(GuestRatingScore, {
      ratingNumber: getGuestRating(reviewRate),
      hasClickHandler: onReviewsSummaryClick
    }, getGuestRating(reviewRate)), /*#__PURE__*/React.createElement(RatingSummary$1, {
      hasClickHandler: onReviewsSummaryClick
    }, /*#__PURE__*/React.createElement("strong", null, getRatingName(getGuestRating(reviewRate))), !is375OrSmaller ? reviewCountDisplay : ''))));
  }
  return null;
};
StarClassGuestRating.propTypes = {
  onReviewsSummaryClick: PropTypes.func,
  starRating: PropTypes.number,
  reviewRate: PropTypes.number,
  reviewCount: PropTypes.number,
  reviewCountLabel: PropTypes.string
};

const NameRatingContainer$1 = styled(Box)`
  display: grid;
  // space for skeleton loader if no content is provided
  min-height: 47px;
  ${_ref => {
  let {
    isLoading
  } = _ref;
  return skeletonLoadingStyles(isLoading);
}};
`;
const HotelName$2 = styled.span`
  font-size: ${getTokenValue(THEME_TOKENS.base.size.header3)};
  padding-bottom: ${SPACE.size4};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-weight: ${getTokenValue(THEME_TOKENS.base.fontWeight.bold)};
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
`;
const NameRating = _ref2 => {
  let {
    hotelName,
    starRating,
    reviewRate,
    reviewCount,
    reviewLoading,
    nameLoading,
    onReviewsSummaryClick,
    onNameClick
  } = _ref2;
  const hasStarRating = starRating > 0;
  const hasGuestReviews = reviewCount > 0;
  return /*#__PURE__*/React.createElement(NameRatingContainer$1, {
    isLoading: nameLoading
  }, /*#__PURE__*/React.createElement(HotelName$2, {
    "data-click-target": "name",
    onClick: onNameClick,
    title: hotelName
  }, hotelName), hasStarRating && hasGuestReviews ? /*#__PURE__*/React.createElement(StarClassGuestRating, {
    mt: 1,
    onReviewsSummaryClick: reviewLoading ? null : onReviewsSummaryClick,
    starRating: starRating,
    reviewRate: reviewRate,
    reviewCount: reviewCount
  }) : null);
};
NameRating.propTypes = {
  onReviewsSummaryClick: PropTypes.func,
  onNameClick: PropTypes.func,
  hotelName: PropTypes.string,
  starRating: PropTypes.number,
  reviewRate: PropTypes.number,
  reviewCount: PropTypes.number,
  nameLoading: PropTypes.bool,
  reviewLoading: PropTypes.bool
};

const HotelAddressContainer = styled(Flex)`
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  min-height: 18px; // space for skeleton loader if no content is provided
  margin-top: 16px;
  ${_ref => {
  let {
    isLoading
  } = _ref;
  return skeletonLoadingStyles(isLoading);
}};

  ${media.Medium`
    margin-top: 12px;
  `};
`;
const HotelAddress = _ref2 => {
  let {
    area,
    distance,
    addressDisplay,
    isLoading
  } = _ref2;
  const milesAway = distance === null || distance === void 0 ? void 0 : distance.toFixed(1);
  return /*#__PURE__*/React.createElement(HotelAddressContainer, {
    isLoading: isLoading
  }, /*#__PURE__*/React.createElement(AreaDistance, {
    distance: milesAway,
    area: area
  }), /*#__PURE__*/React.createElement(AddressRegular, null, addressDisplay));
};
HotelAddress.propTypes = {
  area: PropTypes.string,
  distance: PropTypes.number,
  addressDisplay: PropTypes.string,
  isLoading: PropTypes.bool
};

const ColleaguesLayout = styled.div`
  color: ${getTokenValue(THEME_TOKENS.base.color.secondaryText)};
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const ColleaguesDisplay = styled.div`
  font-size: 12px;
  line-height: 1.2;
`;
const ColleaguesWrapper = _ref => {
  let {
    children
  } = _ref;
  return /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(IconLine, {
    size: "small",
    icon: "Briefcase",
    mr: SPACE.size4
  }), /*#__PURE__*/React.createElement(ColleaguesDisplay, null, children));
};
ColleaguesWrapper.propTypes = {
  children: PropTypes.node
};
const getMultipleColleagueDisplay = (names, visited) => {
  const visitedCalc = visited - 1;
  return /*#__PURE__*/React.createElement(ColleaguesWrapper, null, /*#__PURE__*/React.createElement("b", null, names[0]), " and ", visitedCalc, " ", `other${visitedCalc > 1 ? 's' : ''}`, " have stayed here before");
};
const getSingleColleagueDisplay = names => {
  return /*#__PURE__*/React.createElement(ColleaguesWrapper, null, /*#__PURE__*/React.createElement("b", null, names[0]), " has stayed here before");
};
const ColleaguesHaveStayed = _ref2 => {
  let {
    yourStays,
    colleagueNames,
    colleaguesVisited
  } = _ref2;
  const showYourStays = yourStays > 0;
  const showMultipleColleagues = colleaguesVisited > 1 && (colleagueNames === null || colleagueNames === void 0 ? void 0 : colleagueNames.length) > 0;
  const showSingleColleague = (colleagueNames === null || colleagueNames === void 0 ? void 0 : colleagueNames.length) === 1;
  const showColleaguesNumOnly = colleaguesVisited > 0 && (colleagueNames === null || colleagueNames === void 0 ? void 0 : colleagueNames.length) === 0;
  if (showYourStays || showMultipleColleagues || showSingleColleague || showColleaguesNumOnly) {
    return /*#__PURE__*/React.createElement(ColleaguesLayout, null, showYourStays && /*#__PURE__*/React.createElement(Flex, {
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(IconLine, {
      size: "small",
      icon: "User",
      mr: SPACE.size4
    }), /*#__PURE__*/React.createElement(ColleaguesDisplay, null, "You have stayed here ", /*#__PURE__*/React.createElement("b", null, yourStays), " ", `time${yourStays > 1 ? 's' : ''}`, " before")), showMultipleColleagues && getMultipleColleagueDisplay(colleagueNames, colleaguesVisited), showSingleColleague && getSingleColleagueDisplay(colleagueNames), showColleaguesNumOnly && /*#__PURE__*/React.createElement(ColleaguesWrapper, null, colleaguesVisited, " of your colleagues have stayed at this hotel"));
  }
  return null;
};
ColleaguesHaveStayed.propTypes = {
  yourStays: PropTypes.number,
  colleagueNames: PropTypes.arrayOf(PropTypes.string),
  colleaguesVisited: PropTypes.number
};

/* eslint-disable max-len */
const NoPhotoContainer = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: ${_ref => {
  let {
    gap
  } = _ref;
  return gap;
}}px;
  background: radial-gradient(circle, rgba(255, 255, 255, 1) 0%, rgba(232, 232, 232, 1) 100%);

  svg {
    opacity: 0.4;
  }
`;
const NoPhotoText = styled.div`
  font-size: ${_ref2 => {
  let {
    size
  } = _ref2;
  return size;
}};
  font-weight: 600;
  letter-spacing: 1.5;
  color: black;
  opacity: 0.5;
`;
const CameraGraphic = _ref3 => {
  let {
    size
  } = _ref3;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 96 96"
  }, /*#__PURE__*/React.createElement("path", {
    d: "m88.9,28.71h-20.86l-4.39-13.39c-.45-1.39-1.75-2.32-3.21-2.32h-24.91c-1.45,0-2.74.92-3.2,2.3l-4.51,13.42h-5.16v-5.71h-10.26v5.71h-5.31c-2.81,0-5.1,2.28-5.1,5.08v44.13c0,2.8,2.29,5.08,5.1,5.08h81.81c2.81,0,5.1-2.28,5.1-5.08v-44.13c0-2.8-2.29-5.08-5.1-5.08Zm-74.97-3.84h7.2v1.28h-7.2v-1.28Zm0,2.13h7.2v1.69h-7.2v-1.69Zm21.49-10.67s.06-.08.11-.08h24.91c.05,0,.1.03.11.08l4.06,12.38H31.26l4.17-12.38ZM7.1,31.96h81.81c1.01,0,1.83.82,1.83,1.83v13.98h-27c-2.95-5.68-8.89-9.58-15.74-9.58s-12.79,3.9-15.74,9.58H5.26v-13.98c0-1.01.82-1.83,1.83-1.83Zm55.37,23.89c0,7.95-6.49,14.42-14.47,14.42s-14.47-6.47-14.47-14.42,6.49-14.42,14.47-14.42,14.47,6.47,14.47,14.42Zm26.44,23.89H7.1c-1.01,0-1.83-.82-1.83-1.83v-26.9h25.7c-.44,1.54-.69,3.16-.69,4.83,0,9.74,7.95,17.67,17.73,17.67s17.73-7.93,17.73-17.67c0-1.68-.25-3.3-.69-4.83h25.7v26.9c0,1.01-.82,1.83-1.83,1.83Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m48,43.93c-6.6,0-11.97,5.35-11.97,11.93s5.37,11.93,11.97,11.93,11.97-5.35,11.97-11.93-5.37-11.93-11.97-11.93Zm0,22.34c-5.76,0-10.44-4.67-10.44-10.41s4.68-10.41,10.44-10.41,10.44,4.67,10.44,10.41-4.68,10.41-10.44,10.41Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m58.18,19.26c-.24-.75-.91-1.26-1.67-1.26h-17.01c-.76,0-1.43.5-1.67,1.25l-1.73,5.35c-.18.56-.09,1.17.24,1.65.33.48.86.76,1.43.76h20.42c.56,0,1.09-.28,1.42-.75.33-.47.42-1.09.25-1.64l-1.69-5.35Zm.21,6.06s-.08.1-.19.1h-20.42c-.1,0-.16-.06-.19-.1-.03-.04-.06-.11-.03-.22l1.73-5.35c.03-.1.12-.16.22-.16h17.01c.1,0,.19.07.22.17l1.69,5.35c.03.1,0,.18-.03.22Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m84.65,41c1.8,0,3.26-1.46,3.26-3.25s-1.46-3.25-3.26-3.25-3.26,1.46-3.26,3.25,1.46,3.25,3.26,3.25Zm0-4.68c.79,0,1.44.64,1.44,1.43s-.64,1.43-1.44,1.43-1.44-.64-1.44-1.43.64-1.43,1.44-1.43Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m75.52,41c1.8,0,3.26-1.46,3.26-3.25s-1.46-3.25-3.26-3.25-3.26,1.46-3.26,3.25,1.46,3.25,3.26,3.25Zm0-4.68c.79,0,1.44.64,1.44,1.43s-.64,1.43-1.44,1.43-1.44-.64-1.44-1.43.64-1.43,1.44-1.43Z"
  }));
};
CameraGraphic.propTypes = {
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
const NoPhotoAvailable = _ref4 => {
  let {
    iconSize = 60,
    fontSize = 14,
    gap = 4
  } = _ref4;
  const iconSizeVal = typeof iconSize === 'number' ? `${iconSize}px` : iconSize;
  const fontSizeVal = typeof fontSize === 'number' ? `${fontSize}px` : fontSize;
  return /*#__PURE__*/React.createElement(NoPhotoContainer, {
    gap: gap
  }, /*#__PURE__*/React.createElement(Box, {
    pb: "4px"
  }, /*#__PURE__*/React.createElement(CameraGraphic, {
    size: iconSizeVal
  })), /*#__PURE__*/React.createElement(NoPhotoText, {
    size: fontSizeVal
  }, "NO PHOTO"), /*#__PURE__*/React.createElement(NoPhotoText, {
    size: fontSizeVal
  }, "AVAILABLE"));
};
NoPhotoAvailable.propTypes = {
  iconSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  gap: PropTypes.string
};

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

const HotelContentDimensions = styled(Flex)`
  flex-direction: column;
  // the content in OopPriceSavedCol column provides the default height on mobile
  height: unset;
  flex: 0 0 100%;
`;
const HotelOptionDetails = styled(Flex)`
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  padding: 12px 12px 12px 14px;
`;

// add height: inherit; to this if the height becomes a problem
const HotelNamePriceRow = styled(Flex)`
  justify-content: space-between;
`;
const CarouselMediumCol = styled(Flex)`
  display: flex;
  flex-direction: column;
  flex: 0 0 180px;
`;
const NameRatingSection = styled(Flex)`
  width: 100%;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  padding-right: 12px;
`;

// export const priceColHeight = '70px';

const OopPriceSection = styled(Flex)`
  min-width: 68px;
  justify-content: flex-end;
`;
const AddressActionRow = styled(Flex)`
  width: 100%;
  align-items: flex-end;
`;
const viewDetailsWidth = '80px';
const ViewDetailsButton = styled(Button)`
  width: ${viewDetailsWidth};
`;
styled.div`
  width: 22px;
  height: 22px;
  margin-top: auto;
`;
const PlaceholderBkgd = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: lightgray;
`;
const calculateTabCardPaddingY = () => [3, 3, '24px'];
const calculateTabCardPaddingX = noPadding => noPadding ? [0] : [3, 3, '24px'];
const TabCard = styled(Box)`
  border-radius: 0;
  background-color: white;

  ${_ref => {
  let {
    standalone
  } = _ref;
  return !standalone && css`
      border-top: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
    `;
}};
`;
const TabCardContent = _ref2 => {
  let {
    children,
    standalone,
    ...rest
  } = _ref2;
  return /*#__PURE__*/React.createElement(TabCard, _extends({
    standalone: standalone
  }, rest), /*#__PURE__*/React.createElement(Box, {
    py: calculateTabCardPaddingY(),
    px: calculateTabCardPaddingX(standalone)
  }, children));
};
TabCardContent.propTypes = {
  standalone: PropTypes.bool,
  children: PropTypes.node
};
const TabBox = styled(Box)`
  width: 100%;
  border-radius: 0;

  ${_ref3 => {
  let {
    standalone
  } = _ref3;
  return !standalone && css`
      background-color: white;
    `;
}};
`;

// same as TabCardContent but no box-shadow
const TabCardBox = _ref4 => {
  let {
    children,
    standalone,
    ...rest
  } = _ref4;
  return /*#__PURE__*/React.createElement(TabBox, _extends({
    standalone: standalone
  }, rest), /*#__PURE__*/React.createElement(Box, {
    py: calculateTabCardPaddingY(),
    px: calculateTabCardPaddingX(standalone)
  }, children));
};
TabCardBox.propTypes = {
  standalone: PropTypes.bool,
  children: PropTypes.node
};
const Divider = styled(Box)`
  width: 100%;
  height: 1px;
  background-color: #d1d1d1;
`;
const SingleHotelPhoto = styled(Box)`
  position: relative;
  height: 225px;
  border-radius: 10px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    aspect-ratio: 3 / 1;
  }
`;
const SingleRoomPhoto = styled(SingleHotelPhoto)`
  /* this is to support full width on small desktop/tablet */
  display: grid;
`;
const PhoneNumber = styled.a`
  text-decoration: none;
  color: ${getThemeProp('global.link--color')};
  font-weight: 600;
  white-space: nowrap;

  :hover {
    text-decoration: underline;
  }

  :visited {
    color: ${getThemeProp('global.link--color')};
    text-decoration-color: ${getThemeProp('global.link--color')};
  }
`;
const AmenitiesBulletList = styled.ul`
  padding-inline-start: 0;

  li {
    list-style: none;
    position: relative;
    line-height: 18px;
    padding-left: 14px;
    margin-bottom: 10px;

    &:last-of-type {
      margin-bottom: 0;
    }

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 8px;
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background-color: ${getThemeProp('global.textBody--color')};
      opacity: 0.5;
    }
  }
`;

const hotelImageHeight = 150;
const HotelImageContainer$2 = styled.div`
  position: relative;
  width: 100%;
  height: ${hotelImageHeight}px;
`;
const HotelImage$1 = styled.img`
  width: 100%;
  height: ${hotelImageHeight}px;
  object-fit: cover;
  aspect-ratio: 2 / 1;
`;
const BadgeContainer$2 = styled.div`
  position: absolute;
  top: 8px;
  left: 0;
  z-index: ${ZINDEX.OVERLAY};
`;
const PlaceholderContainer$1 = styled.div`
  position: relative;
  width: 100%;
  height: ${hotelImageHeight}px;
`;
const CloseButton = styled(Button)`
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 18px;
  z-index: ${ZINDEX.OVERLAY};
`;
const HotelImageWithClose = _ref => {
  let {
    hotelImages,
    isPreferred,
    soldOut,
    onClose
  } = _ref;
  const [firstImgLoaded, setFirstImgLoaded] = useState(false);
  const onLoad = () => {
    setFirstImgLoaded(true);
  };
  return /*#__PURE__*/React.createElement(HotelImageContainer$2, {
    soldOut: soldOut
  }, /*#__PURE__*/React.createElement(CloseButton, {
    size: "small",
    icon: /*#__PURE__*/React.createElement(IconClose, null),
    bordered: true,
    round: true,
    onClick: onClose
  }), hotelImages.length > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, isPreferred && /*#__PURE__*/React.createElement(BadgeContainer$2, null, /*#__PURE__*/React.createElement(PreferredBadge, null)), /*#__PURE__*/React.createElement(PlaceholderContainer$1, {
    key: hotelImages[0]
  }, /*#__PURE__*/React.createElement(HotelImage$1, {
    alt: "Hotel",
    src: hotelImages[0],
    onLoad: () => onLoad()
  }), !firstImgLoaded && /*#__PURE__*/React.createElement(PlaceholderBkgd, null))) : /*#__PURE__*/React.createElement(NoPhotoAvailable, null));
};
HotelImageWithClose.propTypes = {
  hotelImages: PropTypes.arrayOf(PropTypes.string),
  isPreferred: PropTypes.bool,
  soldOut: PropTypes.bool,
  onClose: PropTypes.func.isRequired
};

const hotelOptionCardProps = {
  hotelData: PropTypes.shape({
    area: PropTypes.string,
    distance: PropTypes.number,
    hotelAddress: PropTypes.shape({
      address1: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string
    }),
    hotelImages: PropTypes.arrayOf(PropTypes.string),
    propId: PropTypes.string.isRequired,
    hotelName: PropTypes.string.isRequired,
    starRating: PropTypes.number,
    reviewRate: PropTypes.number,
    reviewCount: PropTypes.number,
    policyViolations: PropTypes.arrayOf(PropTypes.string),
    averageNightlyRate: PropTypes.number,
    averageBaseRate: PropTypes.number,
    isPreferred: PropTypes.bool,
    soldOut: PropTypes.bool,
    travelerBookings: PropTypes.number,
    colleagueNames: PropTypes.arrayOf(PropTypes.string),
    colleaguesVisited: PropTypes.number,
    vendorPromo: PropTypes.shape({})
  }),
  active: PropTypes.bool
};

const HotelCard$1 = styled(Box)`
  background-color: white;
  border-radius: 15px;
  overflow: hidden;
  opacity: 0;
  transform: translateY(50px);
  transition: transform 0.3s ease-out, opacity 0.3s;

  ${_ref => {
  let {
    shown
  } = _ref;
  return shown && css`
      transform: translateY(0px);
      opacity: 1;
    `;
}};
`;
const HotelMapCardMobile = _ref2 => {
  let {
    hotelData,
    showRefundableRates,
    openRooms,
    // triggers when most of the card is clicked
    openOverview,
    onClose,
    invitationToken
  } = _ref2;
  const {
    hotelName,
    hotelRating,
    reviewRate,
    reviewCount,
    area,
    distance,
    hotelAddress,
    policyViolations,
    averageNightlyRate,
    averageBaseRate,
    hasRefundableRate,
    averageNightlyRateRefundable,
    isPreferred,
    soldOut,
    hotelImages,
    travelerBookings,
    colleaguesVisited,
    colleagueNames
  } = hotelData;
  const showAddress = hotelAddress === null || hotelAddress === void 0 ? void 0 : hotelAddress.address1;
  const [rendered, setOnRendered] = useState(false);
  const nightlyRate = showRefundableRates && hasRefundableRate ? averageNightlyRateRefundable : averageNightlyRate;
  useEffect(() => {
    setOnRendered(true);
  }, []);
  const handleCardClick = e => {
    /**
     * Elements with the attribute `data-click-target` have their own click handler so we don't want the
     * `openRooms` function to execute in those scenarios.
     *
     * The `.closest()` method traverses up the DOM from the clicked element to handle the scenario when the target
     * element clicked is a child of the parent element that has its own click handler.
     */
    const hasTarget = e.target.closest('[data-click-target]');
    if (!hasTarget) {
      openRooms();
    }
  };
  return /*#__PURE__*/React.createElement(HotelCard$1, {
    shown: rendered
  }, /*#__PURE__*/React.createElement(HotelImageWithClose, {
    hotelImages: hotelImages,
    isPreferred: isPreferred,
    soldOut: soldOut,
    onClose: onClose
  }), /*#__PURE__*/React.createElement(HotelContentDimensions, null, /*#__PURE__*/React.createElement(HotelOptionDetails, {
    onClick: handleCardClick
  }, /*#__PURE__*/React.createElement(HotelNamePriceRow, null, /*#__PURE__*/React.createElement(NameRatingSection, null, /*#__PURE__*/React.createElement(NameRating, {
    hotelName: hotelName,
    starRating: hotelRating,
    reviewRate: reviewRate,
    reviewCount: reviewCount,
    onNameClick: openRooms
  }), /*#__PURE__*/React.createElement(ColleaguesHaveStayed, {
    yourStays: travelerBookings,
    colleaguesVisited: colleaguesVisited,
    colleagueNames: colleagueNames
  })), /*#__PURE__*/React.createElement(OopPriceSection, null, /*#__PURE__*/React.createElement(OutOfPolicy, {
    mt: 1,
    mr: 1,
    policyViolations: policyViolations,
    soldOut: soldOut,
    iconOnly: true,
    invitationToken: invitationToken
  }), /*#__PURE__*/React.createElement(Price, {
    nightlyRate: nightlyRate,
    baseRate: averageBaseRate,
    soldOut: soldOut
  }))), /*#__PURE__*/React.createElement(AddressActionRow, null, showAddress && /*#__PURE__*/React.createElement(HotelAddress, {
    area: area,
    distance: distance,
    addressDisplay: formatLocationAddress(hotelAddress === null || hotelAddress === void 0 ? void 0 : hotelAddress.address1, hotelAddress === null || hotelAddress === void 0 ? void 0 : hotelAddress.city, hotelAddress === null || hotelAddress === void 0 ? void 0 : hotelAddress.state)
  }), /*#__PURE__*/React.createElement(Box, {
    pl: 2,
    ml: "auto"
  }, soldOut ? null : /*#__PURE__*/React.createElement(ViewDetailsButton, {
    text: "Details",
    onClick: openOverview,
    "data-click-target": "rooms",
    size: "small",
    borderedLight: true
  }))))));
};
HotelMapCardMobile.propTypes = {
  ...hotelOptionCardProps,
  showRefundableRates: PropTypes.bool
};

/* eslint-disable max-len */
const carouselWidthMedium = 180;
const carouselHeightSmall = 150;
const HotelImageContainer$1 = styled.div`
  position: relative;
  width: 100%;
  height: ${carouselHeightSmall}px;
  border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)}
    ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)} 0 0;
  overflow: hidden;

  ${media.Medium`
    border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)} 0 0
    ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)};
    width: ${carouselWidthMedium}px;
    height: ${_ref => {
  let {
    heightMedium
  } = _ref;
  return heightMedium;
}}px;
  `};
`;
const LoadingImageBkgd = styled(HotelImageContainer$1)`
  position: relative;
  ${skeletonLoadingStyles};
`;
const LoadingImageContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
const HotelImage = styled.img`
  /* this fixes mobile carousel images in Safari */
  width: -webkit-fill-available;
  height: ${carouselHeightSmall}px;
  object-fit: cover;
  display: ${_ref2 => {
  let {
    renderFallback
  } = _ref2;
  return renderFallback ? 'none' : 'block';
}};
  aspect-ratio: 2 / 1;

  ${media.Medium`
    width: ${carouselWidthMedium}px;
    height: ${_ref3 => {
  let {
    heightMedium
  } = _ref3;
  return heightMedium;
}}px;
  `};
`;
const BadgeContainer$1 = styled.div`
  position: absolute;
  top: 8px;
  left: 0;
  z-index: ${ZINDEX.DROPDOWN};
`;
const PlaceholderContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${carouselHeightSmall}px;

  ${media.Medium`
    width: ${carouselWidthMedium}px;
    height: ${_ref4 => {
  let {
    heightMedium
  } = _ref4;
  return heightMedium;
}}px;
  `};
`;
const ImageLoadingGraphic = _ref5 => {
  let {
    size = 80
  } = _ref5;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 96 96",
    fill: "rgb(196, 200, 207)"
  }, /*#__PURE__*/React.createElement("path", {
    id: "b",
    "data-name": "img",
    d: "m8.94,31.44c0-5.2,4.23-9.44,9.43-9.44s9.44,4.24,9.44,9.44-4.24,9.43-9.44,9.43-9.43-4.23-9.43-9.43Zm-5.59,36.32h74.19l14.63.15c.87,0,1.31-1.05.69-1.66l-34.37-33.63c-.33-.3-.77-.46-1.22-.45-.45.03-.88.22-1.18.55l-14.17,15.52,6.45,6.45c.66.66.66,1.74,0,2.4-.33.33-.77.5-1.2.5s-.87-.17-1.2-.5l-6.48-6.19-8.04-8.04c-.63-.63-1.65-.66-2.32-.07L2.72,66.06c-.67.59-.25,1.69.64,1.69Zm88.3,3.39H3.65c-.91,0-1.65.76-1.65,1.7v.61c0,.94.74,1.7,1.65,1.7h88c.91,0,1.65-.76,1.65-1.7v-.61c0-.94-.74-1.7-1.65-1.7Z"
  }));
};
ImageLoadingGraphic.propTypes = {
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
const HotelImages = _ref6 => {
  let {
    heightMedium,
    hotelImages,
    isPreferred,
    soldOut,
    isLoading
  } = _ref6;
  const [firstImgLoaded, setFirstImgLoaded] = useState(false);
  const [renderFallback, setRenderFallback] = useState(false);
  const onLoad = index => {
    if (index === 0) {
      setFirstImgLoaded(true);
    }
  };
  if (isLoading) {
    return /*#__PURE__*/React.createElement(HotelImageContainer$1, {
      heightMedium: heightMedium
    }, /*#__PURE__*/React.createElement(LoadingImageContainer, null, /*#__PURE__*/React.createElement(ImageLoadingGraphic, null)), /*#__PURE__*/React.createElement(LoadingImageBkgd, {
      heightMedium: heightMedium
    }));
  }
  return /*#__PURE__*/React.createElement(HotelImageContainer$1, {
    heightMedium: heightMedium,
    soldOut: soldOut
  }, (hotelImages === null || hotelImages === void 0 ? void 0 : hotelImages.length) > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, isPreferred && /*#__PURE__*/React.createElement(BadgeContainer$1, null, /*#__PURE__*/React.createElement(PreferredBadge, null)), /*#__PURE__*/React.createElement(Carousel, {
    innerOverlayButtons: true,
    hoverButtons: true,
    loopSlides: false,
    showDots: true
  }, hotelImages.map((h, index) => /*#__PURE__*/React.createElement(PlaceholderContainer, {
    heightMedium: heightMedium,
    key: h
  }, /*#__PURE__*/React.createElement(HotelImage, {
    heightMedium: heightMedium,
    alt: "Hotel",
    src: h,
    onLoad: () => onLoad(index),
    onError: () => setRenderFallback(true),
    renderFallback: renderFallback
  }), renderFallback && /*#__PURE__*/React.createElement(NoPhotoAvailable, null), !firstImgLoaded && !renderFallback && /*#__PURE__*/React.createElement(PlaceholderBkgd, null))))) : /*#__PURE__*/React.createElement(React.Fragment, null, isPreferred && /*#__PURE__*/React.createElement(BadgeContainer$1, null, /*#__PURE__*/React.createElement(PreferredBadge, null)), /*#__PURE__*/React.createElement(NoPhotoAvailable, null)));
};
HotelImages.propTypes = {
  hotelImages: PropTypes.arrayOf(PropTypes.string),
  isPreferred: PropTypes.bool,
  soldOut: PropTypes.bool,
  heightMedium: PropTypes.number,
  isLoading: PropTypes.bool
};

const hotelCardHeightMedium = 150;
const extraMiddleHeight = 20;
const getCardHeightMedium = (colleagues, yourStays) => {
  if (yourStays > 0 && colleagues) {
    return hotelCardHeightMedium + extraMiddleHeight + 10;
  }
  if (colleagues) {
    return hotelCardHeightMedium + extraMiddleHeight;
  }
  return hotelCardHeightMedium;
};
const HotelCard = styled(Flex)`
  flex-direction: column;
  width: 100%;
  background-color: white;
  box-shadow: 0 0 0 1px ${getTokenValue(THEME_TOKENS.card.color.border)} inset;
  border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)};

  ${_ref => {
  let {
    active
  } = _ref;
  return active && css`
      box-shadow: none;
      outline-size: 2px;
      outline-style: solid;
      outline-offset: 0px;
      outline-color: ${getTokenValue(THEME_TOKENS.base.color.action)};
    `;
}};
`;
const HotelCardWrapper = styled.div`
  ${_ref2 => {
  let {
    soldOut
  } = _ref2;
  return soldOut && css`
      filter: grayscale();
    `;
}}

  @media (hover: hover) {
    &:hover ${HotelCard} {
      ${props => !props.isLoading && css`
          cursor: pointer;
          background-color: ${lighten(getThemeProp('global.light--color')(props), 20)};
        `};
    }
  }
`;
const HotelContent = styled(HotelContentDimensions)`
  ${media.Medium`
    height: ${_ref3 => {
  let {
    heightMedium
  } = _ref3;
  return heightMedium;
}}px;
    flex-direction: row;
  `};
`;
const HotelOptionDet = styled(HotelOptionDetails)`
  ${media.Medium`
    height: ${_ref4 => {
  let {
    heightMedium
  } = _ref4;
  return heightMedium;
}}px;
  `};
`;
const NameAndRating = styled(NameRatingSection)`
  ${media.Medium`
    gap: 8px;
  `};
`;
const HotelOptionCard = _ref5 => {
  let {
    hotelData = {},
    active = false,
    openRooms,
    // triggers when most of the card is clicked
    openOverview,
    openReviews,
    pricesLoading = false,
    hotelsLoading = false,
    invitationToken = null,
    showRefundableRates = false
  } = _ref5;
  const {
    hotelName,
    hotelRating,
    reviewRate,
    reviewCount,
    area,
    distance,
    hotelAddress,
    policyViolations,
    averageNightlyRate,
    hasRefundableRate,
    averageNightlyRateRefundable,
    averageBaseRate,
    isPreferred,
    soldOut,
    hotelImages,
    travelerBookings,
    colleaguesVisited,
    colleagueNames
  } = hotelData;

  // colleaguesVisited is specific to a2b
  const hasColleagues = colleaguesVisited > 0 || (colleagueNames === null || colleagueNames === void 0 ? void 0 : colleagueNames.length) > 0;
  const showAddress = (hotelAddress === null || hotelAddress === void 0 ? void 0 : hotelAddress.address1) || hotelsLoading;
  const handleCardClick = e => {
    /**
     * Elements with the attribute `data-click-target` have their own click handler so we don't want the
     * `openRooms` function to execute in those scenarios.
     *
     * The `.closest()` method traverses up the DOM from the clicked element to handle the scenario when the target
     * element clicked is a child of the parent element that has its own click handler.
     */
    const hasTarget = e.target.closest('[data-click-target]');
    if (!hasTarget) {
      openRooms();
    }
  };
  return /*#__PURE__*/React.createElement(HotelCardWrapper, {
    isLoading: hotelsLoading || pricesLoading,
    soldOut: soldOut
  }, /*#__PURE__*/React.createElement(HotelCard, {
    active: active
  }, /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(HotelImages, {
    hotelImages: hotelImages,
    isPreferred: isPreferred,
    soldOut: soldOut,
    isLoading: hotelsLoading
  })), /*#__PURE__*/React.createElement(HotelContent, {
    heightMedium: getCardHeightMedium(hasColleagues, travelerBookings)
  }, /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(CarouselMediumCol, null, /*#__PURE__*/React.createElement(HotelImages, {
    heightMedium: getCardHeightMedium(hasColleagues, travelerBookings),
    hotelImages: hotelImages,
    isPreferred: isPreferred,
    soldOut: soldOut,
    isLoading: hotelsLoading
  }))), /*#__PURE__*/React.createElement(HotelOptionDet, {
    onClick: handleCardClick,
    heightMedium: getCardHeightMedium(hasColleagues, travelerBookings)
  }, /*#__PURE__*/React.createElement(HotelNamePriceRow, null, /*#__PURE__*/React.createElement(NameAndRating, null, /*#__PURE__*/React.createElement(NameRating, {
    hotelName: hotelName,
    starRating: hotelRating,
    reviewRate: reviewRate,
    reviewCount: reviewCount,
    nameLoading: hotelsLoading,
    reviewLoading: pricesLoading,
    onReviewsSummaryClick: openReviews,
    onNameClick: openRooms
  }), !hotelsLoading && /*#__PURE__*/React.createElement(ColleaguesHaveStayed, {
    yourStays: travelerBookings,
    colleaguesVisited: colleaguesVisited,
    colleagueNames: colleagueNames
  })), /*#__PURE__*/React.createElement(OopPriceSection, null, /*#__PURE__*/React.createElement(OutOfPolicy, {
    mt: 1,
    mr: 1,
    policyViolations: policyViolations,
    isLoading: pricesLoading,
    invitationToken: invitationToken,
    soldOut: soldOut
  }), /*#__PURE__*/React.createElement(Price, {
    nightlyRate: hasRefundableRate && showRefundableRates ? averageNightlyRateRefundable : averageNightlyRate,
    baseRate: averageBaseRate,
    soldOut: soldOut,
    isLoading: pricesLoading || (averageNightlyRate === 0 || !averageNightlyRate) && !soldOut
  }))), /*#__PURE__*/React.createElement(AddressActionRow, null, showAddress && /*#__PURE__*/React.createElement(HotelAddress, {
    area: area,
    distance: distance,
    addressDisplay: formatLocationAddress(hotelAddress === null || hotelAddress === void 0 ? void 0 : hotelAddress.address1, hotelAddress === null || hotelAddress === void 0 ? void 0 : hotelAddress.city, hotelAddress === null || hotelAddress === void 0 ? void 0 : hotelAddress.state),
    isLoading: hotelsLoading
  }), /*#__PURE__*/React.createElement(Box, {
    pl: 2,
    ml: "auto"
  }, pricesLoading || soldOut ? /*#__PURE__*/React.createElement(Box, {
    width: soldOut ? 0 : viewDetailsWidth
  }) : /*#__PURE__*/React.createElement(ViewDetailsButton, {
    text: "Details",
    onClick: openOverview,
    "data-click-target": "rooms",
    size: "small",
    borderedLight: true
  })))))));
};
HotelOptionCard.propTypes = {
  ...hotelOptionCardProps
};

const RatingMiniContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 6px;
`;
const GuestRatingTotal = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 14px;
  font-size: 10px;
  line-height: 1;
  font-weight: bold;
  color: #fff;
  background-color: ${_ref => {
  let {
    ratingNumber
  } = _ref;
  return getGuestRatingColor(ratingNumber);
}};
  border-radius: 2px;
  margin-left: 6px;
  margin-right: 4px;
  transform: translateY(-1px);
`;
const RatingSummary = styled.span`
  font-size: 12px;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
`;
const HotelClass = styled.span`
  font-size: 12px;
`;
const RatingMini = _ref2 => {
  let {
    starRating,
    reviewRate,
    reviewCount
  } = _ref2;
  const hasStarRating = starRating > 0;
  const hasGuestReviews = reviewCount > 0;
  if (hasStarRating) {
    return /*#__PURE__*/React.createElement(RatingMiniContainer, null, /*#__PURE__*/React.createElement(HotelClass, null, starRating, "-Star Hotel"), hasGuestReviews && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(GuestRatingTotal, {
      ratingNumber: getGuestRating(reviewRate)
    }, getGuestRating(reviewRate))), /*#__PURE__*/React.createElement(RatingSummary, null, /*#__PURE__*/React.createElement("strong", null, getRatingName(getGuestRating(reviewRate))))));
  }
  return null;
};
RatingMini.propTypes = {
  starRating: PropTypes.number,
  reviewRate: PropTypes.number,
  reviewCount: PropTypes.number
};

const PriceContainer$1 = styled.div`
  display: flex;
  align-items: flex-end;
  align-self: flex-end;
`;
const PriceStrike = styled.div`
  display: inline-block;
  position: relative;
  opacity: 0.8;
  font-size: 14px;
  color: #535353;
  text-align: right;
  margin-right: 12px;
  margin-bottom: 2px;
  text-decoration: line-through;
`;
const SoldOut = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: gray;
  text-align: center;
`;
const PriceMini = _ref => {
  let {
    nightlyRate,
    baseRate,
    soldOut,
    isPreferred
  } = _ref;
  const showPriceStrike = baseRate > nightlyRate;
  return /*#__PURE__*/React.createElement(PriceContainer$1, null, soldOut ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(SoldOut, null, "Sold Out")) : /*#__PURE__*/React.createElement(React.Fragment, null, showPriceStrike && !isPreferred ? /*#__PURE__*/React.createElement(PriceStrike, null, "$", baseRate.toFixed(0)) : null, nightlyRate !== 0 && /*#__PURE__*/React.createElement(PriceButton, {
    price: nightlyRate,
    priceSize: 18,
    labelSize: 9
  })));
};
PriceMini.propTypes = {
  nightlyRate: PropTypes.number,
  baseRate: PropTypes.number,
  soldOut: PropTypes.bool,
  isPreferred: PropTypes.bool
};

const HotelDetails$1 = styled(Flex)`
  position: relative;
  flex-direction: column;
  justify-content: space-between;
  width: 160px;
  margin-left: 8px;
`;
const NameRatingAddress = styled(Flex)`
  flex-direction: column;
`;
const HotelImageContainer = styled.div`
  position: relative;
  width: 90px;
  height: 130px;

  img {
    width: 90px;
    height: 130px;
    border-radius: 6px;
    object-fit: cover;
    aspect-ratio: 2 / 1;
  }
`;
const BadgeContainer = styled.div`
  position: absolute;
  top: 8px;
  left: 0;
  z-index: ${ZINDEX.DROPDOWN};
`;
const HotelName$1 = styled.div`
  font-size: 13px;
  font-weight: bold;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const HotelOptionMini = _ref => {
  let {
    showRefundableRates,
    hotelData
  } = _ref;
  const {
    hotelName,
    hotelRating,
    reviewRate,
    reviewCount,
    hotelAddress,
    averageNightlyRate,
    averageBaseRate,
    hasRefundableRate,
    averageNightlyRateRefundable,
    isPreferred,
    soldOut,
    hotelImages,
    policyViolations,
    distance
  } = hotelData;
  const isOutOfPolicy = policyViolations && policyViolations.length > 0;
  const nightlyRate = showRefundableRates && hasRefundableRate ? averageNightlyRateRefundable : averageNightlyRate;
  const milesAway = distance === null || distance === void 0 ? void 0 : distance.toFixed(1);
  return /*#__PURE__*/React.createElement(OptionMini, {
    isOutOfPolicy: isOutOfPolicy
  }, /*#__PURE__*/React.createElement(HotelImageContainer, null, isPreferred ? /*#__PURE__*/React.createElement(BadgeContainer, null, /*#__PURE__*/React.createElement(PreferredBadge, null)) : null, hotelImages.length > 0 ? /*#__PURE__*/React.createElement("img", {
    alt: "Hotel",
    src: hotelImages[0]
  }) : /*#__PURE__*/React.createElement(NoPhotoAvailable, {
    iconSize: 35,
    fontSize: 9,
    gap: "1px"
  })), /*#__PURE__*/React.createElement(HotelDetails$1, null, /*#__PURE__*/React.createElement(NameRatingAddress, null, /*#__PURE__*/React.createElement(HotelName$1, null, hotelName), /*#__PURE__*/React.createElement(RatingMini, {
    starRating: hotelRating,
    reviewRate: reviewRate,
    reviewCount: reviewCount
  }), /*#__PURE__*/React.createElement(AddressMini, null, formatLocationAddress(hotelAddress.address1, hotelAddress.city, hotelAddress.state)), distance > 0 && /*#__PURE__*/React.createElement(Box, {
    pt: "2px"
  }, /*#__PURE__*/React.createElement(AreaDistance, {
    distance: milesAway
  }))), /*#__PURE__*/React.createElement(Flex, {
    alignItems: "flex-end",
    justifyContent: "flex-end",
    mt: "6px"
  }, /*#__PURE__*/React.createElement(PriceMini, {
    nightlyRate: nightlyRate,
    baseRate: averageBaseRate,
    soldOut: soldOut,
    isPreferred: isPreferred
  }))));
};
HotelOptionMini.propTypes = {
  ...hotelOptionCardProps,
  showRefundableRates: PropTypes.bool
};

const PhotoGrid = styled.div`
  position: relative;
  display: grid;
  height: 225px;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: 110px;
  gap: 6px;
  border-radius: 10px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
const FirstPhoto = styled.img`
  grid-column: span 2;
  grid-row: span 2;
`;
const SecondPhoto = styled.img`
  grid-column: span 1;
  grid-row: span 2;
`;
const ButtonContent = styled.div`
  position: absolute;
  bottom: 12px;
  right: 12px;
`;
const HotelPhotoGrid = _ref => {
  let {
    images,
    buttonContent
  } = _ref;
  const [imgError, setImgError] = useState(false);
  if (images.length > 4 && !imgError) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(SingleHotelPhoto, null, /*#__PURE__*/React.createElement("img", {
      src: getLargeImgUrl(images[0]),
      alt: "",
      onError: () => setImgError(true)
    }), /*#__PURE__*/React.createElement(ButtonContent, null, buttonContent))), /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(PhotoGrid, null, /*#__PURE__*/React.createElement(FirstPhoto, {
      src: getLargeImgUrl(images[0]),
      alt: "",
      onError: () => setImgError(true)
    }), /*#__PURE__*/React.createElement(SecondPhoto, {
      src: getLargeImgUrl(images[1]),
      alt: "",
      onError: () => setImgError(true)
    }), /*#__PURE__*/React.createElement("img", {
      src: getLargeImgUrl(images[2]),
      alt: "",
      onError: () => setImgError(true)
    }), /*#__PURE__*/React.createElement("img", {
      src: getLargeImgUrl(images[3]),
      alt: "",
      onError: () => setImgError(true)
    }), /*#__PURE__*/React.createElement(ButtonContent, null, buttonContent))));
  }
  return null;
};
HotelPhotoGrid.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string),
  buttonContent: PropTypes.node
};

const CheckInOutTime = _ref => {
  let {
    checkInTime,
    checkOutTime,
    ...rest
  } = _ref;
  if (checkInTime || checkOutTime) {
    const checkInOutObj = {};
    if (checkInTime) {
      checkInOutObj.label = 'Check In';
      checkInOutObj.time = checkInTime;
    }
    if (checkOutTime) {
      checkInOutObj.label = 'Check Out';
      checkInOutObj.time = checkOutTime;
    }
    return /*#__PURE__*/React.createElement(Flex, _extends({
      alignItems: "center"
    }, rest), /*#__PURE__*/React.createElement(MetricTileCompact, {
      graphic: /*#__PURE__*/React.createElement(IconFlex, {
        size: 26,
        defaultColor: true
      }, /*#__PURE__*/React.createElement(IconClock, null)),
      label: checkInOutObj.label,
      metric: checkInOutObj.time.replace(/\s/g, '').toLowerCase(),
      reverseOrder: true
    }));
  }
  return null;
};
CheckInOutTime.propTypes = {
  checkInTime: PropTypes.string,
  checkOutTime: PropTypes.string
};

const GapFlex = styled(Flex)`
  gap: 12px;
`;
const HotelDetails = _ref => {
  var _hotelDetails$hotelAd;
  let {
    hotelDetails,
    standalone
  } = _ref;
  const [isLightBoxOpen, setLightboxOpen] = useState(false);
  const galleryPhotos = hotelDetails !== null && hotelDetails !== void 0 && hotelDetails.hotelImages ? hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.hotelImages : [];
  const largeGalleryPhotos = useMemo(() => processLightboxHotelPhotos(galleryPhotos), [galleryPhotos]);
  const showAddress = hotelDetails === null || hotelDetails === void 0 || (_hotelDetails$hotelAd = hotelDetails.hotelAddress) === null || _hotelDetails$hotelAd === void 0 ? void 0 : _hotelDetails$hotelAd.address1;
  const showCheckInTime = (hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.checkIn) !== undefined && (hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.checkIn) !== null;
  const showPhoneNumber = (hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.phone) !== undefined && (hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.phone) !== null;
  const descriptionHasHtml = /<\/?[a-z][\s\S]*>/i.test(hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.hotelDescription);
  return /*#__PURE__*/React.createElement(TabCardContent, {
    standalone: standalone
  }, galleryPhotos.length > 4 && /*#__PURE__*/React.createElement(Box, {
    mb: [3, '24px'],
    px: standalone ? [3, 3, 0] : 0
  }, /*#__PURE__*/React.createElement(HotelPhotoGrid, {
    images: galleryPhotos,
    buttonContent: /*#__PURE__*/React.createElement(Button, {
      bordered: true,
      onClick: () => setLightboxOpen(true),
      text: `${galleryPhotos.length} Photos`,
      size: "small"
    })
  })), /*#__PURE__*/React.createElement(GapFlex, {
    mx: standalone ? [3, 3, 0] : 0,
    mb: descriptionHasHtml ? [3, '24px'] : 3,
    flexDirection: ['column', 'column', 'row'],
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(GapFlex, {
    mt: showCheckInTime || showPhoneNumber ? [3, 0, 0] : 0,
    flexDirection: ['column-reverse', 'column-reverse', 'column']
  }, showCheckInTime && /*#__PURE__*/React.createElement(Flex, null, /*#__PURE__*/React.createElement(CheckInOutTime, {
    mr: 3,
    checkInTime: hotelDetails.checkIn
  }), /*#__PURE__*/React.createElement(CheckInOutTime, {
    checkOutTime: hotelDetails.checkOut
  }))), /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column"
  }, showAddress && /*#__PURE__*/React.createElement(AddressOverview, {
    addressDisplay: formatLocationAddress(hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.hotelAddress.address1, hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.hotelAddress.city, hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.hotelAddress.state)
  }), showPhoneNumber && /*#__PURE__*/React.createElement(Flex, {
    ml: [0, 0, 'auto'],
    alignItems: "center",
    mt: 2
  }, /*#__PURE__*/React.createElement(IconLine, {
    icon: "Phone",
    color: getTokenValue(THEME_TOKENS.base.color.action),
    size: "small",
    mr: SPACE.size4
  }), /*#__PURE__*/React.createElement(PhoneNumber, {
    href: `tel:${hotelDetails.phone}`
  }, hotelDetails.phone)))), hotelDetails.hotelDescription && /*#__PURE__*/React.createElement(HotelDescription, {
    standalone: standalone,
    description: hotelDetails.hotelDescription
  }), /*#__PURE__*/React.createElement(LightBox, {
    open: isLightBoxOpen,
    onClose: () => setLightboxOpen(false),
    slides: largeGalleryPhotos
  }));
};
HotelDetails.propTypes = {
  hotelDetails: PropTypes.shape({
    hotelDescription: PropTypes.string,
    hotelImages: PropTypes.arrayOf(PropTypes.string),
    hotelName: PropTypes.string,
    hotelRating: PropTypes.number,
    reviewRate: PropTypes.number,
    reviewCount: PropTypes.number,
    checkIn: PropTypes.string,
    checkOut: PropTypes.string,
    phone: PropTypes.string,
    hotelAddress: PropTypes.shape({
      address1: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string
    })
  }),
  standalone: PropTypes.bool
};

/* eslint-disable max-len */
({
  size: PropTypes.number
});

/* eslint-disable max-len */
const Bed = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 6 6 C 4.355469 6 3 7.355469 3 9 L 3 15.78125 C 2.390625 16.332031 2 17.121094 2 18 L 2 27 L 7 27 L 7 25 L 25 25 L 25 27 L 30 27 L 30 18 C 30 17.121094 29.609375 16.332031 29 15.78125 L 29 9 C 29 7.355469 27.644531 6 26 6 Z M 6 8 L 26 8 C 26.554688 8 27 8.445313 27 9 L 27 15 L 25 15 L 25 14 C 25 12.355469 23.644531 11 22 11 L 18 11 C 17.234375 11 16.53125 11.300781 16 11.78125 C 15.46875 11.300781 14.765625 11 14 11 L 10 11 C 8.355469 11 7 12.355469 7 14 L 7 15 L 5 15 L 5 9 C 5 8.445313 5.445313 8 6 8 Z M 10 13 L 14 13 C 14.554688 13 15 13.445313 15 14 L 15 15 L 9 15 L 9 14 C 9 13.445313 9.445313 13 10 13 Z M 18 13 L 22 13 C 22.554688 13 23 13.445313 23 14 L 23 15 L 17 15 L 17 14 C 17 13.445313 17.445313 13 18 13 Z M 5 17 L 27 17 C 27.554688 17 28 17.445313 28 18 L 28 25 L 27 25 L 27 23 L 5 23 L 5 25 L 4 25 L 4 18 C 4 17.445313 4.445313 17 5 17 Z"
  }));
};
Bed.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
var Expedia = () => /*#__PURE__*/React.createElement("div", {
  style: {
    width: 68,
    height: 15
  }
}, /*#__PURE__*/React.createElement("svg", {
  id: "a",
  "data-name": "svg78",
  xmlns: "http://www.w3.org/2000/svg",
  width: "68",
  height: "15",
  viewBox: "0 0 144 32"
}, /*#__PURE__*/React.createElement("g", {
  id: "b",
  "data-name": "g76"
}, /*#__PURE__*/React.createElement("g", {
  id: "c",
  "data-name": "g50"
}, /*#__PURE__*/React.createElement("path", {
  id: "d",
  "data-name": "path48",
  d: "m30.57,20.75c2.8-8.2-1.58-17.11-9.78-19.91C12.6-1.95,3.68,2.42.88,10.62c-2.8,8.2,1.58,17.11,9.78,19.91,8.2,2.8,17.11-1.58,19.91-9.78",
  fill: "#202843",
  fillRule: "evenodd"
})), /*#__PURE__*/React.createElement("path", {
  id: "e",
  "data-name": "path54",
  d: "m2.45,24.03c2.44-1.16,10.75-5.24,12.39-6.2,1.34-.78,3.35-1.94,3.35-1.94l.85,1.73,1.3,8.53.99-.57.66-11.92c2-1.25,3.77-2.47,5.61-3.74.68-.47,1.36-.94,2.08-1.43-.23-.44-.47-.86-.74-1.28-.78.38-1.53.73-2.28,1.09-2.03.96-3.96,1.88-6.05,2.99l-10.65-5.39-.99.57,6.74,5.39,1.07,1.6h0s-2,1.16-3.35,1.93c-1.65.94-9.34,6.09-11.56,7.63.18.35.38.68.59,1.01",
  fill: "#ffc94c",
  fillRule: "evenodd"
}), /*#__PURE__*/React.createElement("path", {
  id: "f",
  "data-name": "path56",
  d: "m140.77,12.2v-1.72h3.03v15.68h-3.12v-1.78c-1.11,1.3-2.68,2.15-4.94,2.15-4.46,0-7.32-3.55-7.32-8.21s2.87-8.21,7.32-8.21c1.98,0,3.82.75,5.03,2.08Zm-.21,3.08c-.92-1.37-2.49-2.21-4.32-2.21-2.76,0-4.66,2.34-4.66,5.26s1.83,5.33,4.66,5.33c1.89,0,3.42-1.08,4.32-2.47v-5.91",
  fill: "#202843",
  fillRule: "evenodd"
}), /*#__PURE__*/React.createElement("path", {
  id: "g",
  "data-name": "path58",
  d: "m122.13,6.44c0-1.21.99-2.18,2.21-2.18s2.18,1,2.18,2.18-.97,2.21-2.18,2.21-2.21-.99-2.21-2.21",
  fill: "#202843",
  fillRule: "evenodd"
}), /*#__PURE__*/React.createElement("path", {
  id: "h",
  "data-name": "path60",
  d: "m122.7,26.16h3.24v-15.68h-3.24v15.68Z",
  fill: "#202843",
  fillRule: "evenodd"
}), /*#__PURE__*/React.createElement("path", {
  id: "i",
  "data-name": "path62",
  d: "m116.36,4.66h3.17v21.5h-3.12v-1.78c-1.11,1.3-2.68,2.15-4.94,2.15-4.46,0-7.32-3.55-7.32-8.21s2.87-8.21,7.32-8.21c1.91,0,3.68.69,4.89,1.93v-7.39Zm-.07,10.62c-.92-1.37-2.49-2.21-4.32-2.21-2.76,0-4.66,2.34-4.66,5.26s1.83,5.33,4.66,5.33c1.89,0,3.42-1.08,4.32-2.47v-5.91",
  fill: "#202843",
  fillRule: "evenodd"
}), /*#__PURE__*/React.createElement("path", {
  id: "j",
  "data-name": "path64",
  d: "m95.01,26.56c1.42,0,2.7-.27,3.86-.8,1.16-.53,2.18-1.3,3.06-2.29l-1.94-2.06h0c-.64.69-1.37,1.21-2.17,1.64-.8.4-1.71.6-2.74.6-2.42,0-4.53-1.83-4.83-4.23h12.2s0-.07,0-.1c.03-.37.04-.75.04-1.13,0-4.48-3.21-8.11-7.66-8.11s-7.86,3.54-7.86,8.24,3.37,8.24,8.04,8.25Zm-4.69-9.82h8.85c-.31-2.18-2.11-3.81-4.41-3.81-1.15,0-2.21.5-3.01,1.24-.77.71-1.29,1.65-1.43,2.56",
  fill: "#202843",
  fillRule: "evenodd"
}), /*#__PURE__*/React.createElement("path", {
  id: "k",
  "data-name": "path66",
  d: "m73.2,24.59v7.39h-3.17V10.48h3.12v1.78c1.11-1.3,2.68-2.15,4.94-2.15,4.46,0,7.32,3.55,7.32,8.21s-2.87,8.21-7.32,8.21c-1.91,0-3.68-.69-4.89-1.93Zm.07-3.23c.92,1.37,2.49,2.21,4.32,2.21,2.76,0,4.66-2.34,4.66-5.26s-1.83-5.33-4.66-5.33c-1.89,0-3.42,1.08-4.32,2.47v5.91",
  fill: "#202843",
  fillRule: "evenodd"
}), /*#__PURE__*/React.createElement("path", {
  id: "l",
  "data-name": "path68",
  d: "m58.77,18.1l-5.53-7.62h3.92l3.69,5.2,3.69-5.2h3.86l-5.56,7.65,5.81,8.03h-3.92l-3.93-5.55-3.93,5.55h-3.94l5.84-8.06",
  fill: "#202843",
  fillRule: "evenodd"
}), /*#__PURE__*/React.createElement("path", {
  id: "m",
  "data-name": "path70",
  d: "m42.79,16.96h8.15v-3.08h-8.15v-5.59h8.85v-3.08h-12.4v20.95h12.46v-3.08h-8.92v-6.12",
  fill: "#202843",
  fillRule: "evenodd"
}))));

/* eslint-disable max-len */
var HotelsDotCom = () => /*#__PURE__*/React.createElement("div", {
  style: {
    width: 80,
    height: 16
  }
}, /*#__PURE__*/React.createElement("svg", {
  id: "a",
  "data-name": "svg5838",
  xmlns: "http://www.w3.org/2000/svg",
  width: "80",
  height: "16",
  viewBox: "0 0 107.67 21.64"
}, /*#__PURE__*/React.createElement("g", {
  id: "b",
  "data-name": "layer1"
}, /*#__PURE__*/React.createElement("g", {
  id: "c",
  "data-name": "g5994"
}, /*#__PURE__*/React.createElement("path", {
  id: "d",
  "data-name": "path3013",
  d: "m104.42,6.73c-.1,0-.18-.08-.18-.18v-1.2h-.34c-.08,0-.16-.07-.16-.16s.07-.16.16-.16h1.04c.08,0,.15.07.15.16s-.07.16-.15.16h-.34v1.2c0,.1-.08.18-.17.18m2.57,0c-.1,0-.17-.08-.17-.18v-1.04l-.45,1.16s-.05.07-.09.07-.08-.03-.09-.07l-.45-1.16v1.04c0,.1-.08.18-.17.18s-.18-.08-.18-.18v-1.24c0-.16.13-.29.29-.29.12,0,.23.08.27.19l.33.87.34-.87c.04-.11.15-.19.27-.19.16,0,.29.13.29.29v1.24c0,.1-.08.18-.18.18",
  fill: "#ee2841"
}), /*#__PURE__*/React.createElement("path", {
  id: "e",
  "data-name": "path3017",
  d: "m13.12,4.51c0-.6.19-1.16.5-1.62-.95.28-1.65,1.15-1.65,2.19v1.83l1.15.06v-2.47Z",
  fill: "#ee2841"
}), /*#__PURE__*/React.createElement("path", {
  id: "f",
  "data-name": "path3021",
  d: "m11.97,17.13c0,1.04.7,1.91,1.65,2.19-.32-.46-.5-1.02-.5-1.62v-3.44h-1.15v2.87Z",
  fill: "#ee2841"
}), /*#__PURE__*/React.createElement("path", {
  id: "g",
  "data-name": "path3025",
  d: "m14.84,14.26h-1.15v3.44c0,1.04.7,1.91,1.65,2.19-.32-.46-.5-1.02-.5-1.62v-4.01Z",
  fill: "#ee2841"
}), /*#__PURE__*/React.createElement("path", {
  id: "h",
  "data-name": "path3029",
  d: "m14.84,3.94c0-.6.19-1.16.5-1.62-.95.28-1.65,1.15-1.65,2.19v2.49l1.15.06v-3.13Z",
  fill: "#ee2841"
}), /*#__PURE__*/React.createElement("path", {
  id: "i",
  "data-name": "path3033",
  d: "m1.91,3.21c-.84.5-1.41,1.41-1.41,2.45v10.9c0,1.05.57,1.95,1.41,2.45-.17-.4-.26-.84-.26-1.31V4.51c0-.46.09-.9.26-1.31",
  fill: "#ee2841"
}), /*#__PURE__*/React.createElement("path", {
  id: "j",
  "data-name": "path3037",
  d: "m3.37,3.37c0-.46.09-.9.26-1.31-.84.5-1.41,1.41-1.41,2.45v13.19c0,1.23.78,2.27,1.87,2.68-.45-.58-.73-1.31-.73-2.11V3.37Z",
  fill: "#ee2841"
}), /*#__PURE__*/React.createElement("path", {
  id: "k",
  "data-name": "path3041",
  d: "m17.7,1.65c-1.27,0-2.29,1.03-2.29,2.29v3.73l-5.73-.29V3.37c0-1.58-1.28-2.87-2.87-2.87s-2.87,1.28-2.87,2.87v14.91c0,1.58,1.28,2.87,2.87,2.87s2.87-1.28,2.87-2.87v-4.59h5.73v4.59c0,1.27,1.03,2.29,2.29,2.29s2.29-1.03,2.29-2.29V3.94c0-1.27-1.03-2.29-2.29-2.29",
  fill: "#ee2841"
}), /*#__PURE__*/React.createElement("path", {
  id: "l",
  "data-name": "path3045",
  d: "m37.5,15.61v-4.25h-6.19v4.25c0,.45-.37.84-.82.84s-.86-.39-.86-.84V5.93c0-.45.39-.84.84-.84s.84.39.84.84v3.95h6.19v-3.95c0-.45.37-.84.84-.84s.84.39.84.84v9.69c0,.45-.37.84-.84.84s-.84-.39-.84-.84",
  fill: "#ee2841"
}), /*#__PURE__*/React.createElement("path", {
  id: "m",
  "data-name": "path3049",
  d: "m40.37,12.31c0-2.33,1.58-4.25,4.1-4.25s4.1,1.91,4.1,4.25-1.56,4.26-4.1,4.26-4.1-1.91-4.1-4.26m6.61,0c0-1.53-.89-2.9-2.52-2.9s-2.52,1.38-2.52,2.9.91,2.92,2.52,2.92,2.52-1.38,2.52-2.92",
  fill: "#ee2841"
}), /*#__PURE__*/React.createElement("path", {
  id: "n",
  "data-name": "path3053",
  d: "m57.34,8.06c2.43,0,3.95,1.9,3.95,4.03,0,.45-.32.72-.89.72h-5.52c.12,1.38,1.13,2.52,2.75,2.52.69,0,1.41-.22,2.01-.66.08-.07.2-.08.34-.08.34,0,.6.27.6.6,0,.22-.1.39-.27.5-.81.59-1.76.87-2.84.87-2.42,0-4.2-1.71-4.2-4.26,0-2.35,1.7-4.25,4.06-4.25m-2.48,3.66h4.95c-.03-1.07-.76-2.42-2.47-2.42s-2.4,1.31-2.48,2.42",
  fill: "#ee2841"
}), /*#__PURE__*/React.createElement("path", {
  id: "o",
  "data-name": "path3057",
  d: "m62.25,15.7V5.84c0-.42.34-.75.76-.75s.75.34.75.75v9.85c0,.42-.35.76-.75.76s-.76-.34-.76-.76",
  fill: "#ee2841"
}), /*#__PURE__*/React.createElement("path", {
  id: "p",
  "data-name": "path3061",
  d: "m64.73,15.13c0-.37.29-.65.65-.65.13,0,.27.05.37.12.62.45,1.43.77,2.28.77,1.14,0,1.75-.47,1.75-1.16,0-.81-.92-1.07-1.96-1.31-1.38-.32-2.99-.69-2.99-2.43,0-1.29,1.11-2.4,3.09-2.4,1.13,0,2.01.33,2.7.84.15.12.27.29.27.5,0,.33-.29.62-.62.62-.12,0-.25-.05-.34-.12-.52-.39-1.23-.62-2-.62-1.02,0-1.66.4-1.66,1.06,0,.71.87.94,1.88,1.17,1.41.32,3.09.71,3.09,2.57,0,1.39-1.14,2.49-3.27,2.49-1.13,0-2.13-.29-2.95-.91-.17-.12-.29-.32-.29-.54",
  fill: "#ee2841"
}), /*#__PURE__*/React.createElement("path", {
  id: "q",
  "data-name": "path3065",
  d: "m73.23,14.45c.57,0,1.06.47,1.06,1.04s-.49,1.06-1.06,1.06-1.04-.49-1.04-1.06.47-1.04,1.04-1.04",
  fill: "#ee2841"
}), /*#__PURE__*/React.createElement("path", {
  id: "r",
  "data-name": "path3069",
  d: "m82.13,12.31c0-2.33,1.58-4.25,4.1-4.25s4.1,1.91,4.1,4.25-1.56,4.26-4.1,4.26-4.1-1.91-4.1-4.26m6.61,0c0-1.53-.89-2.9-2.52-2.9s-2.52,1.38-2.52,2.9.91,2.92,2.52,2.92,2.52-1.38,2.52-2.92",
  fill: "#ee2841"
}), /*#__PURE__*/React.createElement("path", {
  id: "s",
  "data-name": "path3073",
  d: "m102.82,10.51v5.19c0,.42-.35.75-.77.75s-.74-.34-.74-.75v-4.73c0-.94-.42-1.39-1.41-1.39-.82,0-1.63.93-2.01,1.51v4.61c0,.42-.33.75-.75.75s-.76-.34-.76-.75v-4.73c0-.94-.4-1.39-1.43-1.39-.79,0-1.58.98-1.98,1.57v4.56c0,.42-.35.75-.75.75s-.76-.34-.76-.75v-6.77c0-.42.34-.76.74-.76s.77.34.77.76v.47c.34-.5,1.41-1.34,2.62-1.34s1.95.64,2.2,1.51c.47-.76,1.57-1.51,2.76-1.51,1.49,0,2.28.82,2.28,2.45",
  fill: "#ee2841"
}), /*#__PURE__*/React.createElement("path", {
  id: "t",
  "data-name": "path3077",
  d: "m78.81,8.06c1.16,0,1.96.37,2.59.94.13.12.23.32.23.5,0,.39-.3.69-.67.69-.19,0-.34-.07-.45-.17-.47-.44-.99-.62-1.63-.62-1.59,0-2.62,1.23-2.62,2.9s1.03,2.87,2.62,2.92c1.08.04,2.16-.75,3.03-2.12.11-.17.27-.49.4-.65.25-.31.42-.16.57-.01.27.28.26.94.09,1.27-.49.91-.9,1.34-1.37,1.8-.65.63-1.63,1.07-2.78,1.07-2.45,0-4.13-1.83-4.13-4.26s1.68-4.25,4.13-4.25",
  fill: "#ee2841"
}), /*#__PURE__*/React.createElement("path", {
  id: "u",
  "data-name": "path3081",
  d: "m54.36,15.12c0,.29-.82.92-1.41,1.21-.33.16-.67.23-1.16.23-1.28,0-1.93-.72-1.93-2v-4.99h-.77c-.35,0-.67-.3-.67-.67s.32-.65.67-.65h.77v-1.54c0-.42.34-.76.76-.76s.75.34.75.76v1.54h1.06c.37,0,.67.3.67.65,0,.37-.3.67-.67.67h-1.06v4.65c0,.57.27.99.79.99.92,0,1.65-1.43,2.06-1.43.39,0,.13.96.13,1.33",
  fill: "#ee2841"
})))));

/* eslint-disable max-len */
({
  size: PropTypes.number
});

/* eslint-disable max-len */
const LuggageTrolly = _ref => {
  let {
    size = 60
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 96 96",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "m83.03,79.33h-3.15v-39.05c0-16.89-13.31-30.74-30.03-31.71v-3.81h1.58c1.04,0,1.89-.84,1.89-1.88s-.85-1.88-1.89-1.88h-6.95c-1.04,0-1.89.84-1.89,1.88s.85,1.88,1.89,1.88h1.58v3.81c-16.72.98-30.03,14.83-30.03,31.71v39.05h-3.15c-1.04,0-1.89.84-1.89,1.88s.85,1.88,1.89,1.88h70.14c1.04,0,1.89-.84,1.89-1.88s-.85-1.88-1.89-1.88Zm-63.21,0v-39.05c0-15.45,12.62-28.02,28.14-28.02s28.14,12.57,28.14,28.02v39.05H19.81Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m20.76,84.53c-2.9,0-5.26,2.35-5.26,5.24s2.36,5.24,5.26,5.24,5.26-2.35,5.26-5.24-2.36-5.24-5.26-5.24Zm0,6.71c-.82,0-1.48-.66-1.48-1.48s.67-1.48,1.48-1.48,1.48.66,1.48,1.48-.67,1.48-1.48,1.48Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m75.16,84.53c-2.9,0-5.26,2.35-5.26,5.24s2.36,5.24,5.26,5.24,5.26-2.35,5.26-5.24-2.36-5.24-5.26-5.24Zm0,6.71c-.82,0-1.48-.66-1.48-1.48s.67-1.48,1.48-1.48,1.48.66,1.48,1.48-.67,1.48-1.48,1.48Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m67.87,44.83h-13.29v-4.79c0-1.04-.85-1.88-1.89-1.88h-9.47c-1.04,0-1.89.84-1.89,1.88v4.79h-13.29c-1.04,0-1.89.84-1.89,1.88v27.98c0,1.04.85,1.88,1.89,1.88h39.83c1.04,0,1.89-.84,1.89-1.88v-27.98c0-1.04-.85-1.88-1.89-1.88Zm-12.81,3.76v24.22h-14.21v-24.22h14.21Zm-9.95-6.67h5.7v2.91h-5.7v-2.91Zm-15.18,6.67h7.14v24.22h-7.14v-24.22Zm36.05,24.22h-7.14v-24.22h7.14v24.22Z"
  }));
};
LuggageTrolly.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const ReviewsEmpty = _ref => {
  let {
    size = 60
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 96 96",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "M51,46h-.8V34.57A7.2,7.2,0,0,0,43,27.38H16.38a7.2,7.2,0,0,0-7.2,7.19V46h-.8a7.2,7.2,0,0,0-7.2,7.19V85.14a1.87,1.87,0,0,0,3.73,0V79h49.6v6.12a1.87,1.87,0,0,0,3.73,0V53.2A7.2,7.2,0,0,0,51,46Zm0,3.73a3.47,3.47,0,0,1,3.47,3.46V64.64H50.24V49.74ZM12.91,34.57a3.47,3.47,0,0,1,3.47-3.46H43a3.47,3.47,0,0,1,3.47,3.46V64.64H12.91ZM8.38,49.74h.8v14.9H4.91V53.2A3.47,3.47,0,0,1,8.38,49.74ZM4.91,75.29V68.37h49.6v6.92Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M20.44,43.28h1.24v1.24a1.87,1.87,0,0,0,3.73,0V43.28h1.25a1.87,1.87,0,1,0,0-3.73H25.41V38.31a1.87,1.87,0,1,0-3.73,0v1.24H20.44a1.87,1.87,0,1,0,0,3.73Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M32.76,43.28H34v1.24a1.87,1.87,0,0,0,3.73,0V43.28H39a1.87,1.87,0,1,0,0-3.73H37.74V38.31a1.87,1.87,0,1,0-3.73,0v1.24H32.76a1.87,1.87,0,1,0,0,3.73Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M20.44,56.08h1.24v1.25a1.87,1.87,0,0,0,3.73,0V56.08h1.25a1.86,1.86,0,1,0,0-3.72H25.41V51.12a1.87,1.87,0,1,0-3.73,0v1.24H20.44a1.86,1.86,0,1,0,0,3.72Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M32.76,56.08H34v1.25a1.87,1.87,0,0,0,3.73,0V56.08H39a1.86,1.86,0,1,0,0-3.72H37.74V51.12a1.87,1.87,0,1,0-3.73,0v1.24H32.76a1.86,1.86,0,1,0,0,3.72Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M95,36.36l-3.6-25.75A1.87,1.87,0,0,0,89.53,9H66.26a1.87,1.87,0,0,0-1.85,1.61l-3.6,25.75a1.86,1.86,0,0,0,1.85,2.12H76V82.84H65.86a1.87,1.87,0,1,0,0,3.73H89.93a1.87,1.87,0,1,0,0-3.73H79.76V38.48H93.13a1.85,1.85,0,0,0,1.41-.64A1.81,1.81,0,0,0,95,36.36Zm-30.17-1.6,3.08-22h20l3.08,22Z"
  })));
};
ReviewsEmpty.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
var Vrbo = () => /*#__PURE__*/React.createElement("div", {
  style: {
    width: 45,
    height: 15
  }
}, /*#__PURE__*/React.createElement("svg", {
  id: "a",
  "data-name": "Layer 1",
  xmlns: "http://www.w3.org/2000/svg",
  width: "45",
  height: "15",
  viewBox: "0 0 121 40"
}, /*#__PURE__*/React.createElement("path", {
  d: "m2.4,5.69H0v.06c2.2.87,3.28,2.54,4.14,6.43l5.92,27.34h1.34L5.45,11.96c-1.03-4.72-2.11-5.59-3.05-6.27Zm4.5,0h-2.25v.06c1.01,1.21,1.3,2.71,2.1,5.98l6.01,27.79h1.34l-6.02-27.88c-.85-3.58-.99-4.57-1.18-5.95Zm8.12,31.91l.89-1.85-5-23.58c-.88-3.66-.68-5.34-.32-6.48h-1.94c-.03,1.49.04,2.73.76,5.86l5.61,26.05Zm1.79-3.74l.9-1.88-4.02-19.4c-.75-3.77-.43-5.73.82-6.84v-.06h-2.14c-.66,1.4-.86,2.81-.1,6.53,0,0,4.54,21.65,4.54,21.65Zm1.8-3.75l.9-1.89-3.05-15.53c-.84-4.37.17-6.28,3.6-6.94v-.06h-3.18c-.92.45-2.86,1.84-1.77,7.02l3.5,17.4Zm5.97-12.38l.09.06c2.13-7.07,2.81-10.2.93-12.11h-2.66v.06c2.97,1.06,3.64,4.09,1.64,11.99Zm.18.12l.09.06c3.35-7.93,4.22-10.1,5.6-12.17v-.06h-2.72v.08c.28,3.17-.81,6.08-2.97,12.09Zm8.25-12.16c-2.83,1.65-4.51,4.72-8.07,12.28l.09.06c4.08-7.65,6.41-10.7,11.68-12.28v-.06s-3.7,0-3.7,0Zm.87,12.57c-.04-.81-.5-1.23-1.24-1.23-1.76,0-3.67,2.79-4.82,5.69h.07c1.16-2.58,2.94-5.15,3.97-5.15.43,0,.71.21.74.75.05,1.13-1.81,5.37-2.58,7.2l-5.65,14.01h1.4l5.6-13.9c.8-2.01,2.58-6.03,2.51-7.37Zm2.51-.17c-.06-1.24-.86-2.09-2.26-2.09-3.04,0-5.38,3.26-6.52,6.71h.06c1.19-3.08,3.22-6.17,5.67-6.17,1.07,0,1.69.59,1.75,1.62.09,1.66-1.59,5.42-2.39,7.51l-5.55,13.84h1.39l5.46-13.74c.91-2.2,2.49-5.8,2.39-7.68Zm2.51-.16c-.09-1.72-1.23-3.01-3.27-3.01-4.38,0-7.05,3.78-8.21,7.78h.06c1.22-3.73,3.74-7.24,7.37-7.24,1.72,0,2.69,1.07,2.77,2.58.11,2.09-1.32,5.47-2.27,7.84l-5.4,13.63h1.45l5.3-13.58c.94-2.47,2.33-5.58,2.2-8Zm2.52-.1c-.12-2.2-1.6-3.92-4.29-3.92-5.57,0-8.78,4.14-9.9,8.8h.07c1.24-4.33,4.08-8.26,9.06-8.26,2.36,0,3.67,1.45,3.78,3.43.14,2.63-1.1,5.53-2.09,8.16l-5.25,13.47h1.4l5.15-13.36c1.08-2.74,2.22-5.48,2.07-8.32Zm13.63-1.35c-.12-2.47-1.58-3.6-3.36-3.6-3.22,0-6.03,2.54-7.74,6,.01-.39.01-.8-.01-1.22-.14-2.68-1.97-4.78-5.3-4.78-6.98,0-10.44,4.6-11.58,9.82h.06c1.21-4.89,4.47-9.28,10.75-9.28,3.01,0,4.66,1.88,4.79,4.29.17,3.17-.88,5.64-1.97,8.48l-5.09,13.31h1.4l5.05-13.2c.34-.91.66-1.75.95-2.58,1.02-2.09,2.25-4.24,2.89-4.24.48,0,.63.41.66.78.11,1.4-.4,3.69-1.5,5.66l.06.05c1.28-1.13,2.71-4.51,2.62-6.28-.04-.7-.33-1.18-1.03-1.18-1.09,0-2.47,2.24-3.49,4.6.12-.37.22-.75.32-1.13,1.09-2.36,2.6-4.38,4.04-4.38.86,0,1.37.59,1.42,1.56.12,2.36-2,5.8-3.77,6.92l.06.05c2.41-1.07,5.07-4.62,4.92-7.51-.07-1.29-.75-1.99-1.82-1.99-1.77,0-3.5,2.06-4.68,4.62.04-.2.07-.41.11-.61,1.32-2.75,3.28-4.93,5.44-4.93,1.18,0,2.08.81,2.16,2.36.18,3.43-3.06,7.08-6.01,8.16l.06.05c3.6-.91,7.37-4.78,7.16-8.75-.1-1.88-1.17-2.79-2.56-2.79-2.49,0-4.71,2.25-6.15,5.23.02-.16.02-.33.03-.5,1.59-3.2,4.09-5.63,6.98-5.63,1.56,0,2.84,1.02,2.96,3.17.24,4.56-4.01,8.43-8.31,9.39l.06.05c5.02-.73,9.69-4.87,9.42-9.97Zm9.75,22.81l-.04.08c6.95-1.88,13.47-14,14.02-19.94.14-1.53-.53-2.43-1.66-2.58-1.66-.23-3.78,1.03-5.32,3l.05.06c1.28-1.54,3.01-2.63,4.24-2.46.8.11,1.33.81,1.23,1.95-.48,5.21-6.49,17.3-12.52,19.89Zm-.11.23l-.03.06c9.46-1.12,16.45-12.6,17.15-20.01.23-2.44-.9-3.74-2.56-3.97-2.52-.35-5.66,1.34-7.59,4.06l.05.06c1.85-2.39,4.42-3.81,6.46-3.53,1.45.2,2.32,1.29,2.13,3.28-.63,6.74-7.05,18.26-15.61,20.05Zm20.1-19.89c.31-3.29-1.16-4.98-3.47-5.3-3.32-.46-7.36,1.57-9.81,5.14l.05.06c2.24-3.08,5.84-4.99,8.73-4.6,1.98.27,3.31,1.77,3.04,4.66-.77,8.21-8.59,19.48-18.73,20.11l-.03.06c11.28-.06,19.38-11.19,20.22-20.13Zm-1.34-6.56c-4.18-.57-9.23,1.89-12.08,6.2l.05.06c2.69-3.88,7.25-6.18,11-5.66,2.52.35,4.24,2.25,3.89,5.99-.89,9.53-8.92,20.25-21.78,20.21l-.03.07c13.35.5,22.38-10,23.34-20.25.36-4.13-1.56-6.23-4.39-6.62Zm-20.23,26.79c-6.2-.26-9.33-3.29-9.02-6.8.13-1.5.69-2.88,1.18-4.12l7.18-18.55c.9-2.31,2.57-5.81,1.25-7.53l-1.63.34v.1c1.39,1.56.1,4.11-.84,6.7l-7.15,18.41c-.48,1.18-.98,2.45-1.12,4.01-.33,3.83,3.07,7.29,10.13,7.5,0,0,.02-.06.02-.06Zm.87-29.85l-7.53,19.41c-.48,1.23-1.12,2.77-1.24,4.33-.28,3.19,2.5,5.8,7.95,6l.03-.06c-4.6-.26-7.11-2.44-6.86-5.3.13-1.5.83-3.26,1.31-4.44l7.78-19.98c.7-1.83,2.76-6.31,1.65-7.81l-1.77.36v.1c1.19,1.7-.5,5.36-1.32,7.39Zm2.96-.42l-8.11,20.86c-.48,1.23-1.25,3.15-1.38,4.6-.22,2.55,1.86,4.3,5.77,4.58l.02-.05c-.81-.09-1.6-.28-2.36-.56-1.6-.58-2.4-1.7-2.26-3.27.13-1.5.91-3.53,1.39-4.76l8.31-21.34c.42-1.13,2.79-6.52,2.22-8.17l-1.71.32v.1c.9,1.29-1.34,6.29-1.89,7.69Zm-5.73,21.87c-.48,1.24-1.33,3.47-1.46,4.92-.16,1.8,1.42,2.78,3.54,3.11l.03-.06c-1.45-.26-2.57-.95-2.44-2.4.12-1.4,1.1-3.9,1.52-5.03l8.65-22.2c.21-.59,3.49-8.54,3.02-9.05l-1.77.36v.1c.07,1.94-2.18,7.24-2.64,8.49,0,0-8.45,21.76-8.45,21.76Zm2.38,1.03c-.48,1.18-1.51,3.71-1.57,5.12-.03.6.43,1.32,1.04,1.62.1.05.2.09.3.12l.04-.09c-.17-.11-.38-.51-.33-1.08.12-1.4,1.23-3.92,1.71-5.15L77.82.03l-1.78.36c.1.42-3.4,9.03-3.55,9.46l-8.79,22.64Zm37.32,6.88l-.03.06c6-.86,12.18-13.45,12.32-21.84.03-1.83-1.01-3.7-3.73-4.15l-.03.07c1.77.47,2.46,1.62,2.43,3.17-.14,8.21-6.14,21.8-10.96,22.69Zm-.11.23c8.2-.85,14.79-11.4,14.95-20.13.04-2.37-1.51-5.68-6.19-6.2l-.03.06c3.68.56,4.98,3.12,4.94,5.23-.15,8.55-6.58,20.03-13.64,20.98,0,0-.03.06-.03.06Zm-.08.17c10.46-.77,17.46-9.35,17.62-18.41.05-2.91-2.01-7.7-8.7-8.24l-.03.06c5.54.58,7.51,4.63,7.46,7.28-.16,8.89-7.05,18.31-16.32,19.26l-.03.05h0Zm9-26.83l-.03.06c7.71.65,9.93,6.13,9.87,9.32-.16,9.22-7.31,16.68-18.89,17.55l-.03.06c12.66-.76,20.08-7.31,20.24-16.7.07-3.45-2.45-9.78-11.16-10.29h0Zm-20.21,17.54c.16-9.17,8.06-16.68,18.9-17.55l.03-.06c-12.14.75-20.09,7.3-20.25,16.64-.06,3.45,2.46,9.84,11.18,10.34l.03-.06c-7.68-.53-9.95-6.13-9.89-9.31Zm2.55,1.88c.15-8.73,7.27-18.32,16.28-19.27l.03-.06c-10.09.77-17.48,9.4-17.63,18.36-.05,2.91,2.01,7.86,8.71,8.3l.03-.06c-5.81-.52-7.47-4.62-7.42-7.27Zm2.54,1.88c.15-8.34,6.85-20.04,13.66-20.98l.03-.06c-7.99.85-14.81,11.56-14.96,20.07-.04,2.37,1.56,5.88,6.19,6.26l.03-.06c-3.69-.45-4.99-3.12-4.95-5.23h0Zm2.55,1.88c.14-7.89,6.36-21.75,11.03-22.69l.03-.06c-5.79.85-12.19,13.66-12.33,21.79-.03,1.83.9,3.91,3.73,4.21l.03-.06c-1.94-.33-2.52-1.63-2.49-3.19Z",
  fill: "#15326f"
})));

/* eslint-disable max-len */
const AirConditioning = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 15 3 L 15 6.5625 L 12.71875 4.28125 L 11.28125 5.71875 L 15 9.4375 L 15 14.25 L 10.875 11.78125 L 9.5625 6.6875 L 7.625 7.1875 L 8.4375 10.3125 L 5.375 8.46875 L 4.34375 10.15625 L 7.46875 12.03125 L 4.25 12.84375 L 4.75 14.78125 L 9.875 13.46875 L 14.09375 16 L 9.875 18.53125 L 4.75 17.21875 L 4.25 19.15625 L 7.46875 19.96875 L 4.34375 21.84375 L 5.375 23.53125 L 8.4375 21.6875 L 7.625 24.8125 L 9.5625 25.3125 L 10.875 20.21875 L 15 17.75 L 15 22.5625 L 11.28125 26.28125 L 12.71875 27.71875 L 15 25.4375 L 15 29 L 17 29 L 17 25.4375 L 19.28125 27.71875 L 20.71875 26.28125 L 17 22.5625 L 17 17.75 L 21.125 20.21875 L 22.4375 25.3125 L 24.375 24.8125 L 23.5625 21.6875 L 26.625 23.53125 L 27.65625 21.84375 L 24.53125 19.96875 L 27.75 19.15625 L 27.25 17.21875 L 22.125 18.53125 L 17.90625 16 L 22.125 13.46875 L 27.25 14.78125 L 27.75 12.84375 L 24.53125 12.03125 L 27.65625 10.15625 L 26.625 8.46875 L 23.5625 10.3125 L 24.375 7.1875 L 22.4375 6.6875 L 21.125 11.78125 L 17 14.25 L 17 9.4375 L 20.71875 5.71875 L 19.28125 4.28125 L 17 6.5625 L 17 3 Z"
  }));
};
AirConditioning.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const BarChart = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "m29,27V4h-8v23h-2v-10h-6v10h-2V10H3v17h-1v2h28v-2h-1Zm-24,0v-15h4v15h-4Zm18,0V6h4v21h-4Z"
  }));
};
BarChart.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const BarLounge = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 8.5 4 C 5.464844 4 3 6.464844 3 9.5 C 3 12.535156 5.464844 15 8.5 15 C 9.3125 15 10.078125 14.796875 10.78125 14.46875 L 16 21.34375 L 16 27 L 12 27 L 12 29 L 22 29 L 22 27 L 18 27 L 18 21.34375 L 26.8125 9.71875 L 27 9.4375 L 27 8 L 13.75 8 C 13.144531 5.679688 10.984375 4 8.5 4 Z M 8.5 6 C 9.890625 6 11.089844 6.839844 11.625 8 L 7 8 L 7 9.4375 L 7.1875 9.71875 L 9.53125 12.8125 C 9.1875 12.933594 8.835938 13 8.5 13 C 6.535156 13 5 11.464844 5 9.5 C 5 7.535156 6.535156 6 8.5 6 Z M 9.9375 10 L 24.0625 10 L 22.5625 12 L 13.5 12 L 15 14 L 21.03125 14 L 17 19.34375 Z"
  }));
};
BarLounge.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const Beach = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M25.51 3.19c-4.295-2.45-9.786-.996-12.266 3.245l-.337.578a.854.854 0 0 0 .32 1.18l6.37 3.633l-3.812 6.588h-.08c-3.683 0-6.873 2.11-8.774 5.093a2.357 2.357 0 0 1 1.982.2c1.646-2.15 4.103-3.565 6.793-3.565c2.804 0 5.355 1.537 6.999 3.843a2.347 2.347 0 0 1 1.691-.607c-1.553-2.37-3.928-4.17-6.715-4.758l3.432-5.93l6.15 3.507a.884.884 0 0 0 1.195-.316l.338-.578c2.479-4.241 1.008-9.665-3.287-12.113Zm-2.556 8.554l-3.682-2.1c.668-1.015 1.302-1.788 2.063-2.471c.74-.665 1.625-1.267 2.824-1.921c.177 1.1.231 2.016.117 2.914c-.135 1.074-.52 2.18-1.322 3.578Zm1.515.864c.886-1.541 1.372-2.868 1.544-4.228c.112-.891.087-1.771-.026-2.7c2.334 2.175 2.977 5.696 1.39 8.587l-2.908-1.659Zm-4.311-6.714c-.923.829-1.663 1.75-2.403 2.886l-2.893-1.65c1.705-2.744 5.01-3.962 8.033-3.158c-1.083.617-1.965 1.229-2.737 1.922ZM8.54 25.408a.874.874 0 0 0-.866-.591a.872.872 0 0 0-.814.658c-.297 1.2-.86 1.858-1.502 2.241c-.672.4-1.527.556-2.483.556a.87.87 0 0 0-.875.864a.87.87 0 0 0 .875.864c1.116 0 2.333-.176 3.387-.804a4.628 4.628 0 0 0 1.548-1.508c2.087 2.629 6.16 2.615 8.187-.028c2.059 2.685 6.23 2.657 8.285-.099C25.418 29.104 27.27 30 29.12 30a.87.87 0 0 0 .874-.864a.87.87 0 0 0-.874-.864c-1.72 0-3.435-1.106-4.002-2.855a.874.874 0 0 0-.828-.601a.874.874 0 0 0-.835.592c-1.124 3.35-5.528 3.308-6.63.14a.874.874 0 0 0-.828-.584a.874.874 0 0 0-.827.583c-1.103 3.17-5.506 3.212-6.63-.139Z"
  }));
};
Beach.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const Breakfast = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "-1 -1 34 34",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "m15.3,16.35c0-.58.47-1.05,1.05-1.05s1.05-.47,1.05-1.05-.47-1.05-1.05-1.05c-1.74,0-3.15,1.41-3.15,3.15,0,.58.47,1.05,1.05,1.05s1.05-.47,1.05-1.05Zm-6.3.35c0-4.25,3.45-7.7,7.7-7.7s7.7,3.45,7.7,7.7-3.45,7.7-7.7,7.7-7.7-3.45-7.7-7.7Zm7.7-5.6c-3.09,0-5.6,2.51-5.6,5.6s2.51,5.6,5.6,5.6,5.6-2.51,5.6-5.6-2.51-5.6-5.6-5.6Zm-8.8-2.81c2.06-3.74,5.83-6.29,10.2-6.29,6.66,0,11.9,5.91,11.9,12.98,0,.79-.07,1.57-.19,2.32-.08.48-.06.99.03,1.52.95,5.13-2.43,10.06-7.56,11.02-2,.37-4.07.09-5.9-.81-1.12-.54-2.37-.72-3.59-.53-.44.07-.89.1-1.34.1-5.3,0-9.45-4.62-9.45-10.15,0-3.6,1.75-6.79,4.42-8.59.62-.42,1.14-.95,1.48-1.56Zm10.2-4.19c-3.5,0-6.62,2.05-8.36,5.21-.52.95-1.29,1.71-2.14,2.29-2.08,1.41-3.5,3.94-3.5,6.86,0,4.53,3.37,8.05,7.35,8.05.35,0,.69-.03,1.02-.08,1.65-.25,3.33,0,4.83.73,3.64,1.79,8.05.29,9.84-3.35.7-1.42.92-3.03.63-4.59-.14-.74-.16-1.5-.04-2.24.11-.64.16-1.3.16-1.98,0-6.11-4.48-10.88-9.8-10.88Z"
  }));
};
Breakfast.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const Casino = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 12 1 L 0 13 L 12 25 L 24 13 L 12 1 z M 12 3.8007812 L 21.199219 13 L 12 22.199219 L 2.8007812 13 L 12 3.8007812 z M 12 8 A 1 1 0 0 0 12 10 A 1 1 0 0 0 12 8 z M 8 12 A 1 1 0 0 0 8 14 A 1 1 0 0 0 8 12 z M 12 12 A 1 1 0 0 0 12 14 A 1 1 0 0 0 12 12 z M 16 12 A 1 1 0 0 0 16 14 A 1 1 0 0 0 16 12 z M 26.800781 13 L 24.800781 15 L 30 15 L 30 27 L 18 27 L 18 21.800781 L 16 23.800781 L 16 29 L 32 29 L 32 13 L 26.800781 13 z M 12 16 A 1 1 0 0 0 12 18 A 1 1 0 0 0 12 16 z M 24 20 A 1 1 0 0 0 24 22 A 1 1 0 0 0 24 20 z"
  }));
};
Casino.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const ChargingStation = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 9 5 C 7.355 5 6 6.355 6 8 L 6 27 L 20 27 L 20 20 L 22 20 L 22 24 C 22 25.645 23.355 27 25 27 C 26.645 27 28 25.645 28 24 L 28 13.8125 C 28 13.0155 27.687 12.28075 27.125 11.71875 L 22.40625 7 L 21 8.40625 L 23.84375 11.25 C 22.76975 11.707 22 12.766 22 14 C 22 15.645 23.355 17 25 17 C 25.352 17 25.684 16.9265 26 16.8125 L 26 24 C 26 24.566 25.566 25 25 25 C 24.434 25 24 24.566 24 24 L 24 20 C 24 18.906 23.094 18 22 18 L 20 18 L 20 8 C 20 6.355 18.645 5 17 5 L 9 5 z M 9 7 L 17 7 C 17.566 7 18 7.434 18 8 L 18 11 L 18 12 L 18 14 L 18 18 L 18 25 L 8 25 L 8 18 L 8 14 L 8 12 L 8 11 L 8 8 C 8 7.434 8.434 7 9 7 z M 13.089844 10.560547 L 10.660156 15.439453 L 11.560547 15.910156 L 12.660156 16.439453 L 11.089844 19.560547 L 12.910156 20.439453 L 15.339844 15.560547 L 14.439453 15.089844 L 13.339844 14.560547 L 14.910156 11.439453 L 13.089844 10.560547 z M 25 13 C 25.562 13 26 13.438 26 14 C 26 14.562 25.562 15 25 15 C 24.437 15 24 14.562 24 14 C 24 13.438 24.437 13 25 13 z"
  }));
};
ChargingStation.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const CoffeeShop = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 11 3 L 11 7 L 13 7 L 13 3 Z M 15 4 L 15 7 L 17 7 L 17 4 Z M 4.875 8 L 5 9.09375 L 6.8125 26.3125 C 6.972656 27.832031 8.285156 29 9.8125 29 L 19.1875 29 C 20.714844 29 22.027344 27.832031 22.1875 26.3125 L 22.65625 22 L 25 22 C 26.644531 22 28 20.644531 28 19 L 28 16 C 28 14.355469 26.644531 13 25 13 L 23.59375 13 L 24 9.09375 L 24.125 8 Z M 7.125 10 L 21.875 10 L 20.1875 26.09375 C 20.132813 26.613281 19.707031 27 19.1875 27 L 9.8125 27 C 9.292969 27 8.867188 26.613281 8.8125 26.09375 Z M 23.375 15 L 25 15 C 25.566406 15 26 15.433594 26 16 L 26 19 C 26 19.566406 25.566406 20 25 20 L 22.84375 20 Z"
  }));
};
CoffeeShop.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const ConciergeBell = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 13 6 L 13 8 L 19 8 L 19 6 Z M 16 9 C 9.703125 9 4.574219 13.839844 4.0625 20 L 2 20 L 2 25 L 30 25 L 30 20 L 27.9375 20 C 27.425781 13.839844 22.296875 9 16 9 Z M 16 11 C 21.226563 11 25.445313 14.914063 25.9375 20 L 6.0625 20 C 6.554688 14.914063 10.773438 11 16 11 Z M 4 22 L 28 22 L 28 23 L 4 23 Z"
  }));
};
ConciergeBell.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const ConstructionTools = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "-1 -1.5 34 34.5",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 5.25 2.75 L 4.6875 3.28125 L 3.28125 4.6875 L 2.75 5.25 L 3.15625 5.90625 L 5.25 9.40625 L 5.53125 9.90625 L 8.46875 9.90625 L 12.46875 13.875 C 8.894531 17.464844 4.347656 22.027344 4.1875 22.1875 C 2.621094 23.753906 2.617188 26.320313 4.21875 27.8125 C 5.78125 29.355469 8.328125 29.394531 9.8125 27.8125 C 9.824219 27.800781 9.832031 27.792969 9.84375 27.78125 L 16 21.59375 L 22.1875 27.8125 L 22.28125 27.875 C 23.851563 29.355469 26.347656 29.375 27.8125 27.8125 L 27.8125 27.78125 L 27.84375 27.78125 C 29.375 26.214844 29.390625 23.667969 27.8125 22.1875 L 27.78125 22.15625 L 22.5625 16.96875 C 26.074219 16.640625 28.824219 13.675781 28.875 10.09375 L 28.90625 10.09375 C 28.910156 10.074219 28.90625 10.050781 28.90625 10.03125 C 28.90625 10.019531 28.90625 10.011719 28.90625 10 C 29.003906 8.84375 28.753906 7.738281 28.15625 6.78125 L 27.46875 5.71875 L 22.8125 10.375 L 21.40625 8.90625 L 26.15625 4.15625 L 24.78125 3.59375 C 23.976563 3.25 23.046875 3 22 3 C 18.15625 3 15 6.15625 15 10 C 15 10.417969 15.089844 10.78125 15.15625 11.15625 C 14.71875 11.59375 14.390625 11.953125 13.875 12.46875 L 9.90625 8.5 L 9.90625 5.53125 L 9.40625 5.25 L 5.90625 3.15625 Z M 22 5 C 22.140625 5 22.238281 5.082031 22.375 5.09375 L 18.59375 8.875 L 19.28125 9.59375 L 22.09375 12.5 L 22.78125 13.21875 L 26.75 9.25 C 26.769531 9.480469 26.933594 9.648438 26.90625 9.90625 L 26.90625 10 C 26.90625 12.753906 24.660156 15 21.90625 15 C 21.539063 15 21.09375 14.914063 20.59375 14.8125 L 20.0625 14.71875 L 19.6875 15.09375 L 8.40625 26.40625 L 8.375 26.40625 L 8.375 26.4375 C 7.664063 27.214844 6.421875 27.234375 5.59375 26.40625 L 5.59375 26.375 L 5.5625 26.375 C 4.785156 25.664063 4.765625 24.421875 5.59375 23.59375 C 5.972656 23.214844 13.3125 15.8125 16.90625 12.21875 L 17.3125 11.8125 L 17.15625 11.25 C 17.074219 10.925781 17 10.367188 17 10 C 17 7.246094 19.246094 5 22 5 Z M 5.5625 5.25 L 7.90625 6.6875 L 7.90625 7.6875 L 7.6875 7.90625 L 6.6875 7.90625 L 5.25 5.5625 Z M 20.1875 17.40625 L 26.40625 23.59375 L 26.40625 23.625 L 26.4375 23.625 C 27.214844 24.335938 27.234375 25.578125 26.40625 26.40625 L 26.375 26.40625 L 26.375 26.4375 C 25.664063 27.214844 24.421875 27.234375 23.59375 26.40625 L 17.40625 20.1875 Z"
  }));
};
ConstructionTools.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const ConvenienceStore = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "m30 10.68l-2-6A1 1 0 0 0 27 4H5a1 1 0 0 0-1 .68l-2 6A1.19 1.19 0 0 0 2 11v6a1 1 0 0 0 1 1h1v10h2V18h6v10h16V18h1a1 1 0 0 0 1-1v-6a1.19 1.19 0 0 0 0-.32ZM26 26H14v-8h12Zm2-10h-4v-4h-2v4h-5v-4h-2v4h-5v-4H8v4H4v-4.84L5.72 6h20.56L28 11.16Z"
  }));
};
ConvenienceStore.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const DoorOpen = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 15.875 4 L 15.78125 4.03125 L 7.78125 5.90625 L 7 6.09375 L 7 26.34375 L 7.8125 26.46875 L 15.8125 27.96875 L 15.90625 28 L 18 28 L 18 4 Z M 20 6 L 20 8 L 23 8 L 23 24 L 20 24 L 20 26 L 25 26 L 25 6 Z M 16 6.03125 L 16 26 L 9 24.6875 L 9 7.65625 Z M 14.34375 15 C 13.976563 15 13.65625 15.449219 13.65625 16 C 13.65625 16.550781 13.976563 17 14.34375 17 C 14.710938 17 15 16.550781 15 16 C 15 15.449219 14.710938 15 14.34375 15 Z"
  }));
};
DoorOpen.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const Fitness = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 21.3125 3.5 C 20.546875 3.5 19.765625 3.796875 19.1875 4.375 C 18.804688 4.757813 18.566406 5.226563 18.4375 5.71875 C 18.230469 5.675781 18.027344 5.625 17.8125 5.625 C 17.046875 5.625 16.265625 5.890625 15.6875 6.46875 C 14.53125 7.625 14.53125 9.53125 15.6875 10.6875 L 16.53125 11.53125 L 11.53125 16.53125 L 10.6875 15.6875 C 10.109375 15.109375 9.359375 14.8125 8.59375 14.8125 C 7.828125 14.8125 7.046875 15.109375 6.46875 15.6875 C 5.722656 16.4375 5.46875 17.5 5.6875 18.46875 C 5.207031 18.59375 4.753906 18.808594 4.375 19.1875 C 3.21875 20.34375 3.21875 22.25 4.375 23.40625 L 4.40625 23.4375 L 5.75 24.8125 L 5.0625 25.5 L 6.5 26.9375 L 7.1875 26.25 L 8.59375 27.625 C 9.75 28.78125 11.65625 28.78125 12.8125 27.625 C 13.1875 27.25 13.429688 26.792969 13.5625 26.3125 C 14.523438 26.519531 15.570313 26.273438 16.3125 25.53125 C 17.46875 24.375 17.46875 22.46875 16.3125 21.3125 L 15.46875 20.46875 L 20.46875 15.46875 L 21.3125 16.3125 C 22.46875 17.46875 24.375 17.46875 25.53125 16.3125 C 26.269531 15.570313 26.519531 14.523438 26.3125 13.5625 C 26.796875 13.429688 27.246094 13.191406 27.625 12.8125 C 28.78125 11.65625 28.78125 9.75 27.625 8.59375 L 26.25 7.1875 L 26.9375 6.5 L 25.5 5.0625 L 24.8125 5.75 L 23.40625 4.375 C 22.828125 3.796875 22.078125 3.5 21.3125 3.5 Z M 21.3125 5.5 C 21.5625 5.5 21.804688 5.585938 22 5.78125 L 26.21875 10 C 26.613281 10.394531 26.609375 10.984375 26.21875 11.375 C 25.828125 11.765625 25.207031 11.765625 24.8125 11.375 L 20.625 7.1875 C 20.234375 6.796875 20.234375 6.175781 20.625 5.78125 C 20.820313 5.585938 21.0625 5.5 21.3125 5.5 Z M 17.8125 7.59375 C 18.0625 7.59375 18.304688 7.710938 18.5 7.90625 L 24.09375 13.5 C 24.484375 13.894531 24.484375 14.511719 24.09375 14.90625 C 23.703125 15.296875 23.113281 15.265625 22.71875 14.875 L 22.6875 14.875 L 17.09375 9.28125 C 16.703125 8.890625 16.734375 8.300781 17.125 7.90625 C 17.316406 7.722656 17.570313 7.59375 17.8125 7.59375 Z M 17.9375 12.9375 L 19.0625 14.0625 L 14.0625 19.0625 L 12.9375 17.9375 Z M 8.59375 16.8125 C 8.84375 16.8125 9.085938 16.929688 9.28125 17.125 L 9.3125 17.125 L 14.90625 22.71875 C 15.296875 23.109375 15.265625 23.699219 14.875 24.09375 C 14.484375 24.464844 13.886719 24.480469 13.5 24.09375 L 12.8125 23.40625 L 12.6875 23.3125 L 8.59375 19.1875 L 7.90625 18.5 C 7.515625 18.105469 7.515625 17.488281 7.90625 17.09375 C 8.101563 16.898438 8.34375 16.8125 8.59375 16.8125 Z M 6.46875 20.3125 C 6.71875 20.3125 6.992188 20.429688 7.1875 20.625 L 11.375 24.8125 C 11.765625 25.203125 11.765625 25.824219 11.375 26.21875 C 10.984375 26.609375 10.394531 26.609375 10 26.21875 L 5.78125 22 C 5.386719 21.605469 5.390625 21.015625 5.78125 20.625 C 5.976563 20.429688 6.21875 20.3125 6.46875 20.3125 Z"
  }));
};
Fitness.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const FoodMenu = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "m7,3v26h-3V3h3Zm21,23V6c0-1.65-1.35-3-3-3H8v26h17c1.65,0,3-1.35,3-3ZM10,5h15c.55,0,1,.45,1,1v20c0,.55-.45,1-1,1h-15V5Zm13,4h-10v2h10v-2Zm0,4h-10v2h10v-2Z"
  }));
};
FoodMenu.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const FreeWiFi = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 16 7 C 10.984375 7 6.457031 9.082031 3.1875 12.40625 L 4.59375 13.8125 C 7.5 10.851563 11.535156 9 16 9 C 20.464844 9 24.5 10.851563 27.40625 13.8125 L 28.8125 12.40625 C 25.542969 9.082031 21.015625 7 16 7 Z M 16 12 C 12.359375 12 9.082031 13.519531 6.71875 15.9375 L 8.125 17.34375 C 10.125 15.289063 12.914063 14 16 14 C 19.085938 14 21.875 15.289063 23.875 17.34375 L 25.28125 15.9375 C 22.917969 13.519531 19.640625 12 16 12 Z M 16 17 C 13.738281 17 11.707031 17.957031 10.25 19.46875 L 11.65625 20.875 C 12.75 19.726563 14.289063 19 16 19 C 17.710938 19 19.25 19.726563 20.34375 20.875 L 21.75 19.46875 C 20.296875 17.957031 18.261719 17 16 17 Z M 16 22 C 15.117188 22 14.332031 22.390625 13.78125 23 L 16 25.21875 L 18.21875 23 C 17.667969 22.390625 16.882813 22 16 22 Z"
  }));
};
FreeWiFi.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const IndoorPool = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 23.5 11 C 21.578125 11 20 12.578125 20 14.5 C 20 16.421875 21.578125 18 23.5 18 C 25.421875 18 27 16.421875 27 14.5 C 27 12.578125 25.421875 11 23.5 11 Z M 13.71875 11.03125 C 13.355469 11.054688 13.003906 11.175781 12.6875 11.40625 L 7.40625 15.1875 L 8.59375 16.8125 L 13.84375 13.03125 L 16.125 15.65625 L 8.71875 21.9375 C 9.125 21.972656 9.558594 22 10 22 C 10.675781 22 11.324219 21.929688 11.96875 21.8125 L 17.40625 17.15625 L 19.4375 19.5 C 20.175781 19.308594 20.933594 19.144531 21.71875 19.0625 L 15.34375 11.71875 C 14.917969 11.222656 14.324219 10.992188 13.71875 11.03125 Z M 23.5 13 C 24.339844 13 25 13.660156 25 14.5 C 25 15.34375 24.339844 16 23.5 16 C 22.65625 16 22 15.34375 22 14.5 C 22 13.660156 22.65625 13 23.5 13 Z M 23 20 C 20.5625 20 18.425781 20.816406 16.34375 21.5625 C 14.261719 22.308594 12.234375 23 10 23 C 4.503906 23 1.6875 20.28125 1.6875 20.28125 L 0.3125 21.71875 C 0.3125 21.71875 3.816406 25 10 25 C 12.644531 25 14.90625 24.191406 17 23.4375 C 19.09375 22.683594 21.015625 22 23 22 C 26.96875 22 30.34375 24.78125 30.34375 24.78125 L 31.65625 23.21875 C 31.65625 23.21875 27.875 20 23 20 Z"
  }));
};
IndoorPool.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const MeetingRoom = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 19.3125 6.96875 C 18.757813 6.972656 18.207031 7 17.625 7.15625 C 17.042969 7.3125 16.460938 7.5625 15.84375 7.96875 C 15.078125 7.519531 14.335938 7.148438 13.625 7.0625 C 12.742188 6.953125 11.921875 7.011719 10.96875 7.03125 C 9.839844 7.054688 9.101563 7.632813 7.90625 8.09375 C 6.710938 8.554688 5.171875 9 3 9 L 2 9 L 2 18.625 L 2.5625 18.90625 L 4.40625 19.8125 L 10.96875 27.125 L 11 27.15625 C 11.75 27.898438 12.761719 28.082031 13.65625 28.03125 C 14.550781 27.980469 15.390625 27.71875 16.0625 27.3125 C 17.539063 26.421875 21.5625 23.625 21.5625 23.625 L 21.65625 23.5625 L 21.71875 23.5 C 22.195313 23.027344 22.433594 22.449219 22.59375 21.84375 L 26.4375 19.90625 L 29.3125 18.9375 L 30 18.71875 L 30 9 L 29 9 C 26.832031 9 25.289063 8.53125 24.09375 8.0625 C 22.898438 7.59375 22.171875 7.023438 21.03125 7 C 20.421875 6.988281 19.867188 6.964844 19.3125 6.96875 Z M 19.34375 8.96875 C 19.792969 8.964844 20.3125 8.988281 20.96875 9 C 21.074219 9.003906 22.035156 9.414063 23.375 9.9375 C 24.507813 10.382813 26.082031 10.777344 28 10.90625 L 28 17.28125 L 25.6875 18.0625 L 25.625 18.0625 L 25.5625 18.09375 L 22.53125 19.625 C 22.363281 19.167969 22.132813 18.746094 21.78125 18.375 L 21.71875 18.28125 L 17.46875 13.0625 L 16.84375 12.28125 L 16.0625 12.90625 L 13.375 15.09375 C 12.472656 15.71875 11.742188 15.539063 11 15.1875 C 10.785156 15.085938 10.796875 15.054688 10.625 14.9375 L 15.1875 11.15625 L 15.25 11.09375 C 16.605469 9.75 17.4375 9.285156 18.15625 9.09375 C 18.515625 8.996094 18.894531 8.972656 19.34375 8.96875 Z M 12.375 9 C 12.757813 8.992188 13.078125 8.996094 13.375 9.03125 C 13.636719 9.0625 13.933594 9.285156 14.1875 9.375 C 14.085938 9.46875 14.011719 9.523438 13.90625 9.625 C 13.882813 9.648438 13.867188 9.664063 13.84375 9.6875 L 8.375 14.21875 L 7.53125 14.90625 L 8.28125 15.6875 C 8.28125 15.6875 9.003906 16.46875 10.125 17 C 11.246094 17.53125 12.976563 17.832031 14.53125 16.75 L 14.59375 16.6875 L 16.53125 15.09375 L 20.21875 19.625 L 20.25 19.6875 L 20.28125 19.71875 C 20.941406 20.371094 20.925781 21.398438 20.28125 22.0625 C 20.273438 22.070313 20.289063 22.085938 20.28125 22.09375 C 20.273438 22.097656 20.140625 22.175781 20.125 22.1875 L 18.8125 20.40625 L 17.1875 21.59375 L 18.46875 23.3125 C 17.949219 23.664063 17.714844 23.847656 17.15625 24.21875 L 15.8125 22.40625 L 14.1875 23.59375 L 15.46875 25.3125 C 15.355469 25.382813 15.121094 25.539063 15.03125 25.59375 C 14.714844 25.785156 14.105469 26 13.5625 26.03125 C 13.050781 26.058594 12.660156 25.941406 12.46875 25.78125 L 12.4375 25.75 L 5.75 18.34375 L 5.625 18.1875 L 4 17.375 L 4 10.90625 C 5.910156 10.78125 7.496094 10.402344 8.625 9.96875 C 9.960938 9.457031 10.914063 9.035156 11.03125 9.03125 C 11.542969 9.019531 11.992188 9.007813 12.375 9 Z"
  }));
};
MeetingRoom.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const Multilingual = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 4 4 L 4 22 L 10 22 L 10 28 L 28 28 L 28 10 L 22 10 L 22 4 Z M 6 6 L 20 6 L 20 10.5625 L 10.5625 20 L 6 20 Z M 11 8 L 11 9 L 8 9 L 8 11 L 12.9375 11 C 12.808594 12.148438 12.457031 13.054688 11.875 13.6875 C 11.53125 13.574219 11.222656 13.433594 10.96875 13.28125 C 10.265625 12.863281 10 12.417969 10 12 L 8 12 C 8 13.191406 8.734375 14.183594 9.71875 14.84375 C 9.226563 14.949219 8.65625 15 8 15 L 8 17 C 9.773438 17 11.25 16.59375 12.375 15.84375 C 12.898438 15.933594 13.429688 16 14 16 L 14 14.125 C 14.542969 13.214844 14.832031 12.152344 14.9375 11 L 16 11 L 16 9 L 13 9 L 13 8 Z M 21.4375 12 L 26 12 L 26 26 L 12 26 L 12 21.4375 Z M 20 13.84375 L 19.0625 16.6875 L 17.0625 22.6875 L 17 22.84375 L 17 24 L 19 24 L 19 23.125 L 19.03125 23 L 20.96875 23 L 21 23.125 L 21 24 L 23 24 L 23 22.84375 L 22.9375 22.6875 L 20.9375 16.6875 Z M 20 20.125 L 20.28125 21 L 19.71875 21 Z"
  }));
};
Multilingual.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const NonSmoking = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "-1 -1.5 34 34.5",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 3.71875 2.28125 L 2.28125 3.71875 L 28.28125 29.71875 L 29.71875 28.28125 L 23.4375 22 L 31 22 L 31 16 L 17.4375 16 Z M 16.03125 4 C 15.671875 4.359375 15.40625 4.785156 15.09375 5.46875 C 14.78125 6.152344 14.503906 7.011719 14.5 7.96875 C 14.496094 8.925781 14.828125 10.019531 15.6875 10.84375 C 16.546875 11.667969 17.859375 12.203125 19.6875 12.4375 C 21.210938 12.632813 22.191406 12.882813 22.75 13.21875 C 23.308594 13.554688 23.539063 13.898438 23.71875 14.71875 L 25.6875 14.28125 C 25.429688 13.101563 24.777344 12.097656 23.78125 11.5 C 22.785156 10.902344 21.566406 10.644531 19.9375 10.4375 C 18.378906 10.238281 17.507813 9.800781 17.0625 9.375 C 16.617188 8.949219 16.5 8.511719 16.5 7.96875 C 16.5 7.425781 16.675781 6.816406 16.90625 6.3125 C 17.136719 5.808594 17.492188 5.351563 17.4375 5.40625 Z M 21.5625 4.3125 L 21.5625 6.3125 C 22.183594 6.3125 22.984375 6.40625 23.59375 6.75 C 24.203125 7.09375 24.71875 7.601563 24.84375 9.0625 L 24.9375 10 L 25.84375 10 C 26.359375 10 27.433594 10.160156 27.96875 10.625 C 28.234375 10.859375 28.40625 11.132813 28.46875 11.65625 C 28.53125 12.179688 28.4375 12.980469 27.96875 14.125 L 29.84375 14.875 C 30.402344 13.515625 30.585938 12.386719 30.46875 11.40625 C 30.351563 10.425781 29.875 9.644531 29.28125 9.125 C 28.378906 8.335938 27.398438 8.132813 26.59375 8.0625 C 26.246094 6.617188 25.523438 5.527344 24.59375 5 C 23.472656 4.363281 22.304688 4.3125 21.5625 4.3125 Z M 1 16 L 1 22 L 17.8125 22 L 15.8125 20 L 3 20 L 3 18 L 13.8125 18 L 11.8125 16 Z M 19.4375 18 L 23 18 L 23 20 L 21.4375 20 Z M 25 18 L 26 18 L 26 20 L 25 20 Z M 28 18 L 29 18 L 29 20 L 28 20 Z"
  }));
};
NonSmoking.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const PaidParking = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 -1 34 34.5",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    d: "M23 3a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-1v19a1 1 0 1 1-2 0V11h-1a2 2 0 0 1-2-2V3Zm-1.5 0H10.406a4 4 0 0 0-3.904 3.132L5.864 9H5a1 1 0 0 0 0 2h.42l-.33 1.485A4 4 0 0 0 3 16v10a3 3 0 0 0 3 3h1a3 3 0 0 0 3-3v-1h12v1a3 3 0 0 0 2.5 2.959v-2.093A1 1 0 0 1 24 26v-1h.5v-2H5v-7a2 2 0 0 1 2-2h17.5v-1.535A3.48 3.48 0 0 1 23.196 12H7.246l1.208-5.434A2 2 0 0 1 10.406 5H21.5V3ZM5 26v-1h3v1a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1Zm7.5-6a1 1 0 0 1 1-1h5a1 1 0 1 1 0 2h-5a1 1 0 0 1-1-1Zm-3-1a1.5 1.5 0 1 0 0-3a1.5 1.5 0 0 0 0 3ZM24 17.5a1.5 1.5 0 1 1-3 0a1.5 1.5 0 0 1 3 0Z"
  }));
};
PaidParking.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const PaintBrush = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "-1 -1 34 34",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 19 2.03125 L 18.28125 2.71875 L 12.71875 8.3125 L 12 9 L 11 10 L 8.71875 12.3125 L 8.15625 12.875 L 8.59375 13.5625 C 8.59375 13.5625 9.203125 14.5 9.6875 15.53125 C 9.929688 16.046875 10.148438 16.59375 10.21875 16.96875 C 10.253906 17.15625 10.257813 17.269531 10.25 17.3125 C 10.140625 17.398438 8.824219 18.441406 7.25 19.71875 C 5.609375 21.046875 3.867188 22.445313 3.15625 23.15625 C 1.601563 24.710938 1.601563 27.257813 3.15625 28.8125 C 4.703125 30.359375 7.226563 30.347656 8.78125 28.8125 L 8.8125 28.8125 C 9.527344 28.097656 10.960938 26.382813 12.28125 24.75 C 13.570313 23.15625 14.65625 21.800781 14.71875 21.71875 C 14.714844 21.722656 14.761719 21.65625 15.125 21.71875 C 15.488281 21.78125 16.003906 21.96875 16.5 22.21875 C 17.492188 22.714844 18.40625 23.375 18.40625 23.375 L 19.09375 23.875 L 19.6875 23.28125 L 22 21 L 23 20 L 23.6875 19.28125 L 29.28125 13.71875 L 29.96875 13 L 29.28125 12.28125 L 19.71875 2.71875 Z M 20.3125 7.25 C 20.503906 7.265625 20.652344 7.339844 20.78125 7.46875 C 21.292969 7.984375 20.546875 9.988281 20.1875 10.84375 C 19.828125 11.703125 19.941406 12.375 20.40625 12.84375 C 21.199219 13.644531 23.296875 11.761719 24.1875 12.65625 C 25.074219 13.550781 23.460938 15.453125 24.03125 16.03125 C 24.042969 16.042969 24.050781 16.050781 24.0625 16.0625 L 22.28125 17.84375 L 14.15625 9.71875 L 15.53125 8.34375 C 15.550781 8.367188 15.570313 8.382813 15.59375 8.40625 C 16.273438 9.089844 17.667969 8.4375 18.25 8.0625 C 18.9375 7.621094 19.742188 7.203125 20.3125 7.25 Z M 12.71875 11.15625 L 20.84375 19.28125 L 20.5625 19.5625 L 18.84375 21.3125 C 18.457031 21.046875 18.1875 20.828125 17.40625 20.4375 C 16.808594 20.140625 16.160156 19.867188 15.46875 19.75 C 14.777344 19.632813 13.902344 19.660156 13.28125 20.28125 L 13.25 20.34375 L 13.21875 20.375 C 13.21875 20.375 12.039063 21.871094 10.71875 23.5 C 9.398438 25.128906 7.867188 26.945313 7.40625 27.40625 C 6.617188 28.195313 5.382813 28.195313 4.59375 27.40625 C 3.804688 26.617188 3.804688 25.382813 4.59375 24.59375 C 5.054688 24.132813 6.871094 22.601563 8.5 21.28125 C 10.128906 19.960938 11.625 18.78125 11.625 18.78125 L 11.65625 18.75 L 11.71875 18.71875 C 12.351563 18.089844 12.3125 17.253906 12.1875 16.59375 C 12.0625 15.933594 11.78125 15.289063 11.5 14.6875 C 11.121094 13.875 10.898438 13.578125 10.65625 13.1875 L 12.4375 11.4375 Z M 6.25 24.65625 C 5.644531 24.65625 5.15625 25.144531 5.15625 25.75 C 5.15625 26.355469 5.644531 26.84375 6.25 26.84375 C 6.855469 26.84375 7.34375 26.355469 7.34375 25.75 C 7.34375 25.144531 6.855469 24.65625 6.25 24.65625 Z"
  }));
};
PaintBrush.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const Parking = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "-1 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 6 5 L 6 27 L 26 27 L 26 5 Z M 8 7 L 24 7 L 24 25 L 8 25 Z M 12 10 L 12 22 L 14 22 L 14 19 L 17 19 C 18.644531 19 20 17.644531 20 16 L 20 13 C 20 11.355469 18.644531 10 17 10 Z M 14 12 L 17 12 C 17.566406 12 18 12.433594 18 13 L 18 16 C 18 16.566406 17.566406 17 17 17 L 14 17 Z"
  }));
};
Parking.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const PetFriendly = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 12.5 5 C 11.429688 5 10.5 5.609375 9.90625 6.4375 C 9.3125 7.265625 9 8.339844 9 9.5 C 9 10.660156 9.3125 11.734375 9.90625 12.5625 C 10.5 13.390625 11.429688 14 12.5 14 C 13.570313 14 14.5 13.390625 15.09375 12.5625 C 15.6875 11.734375 16 10.660156 16 9.5 C 16 8.339844 15.6875 7.265625 15.09375 6.4375 C 14.5 5.609375 13.570313 5 12.5 5 Z M 16 9.5 C 16 10.660156 16.3125 11.734375 16.90625 12.5625 C 17.5 13.390625 18.429688 14 19.5 14 C 20.570313 14 21.5 13.390625 22.09375 12.5625 C 22.6875 11.734375 23 10.660156 23 9.5 C 23 8.339844 22.6875 7.265625 22.09375 6.4375 C 21.5 5.609375 20.570313 5 19.5 5 C 18.429688 5 17.5 5.609375 16.90625 6.4375 C 16.3125 7.265625 16 8.339844 16 9.5 Z M 12.5 7 C 12.8125 7 13.15625 7.15625 13.46875 7.59375 C 13.78125 8.03125 14 8.726563 14 9.5 C 14 10.273438 13.78125 10.96875 13.46875 11.40625 C 13.15625 11.84375 12.8125 12 12.5 12 C 12.1875 12 11.84375 11.84375 11.53125 11.40625 C 11.21875 10.96875 11 10.273438 11 9.5 C 11 8.726563 11.21875 8.03125 11.53125 7.59375 C 11.84375 7.15625 12.1875 7 12.5 7 Z M 19.5 7 C 19.8125 7 20.15625 7.15625 20.46875 7.59375 C 20.78125 8.03125 21 8.726563 21 9.5 C 21 10.273438 20.78125 10.96875 20.46875 11.40625 C 20.15625 11.84375 19.8125 12 19.5 12 C 19.1875 12 18.84375 11.84375 18.53125 11.40625 C 18.21875 10.96875 18 10.273438 18 9.5 C 18 8.726563 18.21875 8.03125 18.53125 7.59375 C 18.84375 7.15625 19.1875 7 19.5 7 Z M 7.5 12 C 6.429688 12 5.5 12.609375 4.90625 13.4375 C 4.3125 14.265625 4 15.339844 4 16.5 C 4 17.660156 4.3125 18.734375 4.90625 19.5625 C 5.5 20.390625 6.429688 21 7.5 21 C 8.570313 21 9.5 20.390625 10.09375 19.5625 C 10.6875 18.734375 11 17.660156 11 16.5 C 11 15.339844 10.6875 14.265625 10.09375 13.4375 C 9.5 12.609375 8.570313 12 7.5 12 Z M 24.5 12 C 23.429688 12 22.5 12.609375 21.90625 13.4375 C 21.3125 14.265625 21 15.339844 21 16.5 C 21 17.660156 21.3125 18.734375 21.90625 19.5625 C 22.5 20.390625 23.429688 21 24.5 21 C 25.570313 21 26.5 20.390625 27.09375 19.5625 C 27.6875 18.734375 28 17.660156 28 16.5 C 28 15.339844 27.6875 14.265625 27.09375 13.4375 C 26.5 12.609375 25.570313 12 24.5 12 Z M 7.5 14 C 7.8125 14 8.15625 14.15625 8.46875 14.59375 C 8.78125 15.03125 9 15.726563 9 16.5 C 9 17.273438 8.78125 17.96875 8.46875 18.40625 C 8.15625 18.84375 7.8125 19 7.5 19 C 7.1875 19 6.84375 18.84375 6.53125 18.40625 C 6.21875 17.96875 6 17.273438 6 16.5 C 6 15.726563 6.21875 15.03125 6.53125 14.59375 C 6.84375 14.15625 7.1875 14 7.5 14 Z M 24.5 14 C 24.8125 14 25.15625 14.15625 25.46875 14.59375 C 25.78125 15.03125 26 15.726563 26 16.5 C 26 17.273438 25.78125 17.96875 25.46875 18.40625 C 25.15625 18.84375 24.8125 19 24.5 19 C 24.1875 19 23.84375 18.84375 23.53125 18.40625 C 23.21875 17.96875 23 17.273438 23 16.5 C 23 15.726563 23.21875 15.03125 23.53125 14.59375 C 23.84375 14.15625 24.1875 14 24.5 14 Z M 16 16 C 14.667969 16 13.738281 16.867188 13.28125 17.625 C 12.824219 18.382813 12.546875 19.015625 12.28125 19.28125 C 12.125 19.4375 11.160156 19.800781 10.15625 20.3125 C 9.652344 20.570313 9.144531 20.914063 8.71875 21.4375 C 8.292969 21.960938 8 22.6875 8 23.5 C 8 25.421875 9.578125 27 11.5 27 C 12.367188 27 13.269531 26.722656 14.15625 26.46875 C 15.042969 26.214844 16 26 16 26 C 16 26 16.957031 26.214844 17.84375 26.46875 C 18.730469 26.722656 19.632813 27 20.5 27 C 22.421875 27 24 25.421875 24 23.5 C 24 22.707031 23.707031 21.980469 23.28125 21.46875 C 22.855469 20.957031 22.34375 20.640625 21.84375 20.375 C 20.84375 19.84375 19.859375 19.421875 19.71875 19.28125 C 19.480469 19.042969 19.210938 18.390625 18.75 17.625 C 18.289063 16.859375 17.339844 16 16 16 Z M 16 18 C 16.660156 18 16.734375 18.160156 17.03125 18.65625 C 17.328125 19.152344 17.554688 19.992188 18.28125 20.71875 C 19.105469 21.542969 20.148438 21.722656 20.90625 22.125 C 21.285156 22.328125 21.578125 22.542969 21.75 22.75 C 21.921875 22.957031 22 23.148438 22 23.5 C 22 24.339844 21.339844 25 20.5 25 C 20.210938 25 19.277344 24.777344 18.40625 24.53125 C 17.535156 24.285156 16.816406 24 16 24 C 15.183594 24 14.464844 24.285156 13.59375 24.53125 C 12.722656 24.777344 11.789063 25 11.5 25 C 10.660156 25 10 24.339844 10 23.5 C 10 23.097656 10.082031 22.890625 10.25 22.6875 C 10.417969 22.484375 10.722656 22.285156 11.09375 22.09375 C 11.839844 21.714844 12.875 21.5625 13.71875 20.71875 C 14.453125 19.984375 14.675781 19.117188 14.96875 18.625 C 15.261719 18.132813 15.332031 18 16 18 Z"
  }));
};
PetFriendly.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const Pool = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 12 4 C 10.347656 4 9 5.347656 9 7 L 9 19.625 L 10 18.90625 L 11 19.625 L 11 7 C 11 6.449219 11.449219 6 12 6 L 13 6 L 13 4 Z M 24 4 C 22.347656 4 21 5.347656 21 7 L 21 8 L 12 8 L 12 10 L 21 10 L 21 12 L 12 12 L 12 14 L 21 14 L 21 16 L 12 16 L 12 18 L 21 18 L 21 19.625 L 22 18.90625 L 23 19.625 L 23 7 C 23 6.449219 23.449219 6 24 6 L 25 6 L 25 4 Z M 10 20.71875 L 9.375 21.21875 C 9.375 21.21875 8.371094 22 7.03125 22 C 5.691406 22 4.625 21.21875 4.625 21.21875 L 3.375 22.78125 C 3.375 22.78125 4.917969 24 7.03125 24 C 8.492188 24 9.421875 23.515625 10 23.15625 C 10.582031 23.511719 11.558594 24 13.03125 24 C 14.492188 24 15.421875 23.515625 16 23.15625 C 16.582031 23.511719 17.558594 24 19.03125 24 C 20.492188 24 21.421875 23.515625 22 23.15625 C 22.582031 23.511719 23.558594 24 25.03125 24 C 27.144531 24 28.625 22.78125 28.625 22.78125 L 27.375 21.21875 C 27.375 21.21875 26.371094 22 25.03125 22 C 23.691406 22 22.625 21.21875 22.625 21.21875 L 22 20.71875 L 21.375 21.21875 C 21.375 21.21875 20.371094 22 19.03125 22 C 17.691406 22 16.625 21.21875 16.625 21.21875 L 16 20.71875 L 15.375 21.21875 C 15.375 21.21875 14.371094 22 13.03125 22 C 11.691406 22 10.625 21.21875 10.625 21.21875 Z M 10 24.71875 L 9.375 25.21875 C 9.375 25.21875 8.371094 26 7.03125 26 C 5.691406 26 4.625 25.21875 4.625 25.21875 L 3.375 26.78125 C 3.375 26.78125 4.917969 28 7.03125 28 C 8.492188 28 9.421875 27.515625 10 27.15625 C 10.582031 27.511719 11.558594 28 13.03125 28 C 14.492188 28 15.421875 27.515625 16 27.15625 C 16.582031 27.511719 17.558594 28 19.03125 28 C 20.492188 28 21.421875 27.515625 22 27.15625 C 22.582031 27.511719 23.558594 28 25.03125 28 C 27.144531 28 28.625 26.78125 28.625 26.78125 L 27.375 25.21875 C 27.375 25.21875 26.371094 26 25.03125 26 C 23.691406 26 22.625 25.21875 22.625 25.21875 L 22 24.71875 L 21.375 25.21875 C 21.375 25.21875 20.371094 26 19.03125 26 C 17.691406 26 16.625 25.21875 16.625 25.21875 L 16 24.71875 L 15.375 25.21875 C 15.375 25.21875 14.371094 26 13.03125 26 C 11.691406 26 10.625 25.21875 10.625 25.21875 Z"
  }));
};
Pool.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const Restaurant = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 11 5 C 9.363281 5 8.136719 6.210938 7.3125 7.5625 C 6.488281 8.914063 6 10.515625 6 12 C 6 14.582031 7.78125 16.464844 10 16.90625 L 10 28 L 12 28 L 12 16.90625 C 14.21875 16.464844 16 14.582031 16 12 C 16 10.515625 15.511719 8.914063 14.6875 7.5625 C 13.863281 6.210938 12.636719 5 11 5 Z M 18 5 L 18 12 C 18 13.851563 19.28125 15.398438 21 15.84375 L 21 28 L 23 28 L 23 15.84375 C 24.71875 15.398438 26 13.851563 26 12 L 26 5 L 24 5 L 24 12 C 24 13.117188 23.117188 14 22 14 C 20.882813 14 20 13.117188 20 12 L 20 5 Z M 21 5 L 21 12 C 21 12.550781 21.449219 13 22 13 C 22.550781 13 23 12.550781 23 12 L 23 5 Z M 11 7 C 11.574219 7 12.34375 7.566406 12.96875 8.59375 C 13.59375 9.621094 14 10.996094 14 12 C 14 14.003906 12.75 15 11 15 C 9.25 15 8 14.003906 8 12 C 8 10.996094 8.40625 9.621094 9.03125 8.59375 C 9.65625 7.566406 10.425781 7 11 7 Z"
  }));
};
Restaurant.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const Shuttle = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 5 5 C 3.355469 5 2 6.355469 2 8 L 2 25 L 5.15625 25 C 5.601563 26.71875 7.148438 28 9 28 C 10.851563 28 12.398438 26.71875 12.84375 25 L 20.15625 25 C 20.601563 26.71875 22.148438 28 24 28 C 25.851563 28 27.398438 26.71875 27.84375 25 L 31 25 L 31 14.5625 C 31 14 30.828125 13.445313 30.53125 12.96875 L 26.4375 6.40625 C 25.890625 5.53125 24.9375 5 23.90625 5 Z M 5 7 L 10 7 L 10 13 L 4 13 L 4 8 C 4 7.4375 4.4375 7 5 7 Z M 12 7 L 18 7 L 18 13 L 12 13 Z M 20 7 L 23.90625 7 C 24.253906 7 24.566406 7.175781 24.75 7.46875 L 28.1875 13 L 20 13 Z M 4 15 L 29 15 L 29 17 L 26 17 L 26 19 L 29 19 L 29 23 L 27.84375 23 C 27.398438 21.28125 25.851563 20 24 20 C 22.148438 20 20.601563 21.28125 20.15625 23 L 12.84375 23 C 12.398438 21.28125 10.851563 20 9 20 C 7.148438 20 5.601563 21.28125 5.15625 23 L 4 23 Z M 9 22 C 10.117188 22 11 22.882813 11 24 C 11 25.117188 10.117188 26 9 26 C 7.882813 26 7 25.117188 7 24 C 7 22.882813 7.882813 22 9 22 Z M 24 22 C 25.117188 22 26 22.882813 26 24 C 26 25.117188 25.117188 26 24 26 C 22.882813 26 22 25.117188 22 24 C 22 22.882813 22.882813 22 24 22 Z"
  }));
};
Shuttle.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const StackedLayers = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 16 5.9375 L 15.625 6.0625 L 5.625 10.0625 L 3.3125 11 L 5.625 11.9375 L 9.53125 13.5 L 5.625 15.0625 L 3.3125 16 L 5.625 16.9375 L 9.53125 18.5 L 5.625 20.0625 L 3.3125 21 L 5.625 21.9375 L 15.625 25.9375 L 16 26.0625 L 16.375 25.9375 L 26.375 21.9375 L 28.6875 21 L 26.375 20.0625 L 22.46875 18.5 L 26.375 16.9375 L 28.6875 16 L 26.375 15.0625 L 22.46875 13.5 L 26.375 11.9375 L 28.6875 11 L 26.375 10.0625 L 16.375 6.0625 Z M 16 8.09375 L 23.28125 11 L 16 13.90625 L 8.71875 11 Z M 12.25 14.59375 L 15.625 15.9375 L 16 16.0625 L 16.375 15.9375 L 19.75 14.59375 L 23.28125 16 L 16 18.90625 L 8.71875 16 Z M 12.25 19.59375 L 15.625 20.9375 L 16 21.0625 L 16.375 20.9375 L 19.75 19.59375 L 23.28125 21 L 16 23.90625 L 8.71875 21 Z"
  }));
};
StackedLayers.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const WashingMachine = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "m9.43,8.57c-.29-.29-.43-.64-.43-1.07,0-.42.14-.78.43-1.07.29-.29.64-.43,1.07-.43.42,0,.78.14,1.07.43.29.29.43.64.43,1.07,0,.42-.14.78-.43,1.07-.29.29-.64.43-1.07.43-.42,0-.78-.14-1.07-.43Zm5.07.43c.42,0,.78-.14,1.07-.43.29-.29.43-.64.43-1.07,0-.42-.14-.78-.43-1.07-.29-.29-.64-.43-1.07-.43-.42,0-.78.14-1.07.43-.29.29-.43.64-.43,1.07,0,.42.14.78.43,1.07.29.29.64.43,1.07.43Zm6.45,4.05l-1.41,1.41c1.95,1.95,1.95,5.12,0,7.07-.94.94-2.2,1.46-3.54,1.46s-2.59-.52-3.54-1.46h0l-1.41,1.41c1.37,1.37,3.16,2.05,4.95,2.05s3.58-.68,4.95-2.05c2.73-2.73,2.73-7.17,0-9.9Zm-1.41,1.41l1.41-1.41c-.34-.34-.71-.64-1.1-.9-.19-.13-.39-.25-.6-.35-.57-.3-1.17-.51-1.79-.64-.07-.02-.14-.03-.21-.04-2.18-.4-4.52.25-6.2,1.93-.17.17-.33.35-.48.53-1.05,1.28-1.57,2.85-1.57,4.42,0,.01,0,.02,0,.03,0,1.11.27,2.22.8,3.22.32.61.74,1.18,1.25,1.69l1.41-1.41,7.07-7.07Zm5.46-10.46H7v24h18V4m2-2v28H5V2h22Z"
  }));
};
WashingMachine.propTypes = {
  size: PropTypes.number
};

/* eslint-disable max-len */
const Wheelchair = _ref => {
  let {
    size = 32
  } = _ref;
  return /*#__PURE__*/React.createElement("svg", {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "currentcolor"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M 20 3 C 18.355469 3 17 4.355469 17 6 C 17 7.644531 18.355469 9 20 9 C 21.644531 9 23 7.644531 23 6 C 23 4.355469 21.644531 3 20 3 Z M 20 5 C 20.5625 5 21 5.4375 21 6 C 21 6.5625 20.5625 7 20 7 C 19.4375 7 19 6.5625 19 6 C 19 5.4375 19.4375 5 20 5 Z M 14.53125 7.78125 L 10.9375 8.1875 C 10.34375 8.253906 9.824219 8.589844 9.5 9.09375 L 7 12.96875 L 8.6875 14.03125 L 11.1875 10.1875 L 13.46875 9.9375 L 12 14.09375 C 8.617188 14.582031 6 17.484375 6 21 C 6 24.855469 9.144531 28 13 28 C 16.855469 28 20 24.855469 20 21 C 20 20.660156 19.953125 20.328125 19.90625 20 L 21.1875 20 L 22.53125 26.09375 L 24.46875 25.65625 L 23.15625 19.5625 C 22.957031 18.648438 22.121094 18 21.1875 18 L 19.3125 18 C 18.914063 17.160156 18.339844 16.429688 17.65625 15.8125 L 18.8125 12.59375 C 19.316406 11.199219 18.710938 9.636719 17.40625 8.9375 L 15.71875 8 L 15.71875 8.03125 C 15.359375 7.839844 14.933594 7.734375 14.53125 7.78125 Z M 15.53125 10.1875 L 16.46875 10.6875 C 16.914063 10.925781 17.109375 11.433594 16.9375 11.90625 L 15.9375 14.65625 C 15.359375 14.386719 14.742188 14.195313 14.09375 14.09375 Z M 13 16 C 15.773438 16 18 18.226563 18 21 C 18 23.773438 15.773438 26 13 26 C 10.226563 26 8 23.773438 8 21 C 8 18.226563 10.226563 16 13 16 Z"
  }));
};
Wheelchair.propTypes = {
  size: PropTypes.number
};

const HotelSpecsContainer = styled(Flex)`
  width: 100%;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;

  ${media.Medium`
    gap: 24px;
  `};
`;
const HotelSpecs = _ref => {
  let {
    hotelSpecsData
  } = _ref;
  const hotelSpecsEmpty = Object.values(hotelSpecsData).every(val => val == null);
  if (hotelSpecsEmpty) {
    return null;
  }
  return /*#__PURE__*/React.createElement(HotelSpecsContainer, null, hotelSpecsData.numFloors && /*#__PURE__*/React.createElement(MetricTileCompact, {
    graphic: /*#__PURE__*/React.createElement(StackedLayers, {
      size: 26
    }),
    label: "Floors",
    metric: hotelSpecsData.numFloors,
    reverseOrder: true
  }), hotelSpecsData.numRooms && /*#__PURE__*/React.createElement(MetricTileCompact, {
    graphic: /*#__PURE__*/React.createElement(DoorOpen, {
      size: 26
    }),
    label: "Rooms",
    metric: hotelSpecsData.numRooms,
    reverseOrder: true
  }), hotelSpecsData.yearBuilt && /*#__PURE__*/React.createElement(MetricTileCompact, {
    graphic: /*#__PURE__*/React.createElement(ConstructionTools, {
      size: 26
    }),
    label: "Built In",
    metric: hotelSpecsData.yearBuilt,
    reverseOrder: true
  }), hotelSpecsData.yearRenovated && /*#__PURE__*/React.createElement(MetricTileCompact, {
    graphic: /*#__PURE__*/React.createElement(PaintBrush, {
      size: 26
    }),
    label: "Renovated",
    metric: hotelSpecsData.yearRenovated,
    reverseOrder: true
  }));
};
HotelSpecs.propTypes = {
  hotelSpecsData: PropTypes.shape({
    numFloors: PropTypes.number,
    numRooms: PropTypes.number,
    yearBuilt: PropTypes.string,
    yearRenovated: PropTypes.string
  })
};

const KeyAmenityIcon = styled.div`
  width: 20px;
  height: 20px;
`;
const KeyAmenityLabel = styled.div`
  height: 14px;
  margin-top: 1px;
`;
const KeyAmenity = styled.div`
  // the longest possible string should be "Parking Available (Fees Apply)"
  // this will account for the plus an icon
  min-width: 228px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;
const AmenitiesSpecsContent = styled(Box)`
  width: 100%;
  margin: 0 auto;

  ${_ref => {
  let {
    standalone
  } = _ref;
  return standalone && css`
      padding: 32px;
      background-color: white;
      border-top: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
    `;
}};

  ${media.Medium`
      ${props => props.standalone && css`
          border: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
          border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)};
        `};
    `};
`;
const KeyAmenitiesList = styled(Flex)`
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  flex-wrap: wrap;
  gap: 14px;
`;
const iconSize = 22;
const supportedIcons = {
  '24HourFrontDesk': /*#__PURE__*/React.createElement(ConciergeBell, {
    size: iconSize
  }),
  FitnessCenter: /*#__PURE__*/React.createElement(Fitness, {
    size: iconSize
  }),
  AC: /*#__PURE__*/React.createElement(AirConditioning, {
    size: iconSize
  }),
  Accessible: /*#__PURE__*/React.createElement(Wheelchair, {
    size: iconSize
  }),
  BarLounge: /*#__PURE__*/React.createElement(BarLounge, {
    size: iconSize
  }),
  Restaurant: /*#__PURE__*/React.createElement(Restaurant, {
    size: iconSize
  }),
  CoffeeShopCafe: /*#__PURE__*/React.createElement(CoffeeShop, {
    size: iconSize
  }),
  SnackBarConvenienceStore: /*#__PURE__*/React.createElement(ConvenienceStore, {
    size: iconSize
  }),
  Beachfront: /*#__PURE__*/React.createElement(Beach, {
    size: iconSize
  }),
  FreeBreakfast: /*#__PURE__*/React.createElement(Breakfast, {
    size: iconSize
  }),
  MeetingRooms: /*#__PURE__*/React.createElement(MeetingRoom, {
    size: iconSize
  }),
  BusinessCenter: /*#__PURE__*/React.createElement(BarChart, {
    size: iconSize
  }),
  Casino: /*#__PURE__*/React.createElement(Casino, {
    size: iconSize
  }),
  FreeParking: /*#__PURE__*/React.createElement(Parking, {
    size: iconSize
  }),
  ParkingAvailableFeeApplies: /*#__PURE__*/React.createElement(PaidParking, {
    size: iconSize
  }),
  ElectricChargeStation: /*#__PURE__*/React.createElement(ChargingStation, {
    size: iconSize
  }),
  SmokeFree: /*#__PURE__*/React.createElement(NonSmoking, {
    size: iconSize
  }),
  OnsiteLaundry: /*#__PURE__*/React.createElement(WashingMachine, {
    size: iconSize
  }),
  FreeWiFi: /*#__PURE__*/React.createElement(FreeWiFi, {
    size: iconSize
  }),
  Pool: /*#__PURE__*/React.createElement(Pool, {
    size: iconSize
  }),
  OutdoorPool: /*#__PURE__*/React.createElement(Pool, {
    size: iconSize
  }),
  IndoorPool: /*#__PURE__*/React.createElement(IndoorPool, {
    size: iconSize
  }),
  MultilingualStaff: /*#__PURE__*/React.createElement(Multilingual, {
    size: iconSize
  }),
  PetFriendly: /*#__PURE__*/React.createElement(PetFriendly, {
    size: iconSize
  }),
  RoomService: /*#__PURE__*/React.createElement(FoodMenu, {
    size: iconSize
  }),
  Shuttle: /*#__PURE__*/React.createElement(Shuttle, {
    size: iconSize
  })
};
const supportedKeyAmenities = Object.keys(supportedIcons);
const KeyAmenityWithIcon = _ref2 => {
  let {
    icon,
    label
  } = _ref2;
  return /*#__PURE__*/React.createElement(KeyAmenity, null, /*#__PURE__*/React.createElement(KeyAmenityIcon, null, icon), /*#__PURE__*/React.createElement(KeyAmenityLabel, null, label));
};
KeyAmenityWithIcon.propTypes = {
  label: PropTypes.string,
  icon: PropTypes.node
};
const getSupportedKeyAmenity = (amenityVal, amenityLabel) => {
  if (supportedKeyAmenities.includes(amenityVal)) {
    return /*#__PURE__*/React.createElement(KeyAmenityWithIcon, {
      icon: supportedIcons[amenityVal],
      label: amenityLabel,
      key: amenityVal
    });
  }
  return null;
};
const HotelAmenities = _ref3 => {
  var _hotelDetails$ameniti, _hotelDetails$ameniti2;
  let {
    hotelDetails,
    standalone
  } = _ref3;
  const hotelSpecsObj = {};
  hotelSpecsObj.numFloors = hotelDetails.numFloors === 0 ? null : hotelDetails.numFloors || null;
  hotelSpecsObj.numRooms = hotelDetails.numRooms === 0 ? null : hotelDetails.numRooms || null;
  hotelSpecsObj.yearBuilt = hotelDetails.yearBuilt === '0' ? null : hotelDetails.yearBuilt || null;
  hotelSpecsObj.yearRenovated = hotelDetails.yearRenovated === '0' ? null : hotelDetails.yearRenovated || null;
  let featuredAmenities = [];
  featuredAmenities = (_hotelDetails$ameniti = hotelDetails.amenities) === null || _hotelDetails$ameniti === void 0 ? void 0 : _hotelDetails$ameniti.filter(amenity => amenity.featured === true);
  const otherAmenities = (_hotelDetails$ameniti2 = hotelDetails.amenities) === null || _hotelDetails$ameniti2 === void 0 ? void 0 : _hotelDetails$ameniti2.filter(amenity => amenity.featured === false);
  return /*#__PURE__*/React.createElement(TabCardContent, {
    pb: 4,
    standalone: standalone
  }, /*#__PURE__*/React.createElement(Box, {
    px: standalone ? [3, 3, 0] : 0,
    mb: [3, 3, 4]
  }, /*#__PURE__*/React.createElement(HotelSpecs, {
    hotelSpecsData: hotelSpecsObj
  })), /*#__PURE__*/React.createElement(AmenitiesSpecsContent, {
    standalone: standalone
  }, featuredAmenities.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Heading3, {
    mb: 2
  }, "Key Amenities"), /*#__PURE__*/React.createElement(Divider, {
    mb: 3
  }), /*#__PURE__*/React.createElement(KeyAmenitiesList, {
    mb: "28px"
  }, featuredAmenities.map(a => {
    return getSupportedKeyAmenity(a.value, a.label);
  }))), /*#__PURE__*/React.createElement(Heading3, {
    mb: 2
  }, "All Amenities"), /*#__PURE__*/React.createElement(Divider, {
    mb: 3
  }), /*#__PURE__*/React.createElement(AmenitiesBulletList, null, otherAmenities.map(a => /*#__PURE__*/React.createElement("li", {
    key: a.label.replace(/[ /]/g, '-')
  }, a.label)))));
};
HotelAmenities.propTypes = {
  hotelDetails: PropTypes.shape({
    numFloors: PropTypes.number,
    numRooms: PropTypes.number,
    yearBuilt: PropTypes.string,
    yearRenovated: PropTypes.string,
    hotelRating: PropTypes.number,
    reviewRate: PropTypes.number,
    reviewCount: PropTypes.number,
    amenities: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    }))
  }),
  standalone: PropTypes.bool
};

const OverallRatingNumber = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  font-size: 18px;
  font-weight: bold;
  color: white;
  background-color: ${getThemeProp('global.info--color')};
  border-radius: 2px;
  padding-top: 3px;
  padding-bottom: 3px;
  margin-right: 12px;
`;
const GuestReviewContainer = styled(Flex)`
  flex-direction: column;
  gap: 12px;

  ${_ref => {
  let {
    standalone
  } = _ref;
  return standalone && css`
      padding: 24px;
      background-color: white;
      border-top: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
    `;
}};

  ${media.Medium`
        ${props => props.standalone && css`
            margin-bottom: 24px;
            border: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
            border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)};
          `};
    `};
`;
const GuestRatingNumber = styled(OverallRatingNumber)`
  background-color: ${getThemeProp('global.accent--color')};
  color: ${getThemeProp('global.textBody--color')};
`;
const GuestReviewTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #363636;
`;
const GuestName = styled(GuestReviewTitle)`
  font-size: 14px;
`;
const GuestReviewComments = styled.div``;
const ReviewDate = styled.div`
  font-size: 14px;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
`;
const getPartnerLogo = reviewSource => {
  const sourceStr = reviewSource.toLowerCase();
  if (sourceStr.includes('hotels.com')) {
    return /*#__PURE__*/React.createElement(HotelsDotCom, null);
  }
  if (sourceStr.includes('expedia')) {
    return /*#__PURE__*/React.createElement(Expedia, null);
  }
  if (sourceStr.includes('vrbo')) {
    return /*#__PURE__*/React.createElement(Vrbo, null);
  }
  return null;
};
const HotelGuestReviews = _ref2 => {
  let {
    hotelReviews = null,
    isLoading,
    standalone
  } = _ref2;
  const theme = useTheme();
  const showPartnerLogo = theme.brand !== 'cheapair';
  if (hotelReviews !== null && (hotelReviews === null || hotelReviews === void 0 ? void 0 : hotelReviews.reviewCount) > 0) {
    const {
      reviews
    } = hotelReviews;
    return /*#__PURE__*/React.createElement(TabCardContent, {
      standalone: standalone
    }, reviews.map((r, idx) => {
      const dateString = r.reviewDate.toString();
      // remove emojis from reviewerComments & use for unique keys
      const combinedChar = `${r.reviewerComments.replace(
      // eslint-disable-next-line max-len
      /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g, '').slice(-10)}${dateString}`;
      return /*#__PURE__*/React.createElement("div", {
        key: combinedChar.replace(/[ .!/]/g, '-')
      }, idx !== 0 && !standalone ? /*#__PURE__*/React.createElement(Divider, null) : null, /*#__PURE__*/React.createElement(GuestReviewContainer, {
        standalone: standalone,
        py: 3,
        pt: idx === 0 && !standalone ? 0 : 3
      }, /*#__PURE__*/React.createElement(Flex, {
        alignItems: "center",
        justifyContent: "space-between"
      }, /*#__PURE__*/React.createElement(Flex, {
        alignItems: "center"
      }, /*#__PURE__*/React.createElement(GuestRatingNumber, null, getGuestRating(r.reviewerRating)), /*#__PURE__*/React.createElement(Heading4, null, getRatingName(getGuestRating(r.reviewerRating)))), /*#__PURE__*/React.createElement(ReviewDate, null, shortMonthDayYear(parseDate(r.reviewDate)))), /*#__PURE__*/React.createElement(GuestName, null, r.reviewerName), /*#__PURE__*/React.createElement(GuestReviewTitle, null, r.reviewTitle), /*#__PURE__*/React.createElement(GuestReviewComments, null, r.reviewerComments), showPartnerLogo && getPartnerLogo(r.verificationSource)));
    }));
  }
  return isLoading ? /*#__PURE__*/React.createElement(TabCardBox, null, /*#__PURE__*/React.createElement(ActivityIndicator, null)) : /*#__PURE__*/React.createElement(TabCardContent, null, /*#__PURE__*/React.createElement(EmptyState, {
    icon: /*#__PURE__*/React.createElement(ReviewsEmpty, null),
    text: "Sorry, we don't have any reviews for this hotel yet",
    size: "fullWidth"
  }));
};
HotelGuestReviews.propTypes = {
  hotelReviews: PropTypes.shape({
    reviewRate: PropTypes.number,
    reviewCount: PropTypes.number,
    reviews: PropTypes.arrayOf(PropTypes.shape({}))
  }),
  isLoading: PropTypes.bool,
  standalone: PropTypes.bool
};

const ValueAddContainer = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: 6px;
`;
const getIcon = valueAddLabel => {
  switch (true) {
    case valueAddLabel.includes('parking'):
      return /*#__PURE__*/React.createElement(IconFlex, {
        size: 20,
        inheritColor: true
      }, /*#__PURE__*/React.createElement(Parking, null));
    case valueAddLabel.includes('wifi') || valueAddLabel.includes('wi-fi') || valueAddLabel.includes('internet') || valueAddLabel.includes('WiFi'):
      return /*#__PURE__*/React.createElement(IconLine, {
        icon: "Wifi"
      });
    case valueAddLabel.includes('breakfast') || valueAddLabel.includes('Breakfast'):
      return /*#__PURE__*/React.createElement(IconFlex, {
        size: 20,
        inheritColor: true
      }, /*#__PURE__*/React.createElement(Breakfast, null));
    case valueAddLabel.includes('deal') || valueAddLabel.includes('save') || valueAddLabel.includes('Save'):
      return /*#__PURE__*/React.createElement(IconFlex, {
        size: 16,
        inheritColor: true
      }, /*#__PURE__*/React.createElement(IconPriceTag, null));
    default:
      // Render a default component or handle the case when no condition matches
      return /*#__PURE__*/React.createElement(IconFlex, {
        size: 16,
        inheritColor: true
      }, /*#__PURE__*/React.createElement(IconCheck, null));
  }
};
const ValueAdd = _ref => {
  let {
    children
  } = _ref;
  return /*#__PURE__*/React.createElement(ValueAddContainer, null, getIcon(children), children);
};
ValueAdd.propTypes = {
  children: PropTypes.string
};

const RateDetailsContainer = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${getThemeProp('global.link--color')};
  margin-right: 4px;
  cursor: pointer;
`;
const RateDetailsText = styled.div`
  border-bottom: 1px dotted;
`;
const RateDetails = _ref => {
  let {
    children,
    getRoomRateDetails,
    rate
  } = _ref;
  const [isLoading, setIsLoading] = useState(!children);
  const [detailsText, setDetailsText] = useState(children);
  const getDetailsText = async () => {
    getRoomRateDetails(rate).then(response => {
      setDetailsText(response.roomDescription);
      setIsLoading(false);
    });
  };
  return /*#__PURE__*/React.createElement(RateDetailsContainer, null, /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(Popover, {
    content: isLoading ? /*#__PURE__*/React.createElement(ActivityIndicator, null) : detailsText,
    placement: "top",
    kind: "dark",
    onOpenChange: children ? null : getDetailsText,
    showCloseButton: true
  }, /*#__PURE__*/React.createElement(RateDetailsText, null, "Rate Details"))), /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(Popover, {
    onOpenChange: children ? null : getDetailsText,
    content: isLoading ? /*#__PURE__*/React.createElement(ActivityIndicator, null) : detailsText,
    placement: "right",
    kind: "dark",
    showCloseButton: true
  }, /*#__PURE__*/React.createElement(RateDetailsText, null, "Rate Details"))));
};
RateDetails.propTypes = {
  children: PropTypes.string,
  getRoomRateDetails: PropTypes.func,
  rate: PropTypes.shape({
    rateDetails: PropTypes.string
  })
};

const IconColor = styled.div`
  color: ${getThemeProp('global.warning--color')};
`;
const StyledNotice = styled(Flex)`
  align-items: flex-start;
  color: ${getThemeProp('global.textBody--color')};
  line-height: 1.4;
  font-size: 14px;
`;
const WarningsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background-color: ${props => tinycolor.mix(getThemeProp('global.warning--color')(props), 'white', 80).toString()};
  padding: 20px;
`;
const ModalBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 500px;
`;
const ModalFooterContainer = styled.div`
  display: flex;
  gap: 4px;
`;
const LeisureRateExplainer = styled.div`
  line-height: 1.2;
  font-weight: 500;
`;
const LeisureRateWarningsModal = _ref => {
  let {
    isOpen,
    closeModal,
    onSubmit,
    rateWarnings
  } = _ref;
  return isOpen ? /*#__PURE__*/React.createElement(Modal, {
    onClose: closeModal
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "About Our \"Leisure\" Rates"), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(ModalBodyContainer, null, /*#__PURE__*/React.createElement(LeisureRateExplainer, null, "This is a rate provided by our partner Expedia. We include these rates because they are sometimes lower, but there are some extra restrictions you should be aware of:"), /*#__PURE__*/React.createElement(WarningsContainer, null, rateWarnings.map(warning => {
    return /*#__PURE__*/React.createElement(StyledNotice, {
      key: warning
    }, /*#__PURE__*/React.createElement(IconColor, null, /*#__PURE__*/React.createElement(IconFlex, {
      size: 16,
      inheritColor: true
    }, /*#__PURE__*/React.createElement(IconWarning, null))), /*#__PURE__*/React.createElement(Box, {
      px: "2",
      flex: "1 1 auto",
      alignSelf: "center"
    }, warning));
  })))), /*#__PURE__*/React.createElement(Modal.Footer, null, /*#__PURE__*/React.createElement(ModalFooterContainer, null, /*#__PURE__*/React.createElement(Button, {
    kind: "danger",
    onClick: closeModal,
    bordered: true,
    text: "Cancel"
  }), /*#__PURE__*/React.createElement(Button, {
    onClick: onSubmit,
    text: "I'm OK with these Restrictions"
  })))) : null;
};
LeisureRateWarningsModal.propTypes = {
  rateWarnings: PropTypes.arrayOf(PropTypes.string),
  closeModal: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isOpen: PropTypes.bool
};

const RateTitle = styled.div`
  width: 100%;
  font-size: ${getTokenValue(THEME_TOKENS.base.size.header3)};
  font-weight: 600;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.3;
  margin-bottom: 10px;
`;
const TitleValueAddRegion = styled(Flex)`
  flex-direction: column;
  justify-content: space-between;
`;
const PriceContainer = styled.div`
  /* container for loader */
  position: relative;
  min-height: 42px;
  display: flex;
  gap: 6px;
  flex-direction: column;
  align-items: flex-end;

  ${media.Medium`
    flex-direction: row;
    align-items: flex-start;
  `};
`;
const PriceButtonContainer = styled.div`
  opacity: ${_ref => {
  let {
    isLoading
  } = _ref;
  return isLoading ? 0 : 1;
}};
`;
const ActivityContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(0, -70%);
`;
const RateContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  border: #eeeeee solid 1px;
  background-color: white;
  border: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
  border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusMedium)};
  padding: 16px;
`;
const DetailsBadgesPaymentArea = styled(Flex)`
  /* this insures enough space for payment badge & price button */
  min-height: 68px;
  padding-top: 4px;
  position: relative;
  justify-content: space-between;
  align-items: flex-end;

  ${media.Medium`
    min-height: 36px;
    padding-left: 32px;
    align-items: center;
  `};
`;
const PaymentArea = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: end;
  justify-content: center;

  ${media.Medium`
    position: relative;
  `};
`;
const InfoArea = styled.div`
  min-height: 110px;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: flex-end;

  ${media.Medium`
    min-height: auto;
  `};
`;
const DetailsBadgesArea = styled(Flex)`
  gap: 8px;

  ${media.Medium`
    gap: 4px;
  `};
`;
const ValueAddsContainer = styled.div`
  display: flex;
  gap: 6px;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;
const getPaymentTypeText = paymentType => {
  switch (paymentType) {
    case 'P':
      return 'Pay Now';
    case 'H':
      return 'Pay at Hotel';
    case 'D':
      return 'Deposit Required';
    default:
      return null;
  }
};
const getLoyaltyProgramBadge = loyaltyProgram => {
  if (loyaltyProgram !== 'X') {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(Tooltip, {
      placement: "top",
      content: `There are no restrictions on loyalty points with this rate. 
        You'll be eligible to earn points in the hotel's program.`
    }, /*#__PURE__*/React.createElement(Badge, {
      interactive: true,
      kind: "neutral",
      iconToken: "Award"
    }, "Earn Points"))), /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(Tooltip, {
      content: `There are no restrictions on loyalty points with this rate. 
            You'll be eligible to earn points in the hotel's program.`
    }, /*#__PURE__*/React.createElement(Badge, {
      interactive: true,
      kind: "neutral",
      iconToken: "Award"
    }, "Earn Points"))));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(Tooltip, {
    placement: "top",
    content: `This is a "consumer" rate that comes from our partner, Expedia. 
          With Expedia rates, you won't earn points in the hotel's loyalty program.`
  }, /*#__PURE__*/React.createElement(Badge, {
    interactive: true,
    iconToken: "InfoCircle",
    kind: "warning"
  }, "No Points"))), /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(Tooltip, {
    content: `This is a "consumer" rate that comes from our partner, Expedia. 
          With Expedia rates, you won't earn points in the hotel's loyalty program.`
  }, /*#__PURE__*/React.createElement(Badge, {
    interactive: true,
    iconToken: "InfoCircle",
    kind: "warning"
  }, "No Points"))));
};
const getCancellationBadge = nonRefundable => {
  if (nonRefundable === '') {
    return null;
  }
  return nonRefundable === 'Y' ? /*#__PURE__*/React.createElement(RefundabilityBadge, {
    isRefundable: false
  }) : /*#__PURE__*/React.createElement(RefundabilityBadge, {
    isRefundable: true
  });
};
const RoomRate = /*#__PURE__*/memo(_ref2 => {
  let {
    rate,
    invitationToken,
    selectionHandler,
    getRoomRateDetails
  } = _ref2;
  const {
    rateTitle,
    valueAdds,
    rateDetails,
    rateWarnings,
    averageNightlyRate,
    paymentType,
    loyaltyProgram,
    nonRefundable,
    policyViolations,
    frontEndKey
  } = rate;
  const paymentTypeText = getPaymentTypeText(paymentType);
  const [isLoading, setIsLoading] = useState(false);
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const selectRate = () => {
    setIsLoading(true);
    if (rateTitle.toLowerCase().includes('leisure rate') && rateWarnings) {
      setWarningModalOpen(true);
    } else {
      selectionHandler(rate, () => setIsLoading(false));
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(LeisureRateWarningsModal, {
    rateWarnings: rateWarnings,
    isOpen: warningModalOpen,
    onSubmit: () => {
      selectionHandler(rate, () => setIsLoading(false));
    },
    closeModal: () => {
      setWarningModalOpen(false);
      setIsLoading(false);
    }
  }), /*#__PURE__*/React.createElement(RateContainer, null, /*#__PURE__*/React.createElement(TitleValueAddRegion, null, /*#__PURE__*/React.createElement(RateTitle, {
    hasValueAdds: Boolean(valueAdds)
  }, rateTitle), valueAdds ? /*#__PURE__*/React.createElement(ValueAddsContainer, null, valueAdds.map(valueAdd => {
    return /*#__PURE__*/React.createElement(ValueAdd, {
      key: `${frontEndKey}${valueAdd}`
    }, valueAdd);
  })) : null, /*#__PURE__*/React.createElement(InfoArea, null, /*#__PURE__*/React.createElement(DetailsBadgesArea, {
    flexDirection: ['column', 'column', 'row'],
    alignItems: [null, null, 'center']
  }, /*#__PURE__*/React.createElement(RateDetails, {
    getRoomRateDetails: getRoomRateDetails,
    rate: rate
  }, rateDetails), getLoyaltyProgramBadge(loyaltyProgram), getCancellationBadge(nonRefundable)))), /*#__PURE__*/React.createElement(DetailsBadgesPaymentArea, null, /*#__PURE__*/React.createElement(PaymentArea, null, /*#__PURE__*/React.createElement(PriceContainer, null, /*#__PURE__*/React.createElement(OutOfPolicy, {
    policyViolations: policyViolations,
    invitationToken: invitationToken
  }), isLoading && /*#__PURE__*/React.createElement(ActivityContainer, null, /*#__PURE__*/React.createElement(ActivityIndicator, null)), /*#__PURE__*/React.createElement(PriceButtonContainer, {
    isLoading: isLoading
  }, /*#__PURE__*/React.createElement(PriceButton, {
    minWidth: "120px",
    bordered: true,
    paymentTypeText: paymentTypeText,
    onClick: selectRate,
    price: averageNightlyRate
  })))))));
});
RoomRate.propTypes = {
  rate: PropTypes.shape({
    rateTitle: PropTypes.string,
    valueAdds: PropTypes.arrayOf(PropTypes.string),
    rateDetails: PropTypes.string,
    averageNightlyRate: PropTypes.number,
    paymentType: PropTypes.string,
    loyaltyProgram: PropTypes.string,
    nonRefundable: PropTypes.string,
    frontEndKey: PropTypes.string,
    policyViolations: PropTypes.arrayOf(PropTypes.string),
    rateWarnings: PropTypes.arrayOf(PropTypes.string)
  }),
  invitationToken: PropTypes.string,
  selectionHandler: PropTypes.func,
  getRoomRateDetails: PropTypes.func
};

const RoomImageContainer = styled(Box)`
  position: relative;
  width: 170px;
  height: 100px;
  border-radius: 10px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    aspect-ratio: 2 / 1;
  }
`;
const LightboxLauncherContainer = styled.div`
  position: relative;
  display: inline-block;
`;
const ButtonContainer = styled.div`
  position: absolute;
  right: 10px;
  bottom: 10px;
`;
const LightboxLauncher = _ref => {
  let {
    images
  } = _ref;
  const [isOpen, setOpen] = useState(false);
  const largeGalleryPhotos = useMemo(() => processLightboxHotelPhotos(images), [images]);
  return /*#__PURE__*/React.createElement(LightboxLauncherContainer, null, /*#__PURE__*/React.createElement(LightBox, {
    open: isOpen,
    onClose: () => setOpen(false),
    slides: largeGalleryPhotos
  }), /*#__PURE__*/React.createElement(MatchMedia.SmallAndLess, null, /*#__PURE__*/React.createElement(SingleRoomPhoto, {
    mt: 3
  }, /*#__PURE__*/React.createElement("img", {
    src: getLargeImgUrl(images[0]),
    alt: ""
  }), /*#__PURE__*/React.createElement(ButtonContainer, null, /*#__PURE__*/React.createElement(Button, {
    bordered: true,
    text: `${images.length} Photos`,
    size: "small",
    onClick: () => setOpen(true)
  })))), /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(RoomImageContainer, {
    ml: 2
  }, /*#__PURE__*/React.createElement(ButtonContainer, null, /*#__PURE__*/React.createElement(Button, {
    bordered: true,
    text: `${images.length} Photos`,
    size: "small",
    onClick: () => setOpen(true)
  })), /*#__PURE__*/React.createElement("img", {
    src: images[0],
    alt: ""
  }))));
};
LightboxLauncher.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string)
};

const RoomCardHeaderContainer = styled(Flex)`
  padding-bottom: 16px;
`;
const RoomInformationContainer = styled(Flex)`
  width: 100%;
  flex-direction: column;
`;
const RoomTitle = styled.div`
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  font-size: ${getTokenValue(THEME_TOKENS.base.size.header2)};
  line-height: 1.4;
  font-weight: bold;
  margin-bottom: 8px;
`;
const BedsContainer = styled.div`
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  display: flex;
  align-items: center;
  gap: 4px;
`;
const BedLabel = styled.span`
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  margin-left: 4px;
`;
const getBedDisplay = (bedNumber, bedType) => {
  const isPlural = bedNumber > 1;
  const beds = [];
  for (let i = 1; i <= bedNumber; i++) {
    beds.push(/*#__PURE__*/React.createElement(Bed, {
      size: 28,
      key: `${i}-${bedType}`
    }));
  }
  return /*#__PURE__*/React.createElement(BedsContainer, null, bedNumber < 4 ? /*#__PURE__*/React.createElement(React.Fragment, null, beds, " ") : null, /*#__PURE__*/React.createElement(BedLabel, null, bedNumber, " ", bedType, " Bed", isPlural ? 's' : ''));
};
const createMarkup = description => {
  return {
    __html: description
  };
};
const getDescriptionHTML = description => {
  // description is set by the back-end
  // eslint-disable-next-line react/no-danger
  return /*#__PURE__*/React.createElement("span", {
    dangerouslySetInnerHTML: createMarkup(description)
  });
};
const RoomCardHeader = _ref => {
  let {
    roomData
  } = _ref;
  const {
    title,
    description,
    images,
    bedNumber,
    bedType
  } = roomData;
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const showBedDisplay = bedNumber > 0 && bedType !== null;
  return /*#__PURE__*/React.createElement(React.Fragment, null, detailsModalOpen ? /*#__PURE__*/React.createElement(Modal, {
    onClose: () => setDetailsModalOpen(false)
  }, /*#__PURE__*/React.createElement(Modal.Header, null, "Room Details"), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(Box, {
    maxWidth: "620px"
  }, getDescriptionHTML(description)))) : null, /*#__PURE__*/React.createElement(RoomCardHeaderContainer, {
    flexDirection: ['column', 'column', 'row']
  }, /*#__PURE__*/React.createElement(RoomInformationContainer, {
    justifyContent: ['space-between', 'space-between', null]
  }, /*#__PURE__*/React.createElement(RoomTitle, null, title), /*#__PURE__*/React.createElement(Flex, {
    alignItems: ['center', 'center', 'flex-start'],
    flexDirection: ['row', 'row', 'column'],
    justifyContent: ['space-between', 'space-between', null]
  }, /*#__PURE__*/React.createElement(Box, {
    mb: [0, 0, '6px']
  }, showBedDisplay && getBedDisplay(bedNumber, bedType)), /*#__PURE__*/React.createElement(Box, {
    mt: [0, 0, 2]
  }, /*#__PURE__*/React.createElement(Button, {
    size: "small",
    fullWidth: false,
    text: "Room Details",
    bordered: true,
    onClick: () => setDetailsModalOpen(true)
  })))), images ? /*#__PURE__*/React.createElement(Box, {
    ml: "auto"
  }, /*#__PURE__*/React.createElement(LightboxLauncher, {
    images: images
  })) : null));
};
RoomCardHeader.propTypes = {
  roomData: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    bedNumber: PropTypes.number,
    bedType: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.string),
    rates: PropTypes.arrayOf(PropTypes.shape({
      rateTitle: PropTypes.string,
      valueAdds: PropTypes.arrayOf(PropTypes.string),
      rateDetails: PropTypes.string,
      averageNightlyRate: PropTypes.number,
      paymentType: PropTypes.string,
      loyaltyProgram: PropTypes.string,
      nonRefundable: PropTypes.string,
      policyViolations: PropTypes.arrayOf(PropTypes.string)
    }))
  })
};

const RoomTabCard = styled(TabCardContent)`
  border: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
  border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)};
`;
const RoomOptionCard = _ref => {
  let {
    roomData,
    invitationToken,
    selectionHandler,
    getRoomRateDetails,
    standalone
  } = _ref;
  const {
    rates
  } = roomData;
  return /*#__PURE__*/React.createElement(RoomTabCard, {
    mx: standalone ? [SPACE.size16, SPACE.size16, 0] : 3,
    mt: 3,
    mb: 4,
    pb: 1
  }, /*#__PURE__*/React.createElement(RoomCardHeader, {
    roomData: roomData
  }), rates.map(rate => {
    return /*#__PURE__*/React.createElement(RoomRate, {
      key: rate.frontEndKey,
      rate: rate,
      selectionHandler: selectionHandler,
      invitationToken: invitationToken,
      getRoomRateDetails: getRoomRateDetails
    });
  }));
};
RoomOptionCard.propTypes = {
  roomData: PropTypes.shape({
    title: PropTypes.string,
    rates: PropTypes.arrayOf(PropTypes.shape({
      rateTitle: PropTypes.string,
      valueAdds: PropTypes.arrayOf(PropTypes.string),
      rateDetails: PropTypes.string,
      averageNightlyRate: PropTypes.number,
      paymentType: PropTypes.string,
      loyaltyProgram: PropTypes.string,
      nonRefundable: PropTypes.string,
      policyViolations: PropTypes.arrayOf(PropTypes.string)
    }))
  }),
  invitationToken: PropTypes.string,
  selectionHandler: PropTypes.func,
  getRoomRateDetails: PropTypes.func,
  standalone: PropTypes.bool
};

const ClearFiltersButton = styled(Button)``;
const RoomListSubHeader = styled(ListHeaderDark)`
  background-color: ${getTokenValue(THEME_TOKENS.card.color.background)};
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  border: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
  border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusSmall)};
  font-size: inherit;
  justify-content: space-between;
`;
const RoomOptions = _ref => {
  let {
    rooms,
    invitationToken,
    rateStatus,
    selectionHandler,
    getRoomRateDetails,
    defaultRoomFilters,
    setRoomFilters,
    roomFilters,
    standalone
  } = _ref;
  const {
    freeCancellation,
    payAtHotel,
    payNow,
    depositRequired,
    inPolicy,
    earnPoints
  } = roomFilters;
  const setFreeCancellation = value => {
    setRoomFilters({
      ...roomFilters,
      freeCancellation: value
    });
  };
  const setPayAtHotel = value => {
    setRoomFilters({
      ...roomFilters,
      payAtHotel: value
    });
  };
  const setPayNow = value => {
    setRoomFilters({
      ...roomFilters,
      payNow: value
    });
  };
  const setDepositRequired = value => {
    setRoomFilters({
      ...roomFilters,
      depositRequired: value
    });
  };
  const setInPolicy = value => {
    setRoomFilters({
      ...roomFilters,
      inPolicy: value
    });
  };
  const setEarnPoints = value => {
    setRoomFilters({
      ...roomFilters,
      earnPoints: value
    });
  };
  const resetPayment = () => {
    setRoomFilters({
      ...roomFilters,
      payAtHotel: defaultRoomFilters.payAtHotel,
      payNow: defaultRoomFilters.payNow,
      depositRequired: defaultRoomFilters.depositRequired
    });
  };
  const getNumActiveFilters = () => {
    let numActiveFilters = 0;
    if (freeCancellation !== defaultRoomFilters.freeCancellation) {
      numActiveFilters++;
    }
    if (payAtHotel !== defaultRoomFilters.payAtHotel) {
      numActiveFilters++;
    }
    if (payNow !== defaultRoomFilters.payNow) {
      numActiveFilters++;
    }
    if (depositRequired !== defaultRoomFilters.depositRequired) {
      numActiveFilters++;
    }
    if (inPolicy !== defaultRoomFilters.inPolicy) {
      numActiveFilters++;
    }
    if (earnPoints !== defaultRoomFilters.earnPoints) {
      numActiveFilters++;
    }
    return numActiveFilters;
  };
  const clearFilters = () => {
    setRoomFilters(defaultRoomFilters);
  };
  const countRatesByProperty = roomList => {
    const rateCounts = {
      total: 0,
      freeCancellation: 0,
      payAtHotel: 0,
      payNow: 0,
      depositRequired: 0,
      inPolicy: 0,
      earnPoints: 0
    };
    roomList.forEach(room => {
      room.rates.forEach(rate => {
        if (rate.nonRefundable !== 'Y') {
          rateCounts.freeCancellation++;
        }
        if (!rate.policyViolations) {
          rateCounts.inPolicy++;
        }
        if (rate.paymentType === 'H') {
          rateCounts.payAtHotel++;
        }
        if (rate.paymentType === 'P') {
          rateCounts.payNow++;
        }
        if (rate.paymentType === 'D') {
          rateCounts.depositRequired++;
        }
        if (rate.loyaltyProgram !== 'X') {
          rateCounts.earnPoints++;
        }
        rateCounts.total++;
      });
    });
    return rateCounts;
  };
  const applyInPolicyFilter = unfilteredRateList => {
    if (inPolicy) {
      return unfilteredRateList.filter(rate => {
        if (!rate.policyViolations) {
          return true;
        }
        return rate.policyViolations.length === 0;
      });
    }
    return unfilteredRateList;
  };
  const applyEarnPointsFilter = unfilteredRateList => {
    if (earnPoints) {
      return unfilteredRateList.filter(rate => {
        if (rate.loyaltyProgram !== 'X') {
          return true;
        }
        return false;
      });
    }
    return unfilteredRateList;
  };
  const applyFreeCancellationFilter = unfilteredRateList => {
    if (freeCancellation === true) {
      return unfilteredRateList.filter(rate => {
        return rate.nonRefundable === 'N';
      });
    }
    return unfilteredRateList;
  };
  const applyPaymentFilter = unfilteredRateList => {
    return unfilteredRateList.filter(rate => {
      if (rate.paymentType === 'H') {
        return payAtHotel;
      }
      if (rate.paymentType === 'P') {
        return payNow;
      }
      if (rate.paymentType === 'D') {
        return depositRequired;
      }
      return true;
    });
  };
  const getRateChangeMessage = status => {
    switch (status) {
      case 'Lower':
        return 'Good news! There is a lower rate for one or more of your hotel nights.';
      case 'SlightlyLower':
        return 'Good news! There is a slightly lower rate for one or more of your hotel nights.';
      case 'Higher':
        return "We're sorry, but the average rate per night has increased. See below for details.";
      case 'SlightlyHigher':
        return "We're sorry, but the average rate per night has increased slightly. See below for details.";
      default:
        return '';
    }
  };
  const applyRateFilters = unfilteredRateList => {
    const filteredRates = applyEarnPointsFilter(applyInPolicyFilter(applyPaymentFilter(applyFreeCancellationFilter(unfilteredRateList))));
    return filteredRates;
  };
  const filterRooms = unfilteredRoomList => {
    const filteredRooms = unfilteredRoomList.map(room => {
      const filteredRates = applyRateFilters(room.rates);
      if (filteredRates.length === 0) {
        return false;
      }
      return {
        ...room,
        rates: filteredRates
      };
    });

    // removes false values from arrays (rooms that don't have rates)
    return filteredRooms.filter(room => {
      return room;
    });
  };
  const filteredList = filterRooms(rooms);
  const numOfFilteredRates = countRatesByProperty(filteredList).total;
  const numOfRatesByProperty = countRatesByProperty(rooms);
  const numOfTotalRates = numOfRatesByProperty.total;
  const numActiveFilters = getNumActiveFilters();
  const getMultifilter = _ref2 => {
    let {
      isSmall,
      showResetFilters
    } = _ref2;
    return /*#__PURE__*/React.createElement(Multifilter, {
      numActiveFilters: getNumActiveFilters(),
      small: isSmall,
      showResetApplyButtons: showResetFilters,
      onReset: () => clearFilters()
    }, /*#__PURE__*/React.createElement(FilterSection, {
      title: "Cancellation Policy"
    }, /*#__PURE__*/React.createElement(FilterCheckbox, {
      name: "nonRefundable",
      label: "Free Cancellation",
      count: numOfRatesByProperty.freeCancellation || 0,
      checked: freeCancellation === true,
      onChange: () => setFreeCancellation(!freeCancellation),
      value: "N",
      id: "N",
      key: "N"
    })), /*#__PURE__*/React.createElement(FilterSection, {
      title: "Payment Info"
    }, /*#__PURE__*/React.createElement(Flex, {
      flexDirection: "column"
    }, /*#__PURE__*/React.createElement(FilterCheckbox, {
      name: "paymentType",
      label: "Pay Now",
      count: numOfRatesByProperty.payNow || 0,
      checked: payNow === true,
      onChange: () => setPayNow(!payNow),
      value: "P",
      id: "P",
      key: "P"
    }), /*#__PURE__*/React.createElement(FilterCheckbox, {
      name: "paymentType",
      label: "Pay at Hotel",
      count: numOfRatesByProperty.payAtHotel || 0,
      checked: payAtHotel === true,
      onChange: () => setPayAtHotel(!payAtHotel),
      value: "H",
      id: "H",
      key: "H"
    }), numOfRatesByProperty.depositRequired ? /*#__PURE__*/React.createElement(FilterCheckbox, {
      name: "paymentType",
      label: "Deposit Required",
      checked: depositRequired,
      onChange: () => setDepositRequired(!depositRequired),
      value: "D",
      id: "D",
      key: "D"
    }) : null)), /*#__PURE__*/React.createElement(FilterSection, {
      title: "Policy"
    }, /*#__PURE__*/React.createElement(FilterCheckbox, {
      name: "inPolicy",
      label: "In Policy",
      count: numOfRatesByProperty.inPolicy || 0,
      checked: inPolicy === true,
      onChange: () => setInPolicy(!inPolicy),
      value: "IP",
      id: "IP",
      key: "IP"
    })), /*#__PURE__*/React.createElement(FilterSection, {
      title: "Points"
    }, /*#__PURE__*/React.createElement(FilterCheckbox, {
      name: "earnPoints",
      label: "Earn Points",
      count: numOfRatesByProperty.earnPoints || 0,
      checked: earnPoints,
      onChange: () => setEarnPoints(!earnPoints),
      value: "EP",
      id: "EP",
      key: "EP"
    })));
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MatchMedia.XLargeOnly, null, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    pb: "24px",
    pt: standalone ? '24px' : 0,
    px: ['12px', '12px', '24px'],
    width: ['100vw', '100vw', 'auto']
  }, /*#__PURE__*/React.createElement(Box, {
    pr: "12px"
  }, getMultifilter({
    isSmall: false,
    showResetFilters: false
  })), /*#__PURE__*/React.createElement(HorizontalScroll, null, /*#__PURE__*/React.createElement(Solofilter, {
    label: "Payment",
    showResetApplyButtons: false,
    isFiltered: payAtHotel !== defaultRoomFilters.payAtHotel || payNow !== defaultRoomFilters.payNow || depositRequired !== defaultRoomFilters.depositRequired,
    onReset: resetPayment
  }, /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(FilterCheckbox, {
    name: "paymentType",
    label: "Pay Now",
    checked: payNow,
    onChange: () => setPayNow(!payNow),
    value: "P",
    id: "P",
    key: "P"
  }), /*#__PURE__*/React.createElement(FilterCheckbox, {
    name: "paymentType",
    label: "Pay at Hotel",
    checked: payAtHotel,
    onChange: () => setPayAtHotel(!payAtHotel),
    value: "H",
    id: "H",
    key: "H"
  }), numOfRatesByProperty.depositRequired ? /*#__PURE__*/React.createElement(FilterCheckbox, {
    name: "paymentType",
    label: "Deposit Required",
    checked: depositRequired,
    onChange: () => setDepositRequired(!depositRequired),
    value: "D",
    id: "D",
    key: "D"
  }) : null)), /*#__PURE__*/React.createElement(SolofilterCheckbox, {
    label: "Free Cancellation",
    checked: freeCancellation === true,
    onChange: () => setFreeCancellation(!freeCancellation)
  }), /*#__PURE__*/React.createElement(SolofilterCheckbox, {
    label: "In Policy",
    checked: inPolicy,
    onChange: () => setInPolicy(!inPolicy)
  }), /*#__PURE__*/React.createElement(SolofilterCheckbox, {
    label: "Earn Points",
    checked: earnPoints,
    onChange: () => setEarnPoints(!earnPoints)
  })))), rateStatus && rateStatus === 'Lower' || rateStatus === 'SlightlyLower' ? /*#__PURE__*/React.createElement(Box, {
    px: standalone ? [SPACE.size16, SPACE.size16, 0] : SPACE.size16
  }, /*#__PURE__*/React.createElement(Notice, {
    mt: standalone ? [SPACE.size24, SPACE.size24, SPACE.size24, SPACE.size24, 0] : 0,
    mb: standalone ? [0, 0, 0, 0, SPACE.size24] : SPACE.size24,
    kind: "success"
  }, getRateChangeMessage(rateStatus))) : null, rateStatus && rateStatus !== 'Lower' && rateStatus !== 'SlightlyLower' ? /*#__PURE__*/React.createElement(Box, {
    px: standalone ? [SPACE.size16, SPACE.size16, 0] : SPACE.size16
  }, /*#__PURE__*/React.createElement(Notice, {
    mt: standalone ? [SPACE.size24, SPACE.size24, SPACE.size24, SPACE.size24, 0] : 0,
    mb: standalone ? [0, 0, 0, 0, SPACE.size24] : SPACE.size24,
    hideIcon: true,
    kind: "warning"
  }, getRateChangeMessage(rateStatus))) : null, /*#__PURE__*/React.createElement(Box, {
    pt: standalone ? [SPACE.size24, SPACE.size24, SPACE.size24, SPACE.size24, 0] : 0,
    px: standalone ? [SPACE.size16, SPACE.size16, 0] : SPACE.size16
  }, /*#__PURE__*/React.createElement(RoomListSubHeader, {
    px: SPACE.size12,
    py: [SPACE.size8, SPACE.size8, SPACE.size8, SPACE.size8, SPACE.size4]
  }, /*#__PURE__*/React.createElement(MatchMedia.LargeAndLess, null, /*#__PURE__*/React.createElement(Box, null, getMultifilter({
    isSmall: true,
    showResetFilters: true
  }))), /*#__PURE__*/React.createElement(Flex, {
    py: 1,
    alignItems: "flex-end"
  }, "Showing\xA0", /*#__PURE__*/React.createElement("b", null, numOfFilteredRates), numOfFilteredRates !== numOfTotalRates ? /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0of\xA0", /*#__PURE__*/React.createElement("b", null, numOfTotalRates)) : null, "\xA0", /*#__PURE__*/React.createElement(MatchMedia.Medium, null, "room\xA0"), "options"), /*#__PURE__*/React.createElement(MatchMedia.XLargeOnly, null, numActiveFilters ? /*#__PURE__*/React.createElement(ClearFiltersButton, {
    size: "small",
    borderedLight: true,
    iconSuffix: /*#__PURE__*/React.createElement(IconClose, null),
    kind: "primary",
    text: "Clear Filters",
    onClick: () => setRoomFilters(defaultRoomFilters)
  }) : null))), filteredList.map(room => {
    return /*#__PURE__*/React.createElement(Fragment, {
      key: room.frontEndKey
    }, /*#__PURE__*/React.createElement(RoomOptionCard, {
      roomData: room,
      invitationToken: invitationToken,
      selectionHandler: selectionHandler,
      getRoomRateDetails: getRoomRateDetails,
      standalone: standalone
    }));
  }), rooms.length !== 0 && filteredList.length === 0 ? /*#__PURE__*/React.createElement(TabCardContent, null, /*#__PURE__*/React.createElement(EmptyState, {
    icon: /*#__PURE__*/React.createElement(ReviewsEmpty, null),
    text: "There are no rooms found with the current filters",
    size: "fullWidth",
    secondaryContent: /*#__PURE__*/React.createElement(Box, {
      pt: SPACE.size16
    }, /*#__PURE__*/React.createElement(Button, {
      text: "Clear Filters",
      onClick: clearFilters
    }))
  })) : null);
};
RoomOptions.propTypes = {
  rooms: PropTypes.arrayOf(PropTypes.shape({
    rate: PropTypes.shape({
      rateTitle: PropTypes.string,
      valueAdds: PropTypes.arrayOf(PropTypes.string),
      rateDetails: PropTypes.string,
      averageNightlyRate: PropTypes.number,
      paymentType: PropTypes.string,
      loyaltyProgram: PropTypes.string,
      nonRefundable: PropTypes.string,
      policyViolations: PropTypes.arrayOf(PropTypes.string)
    })
  })),
  rateStatus: PropTypes.string,
  invitationToken: PropTypes.string,
  selectionHandler: PropTypes.func,
  getRoomRateDetails: PropTypes.func,
  setRoomFilters: PropTypes.func,
  defaultRoomFilters: PropTypes.shape({
    freeCancellation: PropTypes.bool,
    payAtHotel: PropTypes.bool,
    payNow: PropTypes.bool,
    depositRequired: PropTypes.bool,
    inPolicy: PropTypes.bool,
    earnPoints: PropTypes.bool
  }),
  roomFilters: PropTypes.shape({
    freeCancellation: PropTypes.bool,
    payAtHotel: PropTypes.bool,
    payNow: PropTypes.bool,
    depositRequired: PropTypes.bool,
    inPolicy: PropTypes.bool,
    earnPoints: PropTypes.bool
  }),
  standalone: PropTypes.bool
};

const HotelName = styled(Box)`
  font-size: ${getTokenValue(THEME_TOKENS.base.size.header3)};
  font-weight: bold;
  line-height: 1.2;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${_ref => {
  let {
    standalone
  } = _ref;
  return standalone && css`
      ${media.Large`
      font-size: ${getTokenValue(THEME_TOKENS.base.size.header2)};
    `}
    `;
}};
`;
const NameRatingContainer = styled.div`
  display: grid;
`;
const PriceButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-left: 8px;
  margin-left: auto;
`;
const HotelNameHeader = _ref2 => {
  let {
    hotelName,
    standalone,
    starRating,
    reviewRating,
    reviewCount,
    nightlyRate,
    switchToRoomOptionsTab
  } = _ref2;
  return /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(NameRatingContainer, null, /*#__PURE__*/React.createElement(HotelName, {
    pb: 1,
    standalone: standalone
  }, hotelName), /*#__PURE__*/React.createElement(StarClassGuestRating, {
    starRating: starRating,
    reviewRate: reviewRating,
    reviewCount: reviewCount,
    reviewCountLabel: " Verified Reviews"
  })), nightlyRate > 0 && /*#__PURE__*/React.createElement(PriceButtonWrapper, null, /*#__PURE__*/React.createElement(PriceButton, {
    price: nightlyRate,
    onClick: switchToRoomOptionsTab
  })));
};
HotelNameHeader.propTypes = {
  hotelName: PropTypes.string,
  standalone: PropTypes.bool,
  starRating: PropTypes.number,
  reviewRating: PropTypes.number,
  reviewCount: PropTypes.number,
  nightlyRate: PropTypes.number,
  switchToRoomOptionsTab: PropTypes.func
};

const ROOM_OVERVIEW_TABS = {
  OVERVIEW: 'overview',
  ROOM_OPTIONS: 'room-options',
  AMENITIES: 'amenities',
  REVIEWS: 'reviews'
};
const RoomsLayoutContainer = styled.div`
  position: relative;
`;
const TabsContainer = styled.div`
  background-color: white;
`;
const TabLabel = styled.div`
  padding: 16px 0 8px 0;
  font-size: 16px;
  font-weight: 700;
`;
const TabsAndDates = styled.div`
  position: sticky;
  top: 0;
  z-index: ${ZINDEX.STICKY};
  box-shadow: ${getTokenValue(THEME_TOKENS.base.shadow.raised)};
`;
const HotelRoomsLayout = _ref => {
  let {
    roomsData,
    hotelDetails,
    hotelReviews,
    hotelName = '',
    isLoadingReviews,
    isLoadingRooms,
    selectedTab = ROOM_OVERVIEW_TABS.ROOM_OPTIONS,
    checkInDate,
    checkOutDate,
    nightlyRate,
    selectionHandler,
    invitationToken,
    getRoomRateDetails,
    onTabChange,
    standalone = false,
    roomsPageUrl,
    freeCancellationDefault = false,
    inPolicyDefault = false
  } = _ref;
  const defaultRoomFilters = {
    freeCancellation: false,
    payAtHotel: true,
    payNow: true,
    depositRequired: true,
    inPolicy: false,
    earnPoints: false
  };
  const [roomFilters, setRoomFilters] = useState({
    ...defaultRoomFilters,
    inPolicy: inPolicyDefault,
    freeCancellation: freeCancellationDefault
  });
  const theme = useTheme();
  const isMobile = isMatchedMaxWidth(`${theme.global['breakpointSM--size']}px`);
  const roomsTabRef = useRef(null);
  const focusRoomsTab = () => {
    roomsTabRef.current.focus();
  };
  let normalizedRoomsData = null;
  if (roomsData) {
    normalizedRoomsData = normalizeRoomResults(roomsData);
  }
  const openRoomsNewWindow = () => {
    window.open(roomsPageUrl);
  };
  const onTabChangeCallback = tabValue => {
    onTabChange(tabValue);
    window.scrollTo(0, 0);
  };
  return /*#__PURE__*/React.createElement(RoomsLayoutContainer, null, standalone ? /*#__PURE__*/React.createElement(Box, {
    px: [3, 3, 3, 0],
    pb: ['24px', '24px', 4]
  }, /*#__PURE__*/React.createElement(HotelNameHeader, {
    hotelName: hotelName,
    standalone: standalone,
    starRating: hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.hotelRating,
    reviewRating: hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.reviewRate,
    reviewCount: hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.reviewCount,
    nightlyRate: nightlyRate // hide rate on room tab
    ,
    switchToRoomOptionsTab: focusRoomsTab
  })) : null, /*#__PURE__*/React.createElement(Tabs, {
    defaultValue: selectedTab,
    orientation: "vertical",
    onValueChange: onTabChangeCallback
  }, /*#__PURE__*/React.createElement(TabsAndDates, {
    standalone: standalone
  }, /*#__PURE__*/React.createElement(DatesSummary, {
    startDate: checkInDate,
    endDate: checkOutDate,
    secondaryContent: !standalone && roomsPageUrl ? /*#__PURE__*/React.createElement(MatchMedia.Medium, null, /*#__PURE__*/React.createElement(Button, {
      size: "small",
      bordered: true,
      text: "New Window",
      onClick: openRoomsNewWindow,
      iconSuffix: /*#__PURE__*/React.createElement(IconOpenNew, null)
    })) : null,
    px: ['12px', '12px', '24px']
  }), /*#__PURE__*/React.createElement(TabsContainer, null, /*#__PURE__*/React.createElement(Tabs.List, null, /*#__PURE__*/React.createElement(Tabs.Trigger, {
    grow: true,
    value: ROOM_OVERVIEW_TABS.OVERVIEW,
    kind: "secondary"
  }, /*#__PURE__*/React.createElement(TabLabel, null, "Overview")), /*#__PURE__*/React.createElement(Tabs.Trigger, {
    grow: true,
    value: ROOM_OVERVIEW_TABS.ROOM_OPTIONS,
    kind: "secondary",
    ref: roomsTabRef
  }, /*#__PURE__*/React.createElement(TabLabel, null, isMobile ? 'Rooms' : 'Room Options')), /*#__PURE__*/React.createElement(Tabs.Trigger, {
    grow: true,
    value: ROOM_OVERVIEW_TABS.AMENITIES,
    kind: "secondary"
  }, /*#__PURE__*/React.createElement(TabLabel, null, "Amenities")), /*#__PURE__*/React.createElement(Tabs.Trigger, {
    grow: true,
    value: ROOM_OVERVIEW_TABS.REVIEWS,
    kind: "secondary"
  }, /*#__PURE__*/React.createElement(TabLabel, null, "Reviews"))))), !standalone ? /*#__PURE__*/React.createElement(Box, {
    px: [3, 3, 3, '24px'],
    pt: ['24px', '24px', 4],
    pb: standalone ? 0 : ['24px', '24px', 4]
  }, /*#__PURE__*/React.createElement(HotelNameHeader, {
    hotelName: hotelName,
    standalone: standalone,
    starRating: hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.hotelRating,
    reviewRating: hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.reviewRate,
    reviewCount: hotelDetails === null || hotelDetails === void 0 ? void 0 : hotelDetails.reviewCount,
    nightlyRate: selectedTab !== ROOM_OVERVIEW_TABS.ROOM_OPTIONS ? nightlyRate : 0 // hide rate on room tab
    ,
    switchToRoomOptionsTab: focusRoomsTab
  })) : null, /*#__PURE__*/React.createElement(Tabs.Content, {
    value: ROOM_OVERVIEW_TABS.OVERVIEW
  }, selectedTab === ROOM_OVERVIEW_TABS.OVERVIEW ? /*#__PURE__*/React.createElement(HotelDetails, {
    hotelDetails: hotelDetails,
    standalone: standalone
  }) : null), /*#__PURE__*/React.createElement(Tabs.Content, {
    value: ROOM_OVERVIEW_TABS.ROOM_OPTIONS
  }, selectedTab === ROOM_OVERVIEW_TABS.ROOM_OPTIONS ? /*#__PURE__*/React.createElement(React.Fragment, null, isLoadingRooms ? /*#__PURE__*/React.createElement(Box, {
    p: standalone ? 0 : SPACE.size16
  }, /*#__PURE__*/React.createElement(Box, {
    mt: SPACE.size16
  }, /*#__PURE__*/React.createElement(EmptyState, {
    secondaryContent: /*#__PURE__*/React.createElement(Box, {
      mt: 4
    }, /*#__PURE__*/React.createElement(ElasticDotsLoader, {
      kind: "info",
      backgroundColor: "white"
    })),
    icon: /*#__PURE__*/React.createElement(LuggageTrolly, {
      size: isMobile ? 48 : 60
    }),
    text: "We're checking for rooms at this hotel...",
    size: isMobile ? 'small' : 'fullWidth',
    center: true
  }))) : null, !isLoadingRooms && (roomsData ? /*#__PURE__*/React.createElement(RoomOptions, {
    rooms: normalizedRoomsData.rooms,
    invitationToken: invitationToken,
    selectionHandler: selectionHandler,
    rateStatus: roomsData.rateStatus,
    getRoomRateDetails: getRoomRateDetails,
    roomFilters: roomFilters,
    setRoomFilters: setRoomFilters,
    defaultRoomFilters: defaultRoomFilters,
    standalone: standalone
  }) : /*#__PURE__*/React.createElement(TabCardContent, null, /*#__PURE__*/React.createElement(EmptyState, {
    icon: /*#__PURE__*/React.createElement(ReviewsEmpty, null),
    text: "We're sorry but this hotel no longer has rooms available based on what you have requested",
    size: "fullWidth"
  })))) : null), /*#__PURE__*/React.createElement(Tabs.Content, {
    value: ROOM_OVERVIEW_TABS.AMENITIES
  }, selectedTab === ROOM_OVERVIEW_TABS.AMENITIES ? /*#__PURE__*/React.createElement(HotelAmenities, {
    hotelDetails: hotelDetails,
    standalone: standalone
  }) : null), /*#__PURE__*/React.createElement(Tabs.Content, {
    value: ROOM_OVERVIEW_TABS.REVIEWS
  }, selectedTab === ROOM_OVERVIEW_TABS.REVIEWS ? /*#__PURE__*/React.createElement(HotelGuestReviews, {
    hotelReviews: hotelReviews,
    isLoading: isLoadingReviews,
    standalone: standalone
  }) : null)));
};
HotelRoomsLayout.propTypes = {
  roomsData: PropTypes.shape({
    rooms: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string
    })),
    rateStatus: PropTypes.string
  }),
  hotelDetails: PropTypes.shape({
    hotelRating: PropTypes.number,
    reviewRate: PropTypes.number,
    reviewCount: PropTypes.number,
    hotelAddress: PropTypes.shape({
      address1: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string
    })
  }),
  hotelAmenities: PropTypes.shape({}),
  hotelReviews: PropTypes.shape({}),
  hotelName: PropTypes.string,
  isLoadingRooms: PropTypes.bool,
  isLoadingReviews: PropTypes.bool,
  selectedTab: PropTypes.string,
  checkInDate: PropTypes.string,
  checkOutDate: PropTypes.string,
  nightlyRate: PropTypes.number,
  selectionHandler: PropTypes.func,
  onTabChange: PropTypes.func,
  invitationToken: PropTypes.string,
  getRoomRateDetails: PropTypes.func,
  standalone: PropTypes.bool,
  roomsPageUrl: PropTypes.string,
  freeCancellationDefault: PropTypes.bool,
  inPolicyDefault: PropTypes.bool
};

const NoResults = _ref => {
  let {
    onButtonClick,
    noApiResult = false
  } = _ref;
  return /*#__PURE__*/React.createElement(EmptyState, {
    size: "fullWidth",
    iconLineName: "Hotel",
    secondaryContent: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
      py: SPACE.size16
    }, noApiResult ? null : 'Try adjusting your preferences'), /*#__PURE__*/React.createElement(Button, {
      onClick: onButtonClick,
      text: noApiResult ? 'New Search' : 'Clear Filters'
    })),
    text: noApiResult ? 'No hotels matched your search' : 'No hotels matched your filters'
  });
};
NoResults.propTypes = {
  onButtonClick: PropTypes.func.isRequired,
  noApiResult: PropTypes.bool
};

export { HotelDescription, HotelMapCardMobile, HotelOptionCard, HotelOptionMini, HotelRoomsLayout, NoResults, ROOM_OVERVIEW_TABS, RoomOptions, normalizeHotelResults, normalizeRoomResults, processHotelCardData };
//# sourceMappingURL=index.esm.js.map
